import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';

//--Super Admin--//

// --- IMPORTAMOS LA NUEVA VISTA ---
import ClientView from './components/client/ClientView';
// Componentes Admin y Comunes
import BancardPaymentModal from './components/common/BancardPaymentModal';
import ToastContainer from './components/common/ToastContainer';
import DomicilioInfo from './components/common/DomicilioInfo';
import AdminSheet from './components/admin/ui/AdminSheet';
import { getAdminTheme, adButton, adType } from './components/admin/ui/adminTheme';
import ResetPassword from './components/auth/ResetPassword';


// Hooks y Utils
import useCrudActions from './hooks/useCrudActions';
import usePaymentCheck from './hooks/usePaymentCheck';
import { enviarConfirmacion } from './utils/emailService';
import { getDomicilioConfig, domicilioDisponible, dentroDeGranAsuncion, linkMapaDomicilio } from './utils/domicilio';
import { formatPrice, formatDuration, formatDate, getStatusTextColor } from './utils/helpers';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
// Iconos y Firebase
import {
  Scissors, Check, List, MessageCircle, Trash2, CheckCircle, X, Users, Lock, PlusCircle, Edit,
  BarChart2, CalendarDays, ClipboardList, ShoppingCart, PlayCircle, StopCircle, LogOut, User,
  ChevronLeft, ChevronRight, ShoppingBag, CheckSquare, Clock, Building, ChevronDown,
  AlertTriangle, Smartphone, Search, Download, TrendingUp, Banknote, ArrowDown, Loader2,
  FileText, Phone, Tag, Mail, Settings, MapPin, Upload, Image as ImageIcon, Calendar, CreditCard, Store, Eye, Star, DollarSign, UserX, Percent, Ban, Sparkles, Ticket, RefreshCw
} from 'lucide-react';
import { getAuth, onAuthStateChanged, signOut, signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { db, storage, messaging, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { getToken, onMessage } from 'firebase/messaging';
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, Timestamp, where, writeBatch,
  onSnapshot, runTransaction, setDoc, getDoc, collectionGroup
} from 'firebase/firestore';

// Fechas
import {
  format, addDays, subDays, startOfDay, getDay,
  startOfWeek, isSameMonth, addWeeks, parse, addMinutes, isBefore, differenceInDays
} from 'date-fns';
import { es } from 'date-fns/locale';
import { claveTelefono, variantesTelefono, telefonoValido, telefonoInternacional } from './utils/phone';
import { visitasDelPeriodo, camposReinicioVisitas } from './utils/membresias';

// =====================================================================
// ⚡ A pedido: CARGA DIFERIDA del panel admin y SuperAdmin.
// Antes TODO esto se descargaba y se procesaba al entrar al link de
// cualquier barbería — o sea, un cliente que solo quiere reservar
// bajaba también el código de Comisiones, Caja, Membresías, el
// SuperAdmin, etc., que nunca va a usar. Con el caché vacío eso era
// lo que más pesaba. Ahora cada pantalla del panel se baja recién
// cuando alguien entra a /login o /god-mode-2026 (el cliente final
// solo baja lo que necesita: ClientView).
//
// lazyConReintento: si justo después de subir una versión nueva alguien
// tenía la página abierta, el archivo que pide ya no existe con ese
// nombre y falla la carga. En ese caso se recarga la página UNA vez
// (para traer la versión nueva); si vuelve a fallar, muestra el error
// de siempre en vez de quedar en un bucle de recargas.
// =====================================================================
const lazyConReintento = (importador) => lazy(async () => {
  try {
    const modulo = await importador();
    try { sessionStorage.removeItem('chunk-recargado'); } catch (e) { /* navegador restringido */ }
    return modulo;
  } catch (err) {
    let yaRecargo = true;
    try {
      yaRecargo = sessionStorage.getItem('chunk-recargado') === '1';
      if (!yaRecargo) sessionStorage.setItem('chunk-recargado', '1');
    } catch (e) { yaRecargo = true; }
    if (!yaRecargo) { window.location.reload(); return new Promise(() => {}); }
    throw err;
  }
});

// ⚡ En /login el código del panel (marco + Calendario, que es la primera
// pantalla) empieza a bajar ya, en paralelo con la validación de la sesión,
// en vez de esperar a que termine. Los lazy de abajo reusan esta descarga.
if (typeof window !== 'undefined' && window.location.pathname === '/login') {
  import('./components/admin/ModernAdminLayout').catch(() => {});
  import('./components/admin/CustomAgenda').catch(() => {});
}

const SuperAdminDashboard = lazyConReintento(() => import('./components/superadmin/SuperAdminDashboard'));
const FacturacionModal = lazyConReintento(() => import('./components/admin/facturacion/FacturacionModal'));
const CashRegisterModal = lazyConReintento(() => import('./components/admin/modals/CashRegisterModal'));
const RescheduleModal = lazyConReintento(() => import('./components/admin/modals/RescheduleModal'));
const CustomAgenda = lazyConReintento(() => import('./components/admin/CustomAgenda'));
const AdminBookings = lazyConReintento(() => import('./components/admin/AdminBookings'));
const MessagesLog = lazyConReintento(() => import('./components/admin/MessagesLog'));
const ModernAdminLayout = lazyConReintento(() => import('./components/admin/ModernAdminLayout'));
const AdminDashboard = lazyConReintento(() => import('./components/admin/AdminDashboard'));
const CashHistory = lazyConReintento(() => import('./components/admin/CashHistory'));
const CashRegisterView = lazyConReintento(() => import('./components/admin/CashRegisterView'));
const CustomerLoyalty = lazyConReintento(() => import('./components/admin/CustomerLoyalty'));
const ClientsDatabase = lazyConReintento(() => import('./components/admin/ClientsDatabase'));
const LostClients = lazyConReintento(() => import('./components/admin/LostClients'));
const MembershipsManagement = lazyConReintento(() => import('./components/admin/MembershipsManagement'));
const SettingsView = lazyConReintento(() => import('./components/admin/views/SettingsView'));
const BarbersManagement = lazyConReintento(() => import('./components/admin/views/BarbersManagement'));
const ServicesManagement = lazyConReintento(() => import('./components/admin/views/ServicesManagement'));
const LocationsManagement = lazyConReintento(() => import('./components/admin/views/LocationsManagement'));
const AdminLoginForm = lazyConReintento(() => import('./components/admin/AdminLoginForm'));
const LiveView = lazyConReintento(() => import('./components/admin/views/LiveView'));
const BookingWizard = lazyConReintento(() => import('./components/admin/BookingWizard'));
const BarberMobileView = lazyConReintento(() => import('./components/admin/BarberMobileView'));
const Comisiones = lazyConReintento(() => import('./components/admin/Comisiones'));
const ProductsManagement = lazyConReintento(() => import('./components/admin/views/ProductsManagement'));
const BarberReviews = lazyConReintento(() => import('./components/admin/views/BarberReviews'));
const BarberPortal = lazyConReintento(() => import('./components/BarberPortal'));
const AddManualServiceModal = lazyConReintento(() => import('./components/BarberPortal').then(m => ({ default: m.AddManualServiceModal })));

// Configuración inicial
const SLOT_DURATION = 30;
const weekDayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// =====================================================================
// 🔒 chequearSuscripcionVencida — a pedido, para que ni el dueño ni sus
// barberos empleados puedan entrar al panel de admin una vez que se
// venció la prueba gratuita o el período ya pagado. Mismo criterio que
// ya se usaba en handleSelectLocation (que bloqueaba a los CLIENTES de
// reservar con una empresa vencida) — ahora reusado acá, para el login
// en sí, en vez de estar duplicado en cada lugar que lo necesita.
// =====================================================================
const chequearSuscripcionVencida = (companyData) => {
  if (!companyData) return false;
  if (companyData.status === 'suspended' || companyData.status === 'deleted') return true;

  if (companyData.subscriptionStatus === 'trial' && companyData.createdAt) {
    const timeCreated = typeof companyData.createdAt.toMillis === 'function'
      ? companyData.createdAt.toMillis()
      : (companyData.createdAt.seconds ? companyData.createdAt.seconds * 1000 : new Date(companyData.createdAt).getTime() || 0);
    const diffDays = Math.floor(Math.abs(Date.now() - timeCreated) / (1000 * 60 * 60 * 24));
    return diffDays > 14;
  }

  if (companyData.subscriptionStatus === 'paid' && companyData.paidUntil) {
    const timeUntil = typeof companyData.paidUntil.toMillis === 'function'
      ? companyData.paidUntil.toMillis()
      : (companyData.paidUntil.seconds ? companyData.paidUntil.seconds * 1000 : new Date(companyData.paidUntil).getTime() || 0);
    return Date.now() > timeUntil;
  }

  return false;
};



// ----------------------------------------------------
// 👇 1. ESTE COMPONENTE DEBE ESTAR COMPLETAMENTE AFUERA DE APP 👇
// ----------------------------------------------------
const ClientViewDynamic = (props) => {
  const { shopSlug } = useParams();
  const [tenantLocations, setTenantLocations] = useState([]);
  const [loadedTenant, setLoadedTenant] = useState(null); // Evita recargas infinitas
  const [isNotFound, setIsNotFound] = useState(false); // 🔴 NUEVO: Estado para el 404

  useEffect(() => {
    setLoadedTenant(null);
  }, [shopSlug]);

  useEffect(() => {
    // 1. Si la app está descargando los datos de Firebase, esperamos tranquilos
    if (props.loading) return;

    // 2. Si ya terminó de cargar, pero no hay slug o la base de datos está vacía
    if (!shopSlug || !props.locations) {
      return;
    }

    // 3. Buscar si hay un local que coincida con la URL escrita
    const found = props.locations.find(l =>
      l.slug === shopSlug ||
      l.name.toLowerCase().replace(/\s+/g, '-') === shopSlug ||
      (l.slug && l.slug.startsWith(shopSlug))
    );

    if (found) {
      // ✅ LOCAL ENCONTRADO
      setIsNotFound(false);

      // 🧠 SÚPER BUSCADOR DE SUCURSALES
      // 🔧 FIX: antes, el filtro combinaba companyId Y "el slug empieza
      // igual" con un OR — eso significaba que, aunque el companyId NO
      // coincidiera, un local de OTRA barbería con un slug parecido
      // (ej: mismo prefijo por casualidad) igual se colaba en la lista.
      // Ahora es estricto: si found.companyId existe (el caso normal),
      // SOLO se compara por companyId — nada de "empieza igual". El
      // criterio de slug queda solo como respaldo para datos viejos
      // que todavía no tengan companyId cargado.
      const baseSlug = found.slug ? found.slug.replace(/-\d+$/, '') : shopSlug;

      const companyLocs = found.companyId
        ? props.locations.filter(l => l.companyId === found.companyId)
        : props.locations.filter(l => l.slug && l.slug.startsWith(baseSlug));

      setTenantLocations(companyLocs);

      // Seleccionar el local automáticamente si es un link directo (-01)
      // o si la empresa tiene un solo local
      const exactMatch = props.locations.find(l => l.slug === shopSlug);
      const destinoAuto = exactMatch || (companyLocs.length === 1 ? companyLocs[0] : null);

      // 🚀 CARGAR EL LOGO Y AJUSTES DEL NEGOCIO
      // ⚡ A pedido (velocidad): antes esto SIEMPRE se pedía acá, y además
      // handleSelectLocation lo pedía OTRA vez para el mismo local — dos
      // cargas idénticas compitiendo por la red. Ahora, si el local se va
      // a seleccionar solo (destinoAuto), se deja que lo cargue
      // handleSelectLocation (una sola vez, con el local ya conocido). Acá
      // solo se pide cuando NO hay selección automática (empresa con varios
      // locales: el cliente ve el selector, y necesita el logo/colores).
      if (props.loadSettings && companyLocs.length > 0 && loadedTenant !== baseSlug) {
        if (!destinoAuto || props.selectedLocation?.id === destinoAuto.id) {
          const localParaAjustes = destinoAuto || companyLocs[0];
          props.loadSettings(localParaAjustes.id, localParaAjustes); // Descarga el logo dorado
        }
        setLoadedTenant(baseSlug);
      }

      if (destinoAuto && props.selectedLocation?.id !== destinoAuto.id) {
        // ⚡ Se recuerda en este navegador qué local es (solo el id) para que
        // la próxima vez los profesionales y servicios se pidan sin esperar
        // la lista de locales (ver "Precarga de locales" en App).
        try { localStorage.setItem('bg-local-rapido:' + shopSlug, JSON.stringify({ id: destinoAuto.id })); } catch (e) { /* navegador restringido */ }
        props.handleSelectLocation(destinoAuto);
      }
    } else {
      // 🔴 LOCAL NO ENCONTRADO (Ej: entraron a /probando)
      // Solo lanzamos el 404 si ya sabemos que la lista de locales de Firebase se cargó
      if (props.locations.length > 0) {
        setIsNotFound(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug, props.locations, props.selectedLocation?.id, loadedTenant, props.loading]);

  // =====================================================================
  // 🔧 A pedido — parpadeo de la vista de locales: la app siempre arranca
  // en step 0 (selector de locales) y recién salta al paso de
  // profesionales cuando termina de cargar los datos de Firebase y corre
  // el efecto de arriba (que además espera un getDoc de la empresa antes
  // de hacer setStep(1)). Con el caché vacío esa espera es más larga, y
  // se alcanzaba a ver el selector de locales aunque la barbería tenga un
  // solo local. Ahora, mientras estamos entrando a la página de una
  // barbería puntual (hay shopSlug) y todavía NO se resolvió a qué pantalla
  // ir, se muestra una pantalla de carga neutra en lugar del selector.
  //  - "inicialResuelto" se pone en true UNA sola vez: cuando ya se pasó
  //    al paso de profesionales, o cuando los datos cargaron y la
  //    barbería tiene varios locales sin link directo (ahí el selector SÍ
  //    corresponde). Después de eso nunca vuelve a tapar nada, así el
  //    botón "Locales" de una barbería con varios locales sigue andando.
  //  - esperaExcedida es un resguardo: a los 8 segundos se deja de
  //    mostrar la carga pase lo que pase, para no dejar a nadie trabado.
  // =====================================================================
  const [inicialResuelto, setInicialResuelto] = useState(false);
  const [esperaExcedida, setEsperaExcedida] = useState(false);
  const exactMatchLocal = shopSlug ? (props.locations || []).find(l => l.slug === shopSlug) : null;
  const seAutoSeleccionara = !!exactMatchLocal || tenantLocations.length === 1;
  const datosListos = !props.loading && !props.loadingLocations && tenantLocations.length > 0;

  useEffect(() => {
    setInicialResuelto(false);
    setEsperaExcedida(false);
    if (!shopSlug) return;
    const t = setTimeout(() => setEsperaExcedida(true), 8000);
    return () => clearTimeout(t);
  }, [shopSlug]);

  useEffect(() => {
    if (!shopSlug) return;
    if (props.step !== 0 || (datosListos && !seAutoSeleccionara)) setInicialResuelto(true);
  }, [shopSlug, props.step, datosListos, seAutoSeleccionara]);

  // 🔴 RENDERIZADO DE LA PANTALLA 404
  if (isNotFound) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="p-6 bg-gray-800 rounded-full mb-6 border border-gray-700 shadow-2xl">
          <Store size={64} className="text-gray-500" />
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-white mb-4">Barbería no encontrada</h1>
        <p className="text-gray-400 max-w-md text-sm md:text-base leading-relaxed mb-8">
          El enlace que ingresaste no existe, está mal escrito o la barbería ya no se encuentra disponible en nuestra plataforma.
        </p>
        <button
          onClick={() => window.location.replace("https://barbergo.com.py")}
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 px-8 rounded-xl shadow-lg shadow-amber-500/20 transition-transform active:scale-95 flex items-center gap-2"
        >
          <ChevronLeft size={20} /> Volver a BarberGo
        </button>
      </div>
    );
  }

  // ✅ FIX: antes, mientras tenantLocations todavía no terminaba de
  // calcularse (una brevísima ventana justo después de entrar), el
  // fallback usaba props.locations SIN FILTRAR — es decir, TODOS los
  // locales de TODAS las barberías de la plataforma, no solo los de
  // esta empresa. Eso hacía que "locations.length > 1" diera
  // verdadero incluso para una barbería con un solo local, mostrando
  // el botón "Locales" de más. Ahora, si hay un shopSlug (estamos en
  // la página de una barbería puntual), SIEMPRE se usa tenantLocations
  // — aunque esté vacío mientras se calcula — y nunca se cae en la
  // lista completa sin filtrar. Sin shopSlug (otro flujo, sin barbería
  // específica en la URL) se mantiene el comportamiento de siempre.
  const propsToPass = {
    ...props,
    locations: shopSlug ? tenantLocations : props.locations
  };

  // 🔧 Pantalla de carga neutra mientras se resuelve a qué vista ir (ver
  // el comentario de arriba) — evita que se vea el selector de locales
  // por un instante cuando la barbería tiene un solo local.
  if (shopSlug && !inicialResuelto && !esperaExcedida) {
    return (
      <div
        className="min-h-[100dvh] w-full flex items-center justify-center"
        style={{ background: props.isDarkMode ? '#0b0b10' : '#f8fafc' }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: props.isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', borderTopColor: '#d4af37' }}
        />
      </div>
    );
  }

  return <ClientView {...propsToPass} />;
};
// ----------------------------------------------------

// COMPONENTE PARA REDIRIGIR A LA PÁGINA PRINCIPAL DE VENTAS
const RedirectToLanding = () => {
  React.useEffect(() => {
    // Aquí pones la URL de tu página web principal
    window.location.replace("https://barbergo.com.py");
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-amber-500">
      <p className="font-bold animate-pulse">Redirigiendo a BarberGo...</p>
    </div>
  );
};

// 👇 1. PEGAR ESTO JUSTO ARRIBA DE: const App = () => { 👇
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Si hay un error que causa pantalla blanca, actualiza el estado
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      // ESTO ES LO QUE SE MUESTRA EN LUGAR DE LA PANTALLA BLANCA
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#0f172a] text-white p-6 text-center">
          <div className="w-full max-w-sm">
            <h2 className="text-2xl font-black mb-4 text-amber-500">⚠️ AVISO IMPORTANTE</h2>
            <p className="text-sm text-gray-300 mb-6 leading-relaxed">
              Tu navegador actual generó un error al cargar el sistema de reservas. Por favor, abre este enlace en tu navegador principal.
            </p>
            <button
              onClick={(e) => {
                navigator.clipboard.writeText(window.location.href);
                e.currentTarget.innerText = "¡Enlace Copiado! ✓";
                e.currentTarget.classList.add("bg-green-600", "border-green-500", "text-white");
                e.currentTarget.classList.remove("bg-gray-800", "text-amber-500");
              }}
              className="w-full py-4 mb-6 rounded-2xl font-black text-sm uppercase tracking-widest border border-amber-500/50 bg-gray-800 text-amber-500 hover:bg-gray-700 transition-all active:scale-95 shadow-lg"
            >
              📋 Copiar Enlace
            </button>
            <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700 text-left shadow-xl">
              <strong className="text-amber-500 block mb-3 text-xs uppercase tracking-widest">Opción 2 (En iPhone)</strong>
              <p className="text-sm text-gray-300 mb-2">1. Toca los <strong>tres puntitos (...)</strong> arriba a la derecha.</p>
              <p className="text-sm text-gray-300">2. Selecciona <strong>"Abrir en el navegador"</strong> o <strong>"Abrir en Safari"</strong>.</p>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
// 👆 -------------------------------------------------------- 👆

// ⚡ Se muestra en las pantallas que calculan sobre todo el historial de
// reservas mientras éste termina de llegar (ver "CARGA EN DOS ETAPAS").
const CargandoHistorial = ({ isDarkMode }) => (
  <div className="flex items-center justify-center w-full min-h-[400px]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      <p className={`text-xs font-bold uppercase tracking-widest animate-pulse ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
        Cargando historial...
      </p>
    </div>
  </div>
);

const App = () => {




  // Pon este useEffect junto a los demás en tu App.js
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'login') {
      setShowLoginModal(true); // Abrimos el modal
      // Limpiamos la URL para borrar el "?action=login" y que quede perfecta
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);


  const [toasts, setToasts] = useState([]);

  // 🛡️ CONTROL DE CACHÉ ANTI-BUCLES
  useEffect(() => {
    try {
      // 📱 Navegadores embebidos (Instagram, Facebook, Line) — algunos
      // no guardan bien el localStorage/sessionStorage entre cargas de
      // página (ni siquiera dan error, simplemente no persiste). Eso
      // rompe el "freno de emergencia" de abajo: savedVersion nunca
      // queda guardada de verdad, entonces esta lógica cree que SIEMPRE
      // hay una versión nueva y fuerza un reload... que vuelve a
      // arrancar de cero, en un bucle infinito — la pantalla en blanco
      // que reportó Joel es exactamente este patrón. Para estos
      // navegadores, directamente NO se intenta el auto-reload por
      // versión — el resto del control de caché (cache API, si existe)
      // sigue andando igual, no forzamos window.location.reload.
      const esNavegadorEmbebido = typeof navigator !== 'undefined' && /Instagram|FBAN|FBAV|Line\//.test(navigator.userAgent || '');
      if (esNavegadorEmbebido) return;

      // ⚠️ IMPORTANTE: subir este número en CADA deploy con cambios de
      // lógica (como el fix del horario variable). Si se olvida, el
      // navegador sigue sirviendo el JS viejo desde caché aunque el
      // código fuente ya esté actualizado — exactamente lo que pasó acá:
      // App.js calculaba bien el horario, pero la pantalla seguía
      // mostrando la versión vieja cacheada.
      const currentVersion = "1.4.4";
      const savedVersion = localStorage.getItem('app_version');
      const hasReloaded = sessionStorage.getItem('has_reloaded'); // Freno de emergencia

      if (savedVersion !== currentVersion && !hasReloaded) {
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          });
        }

        localStorage.setItem('app_version', currentVersion);
        sessionStorage.setItem('has_reloaded', 'true');

        window.location.reload(true);
      }
    } catch (error) {
      console.warn("Navegador estricto, no se pudo limpiar caché automáticamente.", error);
    }
  }, []);

  const addToast = useCallback((title, message, type = 'info', autoClose = true) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, title, message, type }]);
    if (autoClose) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 6000);
    }
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // 2. ESTADOS GLOBALES
  const [showFacturacionModal, setShowFacturacionModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // ✅ FIX: "loading" de arriba lo comparten ~20 funciones sin relación
  // (guardar ajustes, crear locales, bloquear horarios, etc. — todo lo
  // del panel admin). Si dos de ellas corrían cerca en el tiempo, una
  // podía apagarle el indicador a la otra antes de tiempo, o dejarla
  // trabada — un botón se quedaba "cargando" para siempre hasta
  // recargar la página. Como esto también afectaba al FLUJO DEL
  // CLIENTE (confirmar reserva, buscar horario, buscar "mis reservas"),
  // le damos su propia bandera dedicada, que nada del lado admin puede
  // tocar nunca.
  const [clientLoading, setClientLoading] = useState(false);
  const [isShopSuspended, setIsShopSuspended] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('local');
  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [reportPeriod, setReportPeriod] = useState('month');
  const [modalTab, setModalTab] = useState('details');
  const notificationAudioRef = useRef(null);


  // 1. Agrega este ref junto a los demás refs al inicio del App
  const userHasInteractedRef = useRef(false);

  // 2. Agrega este useEffect para detectar la primera interacción
  useEffect(() => {
    const handleFirstInteraction = () => {
      userHasInteractedRef.current = true;
      // Pre-cargar el audio después de la interacción
      if (notificationAudioRef.current) {
        notificationAudioRef.current.load();
        // Reproducir y pausar inmediatamente para "desbloquear" el audio
        notificationAudioRef.current.play()
          .then(() => {
            notificationAudioRef.current.pause();
            notificationAudioRef.current.currentTime = 0;
          })
          .catch(() => { });
      }
      // Una vez desbloqueado, removemos los listeners
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, []);
  // Agrega este ref junto a los demás refs al inicio del componente
  const notifiedBookingsRef = useRef(new Set());
  const myFCMTokenRef = useRef(null);

  useEffect(() => {
    // 🔇 Sonido de notificación desactivado temporalmente
    notificationAudioRef.current = null;
  }, []);

  // 🛡️ Protegido con try/catch — en navegadores restringidos (como el
  // embebido de Instagram) localStorage.getItem puede directamente
  // TIRAR ERROR en vez de devolver null, y como esto corre en el
  // cuerpo del componente (no en un useEffect), un error acá corta el
  // primer render entero — pantalla en blanco, sin ninguna oportunidad
  // de mostrar nada, ni siquiera el aviso de "abrí en el navegador".
  let paymentOrigin = null;
  try { paymentOrigin = localStorage.getItem('paymentOrigin'); } catch (e) { /* navegador restringido, seguimos sin esto */ }


  const [view, setView] = useState(() => {
    let origin = null;
    try { origin = localStorage.getItem('paymentOrigin'); } catch (e) { /* navegador restringido */ }
    const hasPaymentParams = window.location.search.includes('payment_status');
    const hasAdminHash = window.location.hash === '#admin-secreto-2024';

    // Si volvemos de un pago iniciado en Admin O si tenemos el hash secreto -> Vista Admin
    if ((origin === 'admin' && hasPaymentParams) || hasAdminHash) {
      return 'admin';
    }
    // Si no, Vista Cliente
    return 'booking';


  }); const [adminSubView, setAdminSubView] = useState('calendar');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [step, setStep] = useState(0);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [locations, setLocations] = useState([]);
  // 🔧 FIX DE CARRERA: si onAuthStateChanged (u otro disparador) llama a
  // loadLocations/loadBarbers dos veces seguidas muy rápido (pasa en
  // Firebase al restaurar una sesión guardada), las dos consultas a
  // Firestore corren en paralelo — y no hay garantía de que la que
  // arrancó DESPUÉS termine también DESPUÉS. Si la respuesta de la
  // llamada VIEJA (por ejemplo, de una sesión anterior con OTRA
  // empresa) llega tarde y pisa el resultado de la llamada NUEVA
  // (correcta), se ven barberos/locales de otra barbería por un
  // instante, hasta que algo fuerce una recarga. Este contador se
  // incrementa en cada llamada nueva; cada función solo aplica su
  // resultado si sigue siendo, para ese momento, la llamada MÁS
  // RECIENTE — así, aunque una respuesta vieja llegue tarde, se
  // descarta sola en vez de pisar el dato bueno.
  const locationsRequestRef = useRef(0);
  // ⚡ A pedido: true cuando ya se disparó (y no falló) la carga de locales
  // públicos al montar la app, sin esperar a Firebase Auth — ver el efecto
  // "Precarga de locales" más abajo.
  const locationsPublicasPrecargadasRef = useRef(false);
  // ⚡ Carga adelantada de profesionales/servicios para visitantes que ya
  // entraron antes a este link: { id, promesa } mientras está en curso.
  const precargaPublicaRef = useRef(null);
  const barbersRequestRef = useRef(0);
  // ✅ FIX: antes loadLocations() usaba el mismo "loading" compartido que
  // decenas de otras acciones sin relación en toda la app (guardar,
  // eliminar, confirmar reservas, etc.) — si cualquiera de esas otras
  // terminaba antes, apagaba el indicador de carga de sucursales aunque
  // el fetch real todavía no hubiera llegado, mostrando la pantalla de
  // "Elegí tu sucursal" vacía. En Safari normal esto casi no se nota
  // (todo corre rápido y parejo), pero en navegadores embebidos más
  // lentos/inconsistentes (WhatsApp, Instagram) se hacía visible.
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedAdminLocationId, setSelectedAdminLocationId] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  // 🎛️ Funcionalidades habilitadas por SuperAdmin para esta empresa
  // puntual (independiente del plan) — companies/{id}.enabledFeatures.
  // Todo en false hasta que se cargue de verdad, para que nada se
  // muestre de más mientras tanto.
  // 🔧 A pedido: el Flujo WhatsApp Optimizado ya está validado y se usa en
  // TODAS las empresas — se fuerza acá, en un solo lugar, para que todas las
  // pantallas (reserva, admin, cancelaciones) lo vean activo sin depender del
  // switch por empresa del SuperAdmin.
  const [enabledFeaturesBase, setEnabledFeatures] = useState({});
  const enabledFeatures = useMemo(() => ({ ...enabledFeaturesBase, pruebaFlujoWhatsapp: true }), [enabledFeaturesBase]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [newLocationInfo, setNewLocationInfo] = useState({ name: '', address: '', image: '', mapUrl: '', googleReviewsUrl: '' });
  const [selectedServices, setSelectedServices] = useState([]);
  const [showBancardModal, setShowBancardModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [clientInfo, setClientInfo] = useState({ name: '', phone: '', email: '', ci: '', observaciones: '' });
  // 🏠 Servicio a domicilio: el cliente eligió "A domicilio" y su dirección/ubicación
  const [modoDomicilio, setModoDomicilio] = useState(false);
  const [datosDomicilio, setDatosDomicilio] = useState({ ciudad: '', direccion: '', referencia: '', lat: null, lng: null, origenUbicacion: null, linkUbicacion: '' });
  // ⚡ CARGA EN DOS ETAPAS (dueño/barbero): el historial completo de la
  // empresa son miles de documentos (varios MB) y, mientras baja, Firestore
  // retiene en la misma fila a los locales y barberos — el panel tardaba
  // ~30 s en aparecer. Ahora:
  //   1) El escuchador principal (más abajo) trae los turnos de la empresa
  //      desde hace 7 días en adelante ("bookingsRecientes") — pocos, llegan
  //      en 1-2 s y alcanzan para agenda, vista en vivo y avisos de reservas.
  //   2) Recién después, un segundo escuchador trae SOLO los anteriores
  //      ("bookingsAntiguas"). Las dos consultas no se pisan: cada reserva
  //      se lee (y se cobra) una sola vez. Cuando llega, historialListo = true.
  // Ambas usan el índice companyId + date (firestore.indexes.json).
  // Las pantallas que calculan sobre todo el historial (estadísticas, caja,
  // comisiones, base de clientes, etiquetas Nuevo/Frecuente...) esperan a
  // historialListo para no mostrar nunca un número incompleto.
  const [bookingsRecientes, setBookings] = useState([]);
  const [bookingsAntiguas, setBookingsAntiguas] = useState([]);
  const [ventanaRecientes, setVentanaRecientes] = useState(null); // "companyId|desde" de la etapa 1 ya cargada
  const [historialListoDe, setHistorialListoDe] = useState(null); // companyId cuyo historial ya llegó
  const esStaffConEmpresa = currentUser?.role === 'owner' || currentUser?.role === 'barber';
  const historialListo = !esStaffConEmpresa || (!!currentUser?.companyId && historialListoDe === currentUser.companyId);
  const bookings = useMemo(() => {
    if (!esStaffConEmpresa || !historialListo) return bookingsRecientes;
    const todas = [...bookingsRecientes, ...bookingsAntiguas];
    todas.sort((a, b) => {
      const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (new Date(a.createdAt).getTime() || 0);
      const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (new Date(b.createdAt).getTime() || 0);
      return timeB - timeA;
    });
    return todas;
  }, [esStaffConEmpresa, historialListo, bookingsRecientes, bookingsAntiguas]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  // Refs y otros estados
  const selectedLocationRef = useRef(selectedAdminLocationId);
  const locationsRef = useRef(locations);

  const currentViewRef = useRef(view);
  useEffect(() => {
    currentViewRef.current = view;
  }, [view]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ⚡ Cortina "Sincronizando sucursal..." del Calendario: antes esperaba
  // siempre 800 ms fijos, aunque los barberos ya hubieran llegado. Ahora
  // dura justo hasta que loadBarbers trae los de esta sucursal (con un
  // tope de 4 s por si la consulta falla o tarda demasiado).
  const [barberosListosDe, setBarberosListosDe] = useState(null);
  const [esperaLocalExcedida, setEsperaLocalExcedida] = useState(false);
  useEffect(() => {
    setEsperaLocalExcedida(false);
    if (!selectedAdminLocationId) return;
    const timer = setTimeout(() => setEsperaLocalExcedida(true), 4000);
    return () => clearTimeout(timer);
  }, [selectedAdminLocationId]);
  const cargandoLocal = !!selectedAdminLocationId && !esperaLocalExcedida
    && String(barberosListosDe) !== String(selectedAdminLocationId);

  // 💸 FIX DE COSTOS: antes esto escuchaba la colección "blocked_times"
  // ENTERA — los bloqueos de TODOS los locales de TODAS las empresas —
  // en cada visita a cualquier página (incluida la reserva pública).
  // Cada bloqueo nuevo en cualquier barbería se cobraba como lectura en
  // todos los navegadores abiertos. Ahora solo escucha los bloqueos del
  // local que se está mirando (panel y/o reserva pública). Todas las
  // pantallas que los usan (isBlockFree, CustomAgenda, BookingWizard,
  // BlockTimeModal) ya filtraban igual por locationId, así que no cambia
  // nada de lo que se ve.
  const blockLocIdsKey = [...new Set([selectedAdminLocationId, selectedLocation?.id]
    .filter(Boolean).map(String))].sort().join('|');
  useEffect(() => {
    if (!blockLocIdsKey) { setBlockedTimes([]); return; }
    const q = query(collection(db, "blocked_times"), where('locationId', 'in', blockLocIdsKey.split('|')));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Los desbloqueados quedan guardados (deleted: true) solo como
      // registro de quién y cuándo — para la agenda ya no existen.
      const blocksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(b => !b.deleted);

      // Guardamos los bloqueos en el estado
      setBlockedTimes(blocksData);
    });

    // Limpiamos la conexión cuando el componente se desmonta
    return () => unsubscribe();
  }, [blockLocIdsKey]);


  useEffect(() => {
    // 1. Guardamos el ancho inicial para comparar
    let lastWidth = window.innerWidth;

    const handleResize = () => {
      const currentWidth = window.innerWidth;

      // 2. Solo actualizamos si el ANCHO cambió realmente
      // (Esto evita que el scroll vertical dispare el cambio)
      if (currentWidth !== lastWidth) {
        setIsMobile(currentWidth < 768);
        lastWidth = currentWidth;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [isAdminAuth, setIsAdminAuth] = useState(() => {
    // 🔧 FIX: "admin-auth" en localStorage es compartido por TODO el
    // navegador — no por pestaña, ni por página. Antes, esto hacía que
    // CUALQUIER ruta (incluida la reserva pública de cualquier
    // barbería) arrancara asumiendo que el visitante era el admin, con
    // tal de que hubiera una sesión de admin guardada en algún lado de
    // ese navegador — rompiendo la vista de cliente cuando el propio
    // dueño la abría (en la misma pestaña o en una nueva) estando
    // logueado en el panel. Ahora esa sesión guardada solo se respeta
    // si la URL actual es realmente una ruta de administrador.
    try {
      const path = window.location.pathname;
      const esRutaDeAdmin = path === '/login' || path === '/god-mode-2026';
      if (!esRutaDeAdmin) return false;
      return localStorage.getItem('admin-auth') === 'true';
    } catch (e) { return false; } // navegador restringido
  });

  // 📲 Checkbox de "Enviar WhatsApp al cliente" al crear un turno desde
  // el panel admin — reemplaza la regla automática que ataba el envío
  // a si la fecha era pasada o no. Arranca en true (mismo comportamiento
  // de siempre) — el admin lo destilda a mano cuando no quiere avisar
  // (ej: cargando historial viejo, o cualquier otro motivo puntual).
  const [enviarWhatsAppManual, setEnviarWhatsAppManual] = useState(true);

  const [searchPhone, setSearchPhone] = useState('');
  const [myBookings, setMyBookings] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [currentService, setCurrentService] = useState({ id: null, name: '', duration: '', price: '' });
  const [showCashModal, setShowCashModal] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [showReagendar, setShowReagendar] = useState(false); // 📅 modal de reagendar (dentro de Gestionar Reserva)
  useEffect(() => { if (!bookingToEdit) setShowReagendar(false); }, [bookingToEdit]);

  // 🚫 A pedido de Capelli: bloquear el número del cliente directo desde
  // el modal de Gestionar Reserva, sin tener que ir a la Ficha de
  // Cliente aparte. Mismo documento client_profiles (keyed por
  // companyId_teléfono) que ya usa ClientFichaModal.jsx — se carga acá
  // apenas se abre el modal con una reserva, y se vuelve a poner en
  // null al cerrarlo.
  const [clienteBloqueadoInfo, setClienteBloqueadoInfo] = useState(null); // null mientras carga/sin datos | { blocked, reason }
  useEffect(() => {
    if (!bookingToEdit?.client?.phone || !bookingToEdit?.companyId) { setClienteBloqueadoInfo(null); return; }
    const docId = `${bookingToEdit.companyId}_${bookingToEdit.client.phone}`;
    getDoc(doc(db, 'client_profiles', docId))
      .then(snap => {
        if (snap.exists()) {
          setClienteBloqueadoInfo({ blocked: !!snap.data().blocked, reason: snap.data().blockReason || '' });
        } else {
          setClienteBloqueadoInfo({ blocked: false, reason: '' });
        }
      })
      .catch(err => { console.error('Error chequeando bloqueo:', err); setClienteBloqueadoInfo({ blocked: false, reason: '' }); });
  }, [bookingToEdit?.client?.phone, bookingToEdit?.companyId]);

  const handleToggleBlockDesdeModal = async () => {
    if (!bookingToEdit?.client?.phone || !bookingToEdit?.companyId) return;
    const nuevoEstado = !clienteBloqueadoInfo?.blocked;
    if (nuevoEstado) {
      const motivo = window.prompt(`¿Bloquear a ${bookingToEdit.client?.name || 'este cliente'}? Podés escribir un motivo (opcional):`);
      if (motivo === null) return; // canceló el prompt
      try {
        const docId = `${bookingToEdit.companyId}_${bookingToEdit.client.phone}`;
        await setDoc(doc(db, 'client_profiles', docId), {
          companyId: bookingToEdit.companyId,
          phone: bookingToEdit.client.phone,
          blocked: true,
          blockReason: motivo || null,
          blockedAt: Timestamp.now(),
        }, { merge: true });
        setClienteBloqueadoInfo({ blocked: true, reason: motivo || '' });
        addToast('Bloqueado', 'El cliente ya no puede reservar.', 'success');
      } catch (err) {
        console.error('Error bloqueando cliente:', err);
        addToast('Error', 'No se pudo bloquear al cliente.', 'error');
      }
    } else {
      if (!window.confirm(`¿Desbloquear a ${bookingToEdit.client?.name || 'este cliente'}?`)) return;
      try {
        const docId = `${bookingToEdit.companyId}_${bookingToEdit.client.phone}`;
        await setDoc(doc(db, 'client_profiles', docId), {
          companyId: bookingToEdit.companyId,
          phone: bookingToEdit.client.phone,
          blocked: false,
          blockReason: null,
          blockedAt: null,
        }, { merge: true });
        setClienteBloqueadoInfo({ blocked: false, reason: '' });
        addToast('Desbloqueado', 'El cliente puede volver a reservar.', 'success');
      } catch (err) {
        console.error('Error desbloqueando cliente:', err);
        addToast('Error', 'No se pudo desbloquear al cliente.', 'error');
      }
    }
  };
  const [showBarberModal, setShowBarberModal] = useState(false);

  // 📅 Modal de "Agregar Turno" nuevo, propio del Calendario — separado
  // por completo de ClientView.jsx. Guarda { barber, time, date } con
  // los que se abrió (por si vino de tocar un horario puntual en la
  // grilla), o null si se abrió con el botón genérico.
  const [showQuickBookingModal, setShowQuickBookingModal] = useState(false);
  // 🧑‍🔧 Servicio fuera de agenda desde la vista del admin: { fecha, barberoId } o null
  const [fueraDeAgendaAdmin, setFueraDeAgendaAdmin] = useState(null);
  const [quickBookingPrefill, setQuickBookingPrefill] = useState(null);
  const [currentBarber, setCurrentBarber] = useState({ id: null, docId: null, name: '', specialty: '', image: '' });
  const [fileToUpload, setFileToUpload] = useState(null);
  const [bannerToUpload, setBannerToUpload] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPaid, setIsPaid] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekOffset, setWeekOffset] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [viewServicePhoto, setViewServicePhoto] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [liveTime, setLiveTime] = useState(new Date());
  const [lastBookingId, setLastBookingId] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const isInitialLoad = useRef(true);
  const processedBancardRef = useRef(new Set());
  const [logoVersion, setLogoVersion] = useState(Date.now());
  const [shopSettings, setShopSettings] = useState({
    shopName: '', address: 'Av. Principal 123, Asunción', openDays: [1, 2, 3, 4, 5, 6],
    logoUrl: '', openTime: '09:00', closeTime: '19:00', lunchStart: '12:00', lunchEnd: '13:00', requirePayment: false
  });
  const [pendingEmailData, setPendingEmailData] = useState(null);
  const [bancardKeys, setBancardKeys] = useState({ public_key: '', private_key: '' });

  // =====================================================================
  // 💱 PRECIO EN MONEDA ALTERNATIVA (para zonas de frontera)
  // ---------------------------------------------------------------------
  // Ahora es general: cualquier barbería puede activarlo desde
  // Configuración (switch + selector de moneda: Real, Peso argentino,
  // Boliviano o Dólar) — antes era manual y solo para Real brasileño.
  //
  // El tipo de cambio se pide UNA sola vez con base en guaraníes
  // (Gs → todas las monedas de una), y se cachea en Firestore
  // (config/exchange_rates) por hasta 20hs — así aunque tengas 100
  // barberías con esto activado, no se golpea la API de más, y todas
  // comparten la misma tabla de tasas ya calculada.
  // API usada: https://www.exchangerate-api.com/docs — versión CON clave
  // registrada (gratis, hay que crearla una vez en su sitio). Antes
  // usábamos el acceso libre sin cuenta (open.er-api.com) — mismo
  // proveedor, mismo dato, pero la versión con clave es más confiable.
  // ⚠️ OJO: la forma de la respuesta cambia con la clave — acá el campo
  // se llama "conversion_rates", no "rates" como en el acceso libre.
  // =====================================================================
  const [exchangeRates, setExchangeRates] = useState(null); // { USD: 0.00014, BRL: 0.00083, ... } — unidades de esa moneda por 1 Gs

  // 🔑 Reemplazar por la clave gratuita real de exchangerate-api.com
  // (Dashboard → API Key, después de crear la cuenta).
  const EXCHANGE_RATE_API_KEY = 'TU_CLAVE_GRATUITA_ACA';

  const loadExchangeRates = async () => {
    try {
      const ratesRef = doc(db, "config", "exchange_rates");
      const ratesSnap = await getDoc(ratesRef);
      const now = Date.now();
      const VEINTE_HORAS = 20 * 60 * 60 * 1000;

      if (ratesSnap.exists()) {
        const data = ratesSnap.data();
        const updatedAtMs = data.updatedAt?.toMillis ? data.updatedAt.toMillis() : 0;
        if (data.rates && (now - updatedAtMs) < VEINTE_HORAS) {
          setExchangeRates(data.rates);
          return; // Todavía fresco (menos de 20hs) — no hace falta pedir de nuevo
        }
      }

      // Sin valor guardado, o ya pasaron más de 20hs — pedimos uno fresco
      const response = await fetch(`https://v6.exchangerate-api.com/v6/${EXCHANGE_RATE_API_KEY}/latest/PYG`);
      const apiData = await response.json();
      // ✅ FIX: acá el campo se llama "conversion_rates", no "rates" —
      // si no se cambia esto, la respuesta llega bien pero el código
      // nunca la encuentra, y se queda pegado mostrando el valor viejo
      // cacheado (o nada, la primera vez).
      const nuevasRates = apiData?.conversion_rates;

      if (nuevasRates) {
        await setDoc(ratesRef, { rates: nuevasRates, updatedAt: Timestamp.now() });
        setExchangeRates(nuevasRates);
      } else if (ratesSnap.exists()) {
        // La API externa falló, pero había un valor viejo guardado — mejor
        // mostrar ese (aunque tenga más de 20hs) que no mostrar nada
        setExchangeRates(ratesSnap.data().rates || null);
      }
    } catch (error) {
      console.error("❌ Error cargando tipo de cambio:", error);
    }
  };

  useEffect(() => {
    if (shopSettings?.showAltCurrency) {
      loadExchangeRates();
    }
  }, [shopSettings?.showAltCurrency]);

  // =====================================================================
  // 🎫 MEMBRESÍAS — chequeo de cobertura al reservar
  // ---------------------------------------------------------------------
  // Solo actúa si shopSettings.enableMemberships está prendido (switch
  // en Configuración). Busca si el teléfono que está escribiendo el
  // cliente tiene una membresía ACTIVA en este local, y si el/los
  // servicio(s) elegidos + el día de hoy están dentro de lo que cubre
  // su plan. El resultado decide si el turno se cobra normal o queda
  // incluido (Gs. 0) — ver confirmBooking() más abajo, que es donde
  // efectivamente se usa para no cobrar y descontar la visita.
  // =====================================================================
  const [membershipCoverage, setMembershipCoverage] = useState(null);
  // null = no aplica / todavía no se chequeó. Si aplica:
  // { covered: bool, member, plan, reason }

  // ✅ Ahora, además del teléfono, también prueba CI y email (si se le
  // pasan) — mismo criterio que checkMyMembership. Cierra el hueco de
  // ofrecerle "inscribirte" a alguien que YA es socio pero escribió un
  // número distinto al que tiene registrado.
  const checkMembershipCoverage = async (phone, locId, selectedServiceIds, dateStr, ci, email) => {
    if (!shopSettings?.enableMemberships || !locId || (!phone && !ci && !email)) {
      setMembershipCoverage(null);
      return null;
    }
    try {
      // ⚡ Lanzamos las tres búsquedas EN PARALELO (Promise.all) en vez de
      // una atrás de otra — antes, si el teléfono no encontraba nada,
      // había que esperar esa consulta completa antes de siquiera
      // empezar la de CI, y esa antes de la de email. En el caso más
      // común (alguien que no es socio, buscando por las 3 vías) esto
      // significa esperar 3 round-trips seguidos a Firestore en vez de
      // 1 solo tiempo de espera con las 3 corriendo a la vez.
      let member = null;
      const cleanPhone = String(phone || '').replace(/\D/g, '');
      const cleanCi = String(ci || '').replace(/\D/g, '');
      const cleanEmail = String(email || '').trim().toLowerCase();

      const [snapPhone, snapCi, snapEmail] = await Promise.all([
        cleanPhone
          ? getDocs(query(collection(db, 'locations', locId, 'members'), where('clientPhone', 'in', variantesTelefono(cleanPhone)), where('status', '==', 'active')))
          : Promise.resolve(null),
        cleanCi
          ? getDocs(query(collection(db, 'locations', locId, 'members'), where('clientCi', '==', cleanCi), where('status', '==', 'active')))
          : Promise.resolve(null),
        cleanEmail
          ? getDocs(query(collection(db, 'locations', locId, 'members'), where('clientEmail', '==', cleanEmail), where('status', '==', 'active')))
          : Promise.resolve(null),
      ]);

      // Prioridad si coincide por más de una vía: teléfono > CI > email
      // (mismo orden que antes, solo que ahora las 3 ya corrieron juntas).
      if (snapPhone && !snapPhone.empty) member = { id: snapPhone.docs[0].id, ...snapPhone.docs[0].data() };
      else if (snapCi && !snapCi.empty) member = { id: snapCi.docs[0].id, ...snapCi.docs[0].data() };
      else if (snapEmail && !snapEmail.empty) member = { id: snapEmail.docs[0].id, ...snapEmail.docs[0].data() };

      if (!member) { setMembershipCoverage(null); return null; }
      member = visitasDelPeriodo(member, dateStr || undefined);
      const planSnap = await getDoc(doc(db, 'locations', locId, 'membershipPlans', member.planId));
      if (!planSnap.exists()) { setMembershipCoverage(null); return null; }
      const plan = { id: planSnap.id, ...planSnap.data() };

      // 🔧 A pedido: si ya pasó la fecha de renovación (renewalDate) y
      // todavía no pagó, queda SUSPENDIDO — sin acceso a los beneficios
      // de su plan, aunque su status en Firestore siga en 'active' (el
      // status solo cambia cuando el admin cancela la membresía a mano;
      // renewalDate vencida es un estado aparte, de "debe"). Apenas
      // registra el cobro (registrarCobro en MembershipsManagement),
      // renewalDate avanza y recupera el acceso — el día de cobro
      // (ej: el 25) se mantiene siempre fijo ahí, sin importar qué día
      // pague de verdad.
      const hoyParaVencimiento = dateStr || (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
      // 💰 Socio dado de alta/reactivado que todavía no pagó su primer
      // cobro (cicloPagado:false): tampoco tiene cobertura, aunque su
      // renewalDate (alta + 1 mes) esté en el futuro.
      const sinPrimerCobro = member.cicloPagado === false;
      if (sinPrimerCobro || (member.renewalDate && member.renewalDate < hoyParaVencimiento)) {
        const result = {
          covered: false,
          member,
          plan,
          reason: sinPrimerCobro
            ? 'Membresía sin pagar — hay que registrar el primer cobro para activar el acceso.'
            : 'Membresía vencida — hay que registrar el pago para recuperar el acceso.',
          serviciosNoCubiertosIds: (selectedServiceIds || []).filter(id => !String(id).startsWith('plan_')),
          vencida: true,
        };
        setMembershipCoverage(result);
        return result;
      }

      // 🎁 Separamos los servicios elegidos en "bonus" (cupo aparte) vs
      // "principales" (cupo normal de siempre) — un socio puede pedir
      // los dos en la misma reserva, y cada uno se chequea contra su
      // propio cupo, sin mezclarse.
      const bonusIds = plan.bonusServiceIds || [];
      const eligioElPlanDirecto = (selectedServiceIds || []).includes(`plan_${plan.id}`);
      const serviciosElegidos = selectedServiceIds || [];
      const serviciosBonusElegidos = serviciosElegidos.filter(id => bonusIds.includes(id));
      const serviciosPrincipalesElegidos = serviciosElegidos.filter(id => !bonusIds.includes(id));

      const usaCupoPrincipal = serviciosPrincipalesElegidos.length > 0 || eligioElPlanDirecto || serviciosElegidos.length === 0;
      const usaCupoBonus = serviciosBonusElegidos.length > 0;

      // ¿Le queda cupo principal este período?
      const usados = member.visitsUsedThisPeriod || 0;
      const tieneCupoPrincipal = !usaCupoPrincipal || plan.visitsPerMonth === null || usados < plan.visitsPerMonth;

      // ¿Le queda cupo bonus este período?
      const bonusUsados = member.bonusVisitsUsedThisPeriod || 0;
      const bonusPorMes = plan.bonusVisitsPerMonth || 0;
      const tieneCupoBonus = !usaCupoBonus || bonusUsados < bonusPorMes;

      const tieneCupo = tieneCupoPrincipal && tieneCupoBonus;

      // ¿Hoy es un día permitido? (sin restricción = todos los días)
      let diaOk = true;
      if (plan.allowedDays && plan.allowedDays.length > 0 && dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dow = new Date(y, m - 1, d).getDay();
        diaOk = plan.allowedDays.includes(dow);
      }

      // 📅 Límites por día del plan (si los tiene):
      // - cuposPorDia: socios DISTINTOS de este plan con turno ese día,
      //   compartido entre todos. Si el mismo socio ya tiene turno ese
      //   día, no ocupa otro cupo.
      // - maxServiciosPorDia: servicios cubiertos por socio en el día;
      //   lo que pase de eso se cobra aparte (va a serviciosNoCubiertosIds).
      // Se cuenta con las reservas del día del local (sin canceladas ni
      // ausentes). Las reservas viejas no tienen membershipPlanId: para
      // esas se compara por nombre del plan.
      let hayCupoDelDia = true;
      let serviciosRestantesHoy = Infinity;
      if (dateStr && diaOk && (plan.cuposPorDia || plan.maxServiciosPorDia)) {
        const snapDia = await getDocs(query(collection(db, 'bookings'), where('locationId', '==', locId), where('date', '==', dateStr)));
        const turnosDelPlan = snapDia.docs.map(d => d.data()).filter(b =>
          b.membershipId && b.status !== 'cancelled' && b.status !== 'no_show'
          && (b.membershipPlanId ? b.membershipPlanId === plan.id : b.membershipPlanName === plan.name)
        );
        if (plan.cuposPorDia) {
          const otrosSocios = new Set(turnosDelPlan.map(b => b.membershipId).filter(id => id !== member.id));
          hayCupoDelDia = otrosSocios.size < plan.cuposPorDia;
        }
        if (plan.maxServiciosPorDia) {
          const cubreServicio = (id) => !String(id).startsWith('plan_') && !bonusIds.includes(id)
            && (!plan.allowedServiceIds?.length || plan.allowedServiceIds.includes(id));
          const usadosHoy = turnosDelPlan
            .filter(b => b.membershipId === member.id)
            .reduce((n, b) => n + (b.services || []).filter(sv => cubreServicio(sv.id)).length, 0);
          serviciosRestantesHoy = Math.max(0, plan.maxServiciosPorDia - usadosHoy);
        }
      }

      // ✅ FIX: antes, si el cliente agregaba UN servicio que el plan no
      // cubre, la cobertura entera se cortaba — ni siquiera se cubría lo
      // que sí correspondía. Ahora es PARCIAL: los servicios que están
      // en allowedServiceIds salen gratis igual, y solo lo que NO está
      // en la lista se suma al total a pagar aparte. El cupo y el día sí
      // siguen siendo todo-o-nada (si no hay cupo o no es el día, no se
      // cubre nada de esta reserva).
      const sinRestriccionDeServicio = !plan.allowedServiceIds || plan.allowedServiceIds.length === 0;
      let serviciosNoCubiertosIds = [];
      let covered = false;

      if (tieneCupo && diaOk && hayCupoDelDia) {
        if (sinRestriccionDeServicio) {
          covered = true; // el plan no restringe por servicio, cubre cualquier cosa
        } else {
          // ✅ FIX: antes, si eligioElPlanDirecto era true (seleccionó la
          // tarjeta "Black" en sí), se cubría TODO lo elegido sin mirar
          // nada más — si además agregaba un servicio que Black no
          // cubre (ej. Coloración), también salía gratis por error. El
          // pseudo-servicio "plan_{id}" en sí SÍ está cubierto siempre
          // (es la cuota que ya paga), pero cualquier OTRO servicio
          // elegido tiene que seguir estando en allowedServiceIds para
          // ser gratis — si no, se suma aparte igual que cualquier extra.
          serviciosNoCubiertosIds = serviciosPrincipalesElegidos.filter(id =>
            id !== `plan_${plan.id}` && !plan.allowedServiceIds.includes(id)
          );
          const algoCubierto = eligioElPlanDirecto
            || serviciosPrincipalesElegidos.some(id => plan.allowedServiceIds.includes(id))
            || serviciosBonusElegidos.length > 0;
          covered = algoCubierto; // cubre lo que sí entra, aunque sea parcial
        }
      }

      // 📅 Tope de servicios por día: los que pasan del tope se cobran aparte.
      // Si no queda NINGÚN servicio cubierto (ni la tarjeta del plan ni un
      // bonus), la reserva no usa la membresía.
      let excedeServiciosDelDia = false;
      if (covered && serviciosRestantesHoy !== Infinity) {
        const cubiertos = serviciosPrincipalesElegidos.filter(id =>
          !String(id).startsWith('plan_') && !serviciosNoCubiertosIds.includes(id)
        );
        const exceso = cubiertos.slice(serviciosRestantesHoy);
        if (exceso.length > 0) {
          excedeServiciosDelDia = true;
          serviciosNoCubiertosIds = [...serviciosNoCubiertosIds, ...exceso];
          if (exceso.length === cubiertos.length && !eligioElPlanDirecto && serviciosBonusElegidos.length === 0) covered = false;
        }
      }

      let reason = '';
      if (tieneCupo && diaOk && !hayCupoDelDia) reason = `Ya se completaron los ${plan.cuposPorDia} cupos de tu plan para este día — probá otro día`;
      else if (excedeServiciosDelDia) reason = `Tu plan cubre hasta ${plan.maxServiciosPorDia} ${plan.maxServiciosPorDia === 1 ? 'servicio' : 'servicios'} por día — el resto se cobra aparte`;
      else if (!tieneCupoPrincipal && !tieneCupoBonus) reason = 'Sin cupo disponible este mes';
      else if (!tieneCupoPrincipal) reason = 'Sin cupo disponible este mes para tus visitas normales';
      else if (!tieneCupoBonus) reason = 'Ya usaste tu bonus de este mes';
      else if (!diaOk) {
        const diasTexto = plan.allowedDays.map(d => weekDayNames[d]).join(', ');
        reason = `Tu plan aplica: ${diasTexto}`;
      } else if (!covered) {
        reason = 'Ese servicio no está incluido en tu plan';
      } else if (serviciosNoCubiertosIds.length > 0) {
        reason = 'Algunos de estos servicios no están en tu plan — se cobran aparte';
      }

      // 🚫 sinCupo: ya usó todas las visitas de su plan en este período —
      // la vista del cliente muestra un aviso antes de confirmar (se cobra
      // el precio normal, o puede renovar el plan).
      const sinCupo = usaCupoPrincipal && !tieneCupoPrincipal;
      const result = { covered, member, plan, reason, serviciosNoCubiertosIds, sinCupo };
      setMembershipCoverage(result);
      return result;
    } catch (e) {
      console.error('❌ Error chequeando membresía:', e.message);
      setMembershipCoverage(null);
      return null;
    }
  };

  // =====================================================================
  // 🎁 Descuenta el cupo correcto (principal y/o bonus) de un socio al
  // completarse un turno. Mira los servicios REALES de la reserva contra
  // lo que el plan define como bonus — si la reserva mezcla un servicio
  // normal con el bonus en la misma visita, descuenta los dos cupos a la
  // vez, cada uno el suyo. Reemplaza la lógica que antes vivía duplicada
  // en updateBookingStatus y en el guardado de Editar Reserva.
  // =====================================================================
  const incrementarVisitaMembresia = async (locId, membershipId, bookingServices, bookingId) => {
    try {
      const memberRef = doc(db, 'locations', locId, 'members', membershipId);
      const memberSnap = await getDoc(memberRef);
      if (!memberSnap.exists()) return;
      const memberGuardado = memberSnap.data();
      const member = visitasDelPeriodo(memberGuardado);

      let usaPrincipal = true; // default: si no hay plan/bonus definido, es el cupo principal
      let usaBonus = false;

      if (member.planId) {
        const planSnap = await getDoc(doc(db, 'locations', locId, 'membershipPlans', member.planId));
        if (planSnap.exists()) {
          const plan = planSnap.data();
          const bonusIds = plan.bonusServiceIds || [];
          const serviceIds = (bookingServices || []).map(s => s.id).filter(Boolean);
          const esServicioDelPlanDirecto = serviceIds.some(id => String(id).startsWith('plan_'));
          const serviciosBonus = serviceIds.filter(id => bonusIds.includes(id));
          const serviciosPrincipales = serviceIds.filter(id => !bonusIds.includes(id));
          usaBonus = serviciosBonus.length > 0;
          usaPrincipal = serviciosPrincipales.length > 0 || esServicioDelPlanDirecto || serviceIds.length === 0;
        }
      }

      // Si tocaba un reinicio por pago adelantado, los contadores arrancan
      // en 0 y la visita se suma encima.
      const updateData = { ...camposReinicioVisitas(memberGuardado), updatedAt: Timestamp.now() };
      if (usaPrincipal) updateData.visitsUsedThisPeriod = (member.visitsUsedThisPeriod || 0) + 1;
      if (usaBonus) updateData.bonusVisitsUsedThisPeriod = (member.bonusVisitsUsedThisPeriod || 0) + 1;

      await addDoc(collection(db, 'locations', locId, 'members', membershipId, 'visits'), {
        date: format(new Date(), 'yyyy-MM-dd'),
        bookingId: bookingId || null,
        registeredManually: false,
        usaBonus,
        createdAt: Timestamp.now()
      });
      await updateDoc(memberRef, updateData);
    } catch (e) {
      console.error('⚠️ No se pudo descontar la visita de la membresía:', e.message);
    }
  };

  // Dispara el chequeo automáticamente cuando el teléfono queda completo
  // (10 díg PY o 11 díg BR) y ya hay servicios/fecha elegidos — no hace
  // falta que el cliente haga nada más. También reacciona si carga CI o
  // email antes que el teléfono (ej: vino del cuadro "¿Tenés membresía?"
  // y ya tiene esos datos, aunque el teléfono todavía no esté completo).
  useEffect(() => {
    if (!shopSettings?.enableMemberships) { setMembershipCoverage(null); return; }
    const cleanPhone = (clientInfo.phone || '').replace(/\D/g, '');
    const largoCompleto = cleanPhone.length === 10 || cleanPhone.length === 11;
    const tieneCiOEmail = !!(clientInfo.ci || clientInfo.email);
    if ((!largoCompleto && !tieneCiOEmail) || !selectedLocation?.id || selectedServices.length === 0) {
      setMembershipCoverage(null);
      return;
    }
    checkMembershipCoverage(cleanPhone, selectedLocation.id, selectedServices.map(s => s.id), selectedDate, clientInfo.ci, clientInfo.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo.phone, clientInfo.ci, clientInfo.email, selectedLocation?.id, selectedServices, selectedDate, shopSettings?.enableMemberships]);

  // =====================================================================
  // 🎫 PLANES DE MEMBRESÍA DEL LOCAL
  // ---------------------------------------------------------------------
  // El cliente escribe su número en el cuadro "¿Tenés membresía?" y, si
  // es socio, se le marca su plan. 🔒 Las altas de socios las hace SOLO
  // el admin desde Membresías — desde la vista pública ya no se puede
  // inscribir nadie.
  // =====================================================================
  const [membershipPlansAll, setMembershipPlansAll] = useState([]);
  useEffect(() => {
    if (!shopSettings?.enableMemberships || !selectedLocation?.id) { setMembershipPlansAll([]); return; }
    const plansRef = collection(db, 'locations', String(selectedLocation.id), 'membershipPlans');
    getDocs(plansRef)
      .then(snap => setMembershipPlansAll(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(err => { console.error('❌ Error cargando planes:', err.message); setMembershipPlansAll([]); });
  }, [shopSettings?.enableMemberships, selectedLocation?.id]);

  // Lo que efectivamente se cobra se resuelve más abajo, junto a
  // totalPrice (effectiveTotalPrice = 0 si está cubierto, si no el
  // precio normal) — acá arriba `totalPrice` todavía no existe.

  // --- ESTADO PARA EL MODAL DE BLOQUEO ---
  const [blockModalConfig, setBlockModalConfig] = useState({
    isOpen: false, barber: null, dateStr: '', startTime: '', endTime: ''
  });

  // --- CORRECCIÓN DE TEMA (PERSISTENCIA AL RECARGAR) ---
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('barberAppTheme');
      return savedTheme !== null ? JSON.parse(savedTheme) : true; // Por defecto true (oscuro)
    } catch {
      return true;
    }
  });

  // Guardar en LocalStorage cada vez que cambie
  useEffect(() => {
    try { localStorage.setItem('barberAppTheme', JSON.stringify(isDarkMode)); }
    catch (e) { /* navegador restringido (ej: Instagram/Facebook in-app) — no persiste, no rompe nada */ }
  }, [isDarkMode]);

  // Sincronizar con DB para cambiar de tema dinámicamente
  useEffect(() => {
    // Ya no exigimos que exista el nombre, solo nos fijamos en la preferencia del tema
    if (shopSettings.theme) {
      const dbIsDark = shopSettings.theme === 'dark';
      if (dbIsDark !== isDarkMode) {
        setIsDarkMode(dbIsDark);
      }
    }
  }, [shopSettings.theme]);


  const themeClasses = {
    mainBg: isDarkMode
      ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white"
      : "bg-[#F8FAFC] text-slate-900",
    cardBg: isDarkMode
      ? "bg-gray-800 border-gray-700 shadow-2xl"
      : "bg-white border-slate-200 shadow-sm",
    textTitle: isDarkMode ? "text-white" : "text-slate-900",
    textMuted: isDarkMode ? "text-gray-400" : "text-slate-500",
    inputBg: isDarkMode ? "bg-gray-900/80 border-gray-700" : "bg-white border-slate-300 text-slate-900"
  };

  // --- GENERAR ENLACE PARA CALENDARIO DEL CLIENTE ---
  const generateGoogleCalendarLink = () => {
    if (!selectedDate || !selectedTime) return "#";
    try {
      const startDateTime = new Date(`${selectedDate}T${selectedTime}:00`);
      const duration = totalDuration || 30;
      const endDateTime = new Date(startDateTime.getTime() + (duration * 60000));
      const formatGCal = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, "");
      const start = formatGCal(startDateTime);
      const end = formatGCal(endDateTime);
      const title = encodeURIComponent(`Cita en ${shopSettings.shopName || 'Barbería'}`);
      const barberName = selectedBarber ? selectedBarber.name : "Barbero";
      const serviceList = selectedServices && selectedServices.length > 0
        ? selectedServices.map(s => s.name).join(', ')
        : 'Servicio de Barbería';
      const details = encodeURIComponent(
        `Servicios: ${serviceList}\n` +
        `Barbero: ${barberName}\n` +
        `Ticket: #${lastBookingId ? lastBookingId.slice(-5) : 'Pendiente'}\n\n` +
        `¡Te esperamos!`
      );
      const location = encodeURIComponent(shopSettings.address || 'Barbería');
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&sf=true&output=xml`;
    } catch (error) {
      console.error("Error generando link calendario:", error);
      return "#";
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 100,
      behavior: 'smooth'
    });
  }, [step]);

  // =====================================================================
  // FUNCIÓN activarNotificaciones — reemplazá la que está en App.js
  // Esta versión registra el SW correctamente y guarda el token en Firestore
  // =====================================================================

  const activarNotificaciones = async () => {
    try {
      // 1. Verificar soporte
      if (!('Notification' in window)) {
        addToast("No soportado", "Tu navegador no soporta notificaciones.", "error");
        return;
      }
      if (!('serviceWorker' in navigator)) {
        addToast("No soportado", "Tu navegador no soporta Service Workers.", "error");
        return;
      }
      // 🛡️ "messaging" puede estar en null si este navegador no soporta
      // Firebase Messaging (ver firebase.js) — se avisa de forma clara,
      // en vez de dejar que getToken() truene más abajo con un error
      // genérico.
      if (!messaging) {
        addToast("No soportado", "Este navegador no soporta notificaciones push. Probá desde Chrome o desde la app instalada.", "error");
        return;
      }

      // 2. Pedir permiso
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        addToast("Permiso denegado", "Activá las notificaciones en la configuración del navegador.", "error");
        return;
      }

      // 3. Registrar el Service Worker (si no está registrado ya)
      let swRegistration;
      try {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/'
        });
        // Esperamos a que esté activo
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.error('❌ Error registrando SW:', swError);
        addToast("Error SW", "No se pudo registrar el service worker.", "error");
        return;
      }

      // 4. Obtener token FCM
      const currentToken = await getToken(messaging, {
        vapidKey: "BJCNJftN7SPOBUF-04UsPJhNxL8mRqp3TNmXZw69hf3q_r8KdRQrrZpw0TMsvsJkQiYDXVmqhZKmB8Wl3D45FVM",
        serviceWorkerRegistration: swRegistration
      });

      if (!currentToken) {
        addToast("Error", "No se pudo obtener el token de notificación.", "error");
        return;
      }

      // 5. Guardar token en Firestore
      const locId = selectedAdminLocationId || locations[0]?.id || null;

      if (currentToken && currentUser) {
        const deviceId = `${currentUser.uid}_${navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop'}`;
        await setDoc(doc(db, "admin_tokens", deviceId), {
          token: currentToken,
          email: currentUser.email,
          role: currentUser.role,
          barberId: currentUser.barberId || null,
          locationId: locId,
          uid: currentUser.uid,
          updatedAt: Timestamp.now()
        });
        myFCMTokenRef.current = currentToken;
        addToast("🔔 Notificaciones Activas", "Vas a recibir alertas cuando lleguen reservas.", "success");
      }

    } catch (error) {
      console.error("❌ Error activando notificaciones:", error);
      addToast("Error", "No se pudieron activar las notificaciones: " + error.message, "error");
    }
  };

  // 🛡️ "messaging" ahora se resuelve de forma asíncrona en firebase.js
  // (puede quedar en null si el navegador no lo soporta, o tardar un
  // instante en estar listo). Se reintenta unas pocas veces con una
  // pausa corta — si después de eso sigue en null, asumimos que este
  // navegador no lo soporta y no insistimos más (el resto de la app
  // sigue funcionando igual, solo sin notificaciones push acá).
  useEffect(() => {
    if (!isAdminAuth) return;

    let unsubscribe = null;
    let cancelado = false;

    const intentarSuscribir = (intentos = 0) => {
      if (cancelado) return;
      if (messaging) {
        unsubscribe = onMessage(messaging, (payload) => {
          // 🔇 Sonido desactivado temporalmente
          if (navigator.vibrate) navigator.vibrate([500, 200, 500]);

          const title = payload.data?.title || '¡Nueva Reserva!';
          const body = payload.data?.body || '';
          addToast(title, body, 'success', false);
        });
      } else if (intentos < 20) {
        setTimeout(() => intentarSuscribir(intentos + 1), 200);
      }
    };

    intentarSuscribir();

    return () => {
      cancelado = true;
      if (unsubscribe) unsubscribe();
    };
  }, [isAdminAuth, addToast]);


  useEffect(() => { selectedLocationRef.current = selectedAdminLocationId; }, [selectedAdminLocationId]);
  useEffect(() => { locationsRef.current = locations; }, [locations]);

  const uploadImage = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) { resolve(null); return; }
      const cloudName = process.env.REACT_APP_CLOUDINARY_NAME;
      const uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET;
      if (!cloudName || !uploadPreset) {
        reject("Error de configuración de Cloudinary");
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      setUploadProgress(30);
      fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          if (data.secure_url) {
            setUploadProgress(100);
            // Entregamos la URL limpia (Cloudinary ya la comprimirá desde adentro)
            resolve(data.secure_url);
          } else {
            reject(data.error || 'Fallo la subida a Cloudinary');
          }
        })
        .catch(error => {
          setUploadProgress(0);
          reject(error);
        });
    });
  };

  // 📸 Galería "Antes / Después" de la ficha de cliente — a propósito
  // sube a una cuenta de Cloudinary SEPARADA de la de los logos
  // (Cloud Name: dhqxgttpp, Upload Preset: ficha_clientes). Al ser una
  // función multi-barbería con potencialmente muchas fotos por
  // cliente, se aísla en su propia cuenta para no arriesgar el límite
  // de almacenamiento compartido con el branding de todas las
  // barberías. Requiere las variables de entorno
  // REACT_APP_CLOUDINARY_GALLERY_NAME y REACT_APP_CLOUDINARY_GALLERY_PRESET
  // cargadas en el .env (no hardcodeadas acá, mismo criterio que uploadImage).
  const uploadGalleryImage = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) { resolve(null); return; }
      const cloudName = process.env.REACT_APP_CLOUDINARY_GALLERY_NAME;
      const uploadPreset = process.env.REACT_APP_CLOUDINARY_GALLERY_PRESET;
      if (!cloudName || !uploadPreset) {
        reject("Error de configuración de Cloudinary (galería de fichas)");
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          if (data.secure_url) {
            resolve(data.secure_url);
          } else {
            reject(data.error || 'Fallo la subida de la foto');
          }
        })
        .catch(error => reject(error));
    });
  };

  // --- 🆕 GENERAR HORARIOS DINÁMICOS ---
  // ✅ FIX (parte 2): el problema no era solo que timeSlots ignorara el
  // horario variable — ADEMÁS, más abajo, isBlockFree() tenía su PROPIO
  // chequeo de apertura/cierre usando shopSettings.openTime/closeTime
  // (el horario global) de forma completamente independiente. Entonces
  // timeSlots ya generaba bien los horarios desde las 09:00, pero
  // isBlockFree los volvía a filtrar contra el horario global (10:00) y
  // los rechazaba igual. Para que esto no se vuelva a desincronizar,
  // ahora hay UN SOLO lugar que decide el horario efectivo de un día
  // (getScheduleForDate), y tanto timeSlots/lunchSlots como isBlockFree
  // lo usan — no hay una segunda copia de esta lógica dando vueltas.
  const getScheduleForDate = (dateStr) => {
    let openTime = shopSettings.openTime;
    let closeTime = shopSettings.closeTime;
    let lunchStart = shopSettings.lunchStart;
    let lunchEnd = shopSettings.lunchEnd;

    if (shopSettings.hasVariableDays && dateStr) {
      try {
        const dayOfWeek = parse(dateStr, 'yyyy-MM-dd', new Date()).getDay();
        const daySchedule = shopSettings.variableSchedule?.[dayOfWeek];
        if (daySchedule) {
          openTime = daySchedule.openTime || openTime;
          closeTime = daySchedule.closeTime || closeTime;
          lunchStart = daySchedule.lunchStart || lunchStart;
          lunchEnd = daySchedule.lunchEnd || lunchEnd;
        }
      } catch (e) { /* fecha todavía no válida, seguimos con el horario global */ }
    }
    // 🏠 A domicilio se atiende hasta la hora propia de domicilio (22:00 por defecto)
    if (modoDomicilio) closeTime = getDomicilioConfig(shopSettings).horaFin || closeTime;
    return { openTime, closeTime, lunchStart, lunchEnd };
  };

  const timeSlots = useMemo(() => {
    const { openTime, closeTime } = getScheduleForDate(selectedDate);

    if (!openTime || !closeTime) return [];
    const slots = [];
    const referenceDate = new Date();
    let current = parse(openTime, 'HH:mm', referenceDate);
    const end = parse(closeTime, 'HH:mm', referenceDate);
    let safetyGuard = 0;
    while (isBefore(current, end) && safetyGuard < 50) {
      slots.push(format(current, 'HH:mm'));
      current = addMinutes(current, SLOT_DURATION);
      safetyGuard++;
    }
    return slots;
  }, [shopSettings.openTime, shopSettings.closeTime, shopSettings.hasVariableDays, shopSettings.variableSchedule, shopSettings.domicilio, modoDomicilio, selectedDate]);

  const lunchSlots = useMemo(() => {
    const { lunchStart, lunchEnd } = getScheduleForDate(selectedDate);

    if (!lunchStart || !lunchEnd) return [];
    const slots = [];
    const referenceDate = new Date();
    let current = parse(lunchStart, 'HH:mm', referenceDate);
    const end = parse(lunchEnd, 'HH:mm', referenceDate);
    let safetyGuard = 0;
    while (isBefore(current, end) && safetyGuard < 10) {
      slots.push(format(current, 'HH:mm'));
      current = addMinutes(current, SLOT_DURATION);
      safetyGuard++;
    }
    return slots;
  }, [shopSettings.lunchStart, shopSettings.lunchEnd, shopSettings.hasVariableDays, shopSettings.variableSchedule, selectedDate]);

  // ⏱️ Duración real de un servicio para ESTE barbero puntual — si el
  // barbero tiene una duración personalizada cargada para ese servicio
  // (serviceDurationOverrides), se usa esa; si no, la estándar del
  // servicio. Centralizado acá para que el cálculo sea SIEMPRE
  // consistente, sin importar desde qué flujo se reserve.
  const getServiceDuration = (service, barber) => {
    const override = barber?.serviceDurationOverrides?.[service.id];
    return (override !== undefined && override !== null && override !== '') ? Number(override) : Number(service.duration) || 0;
  };

  // 🔧 FIX: la agenda SIEMPRE bloquea en múltiplos de SLOT_DURATION (30
  // min) — antes se guardaba/mostraba la suma cruda de los servicios
  // (ej: 115 min) mientras el calendario en realidad bloqueaba 120 min
  // (4 slots de 30). Esa diferencia hacía que la tarjeta de la reserva
  // mostrara un horario de fin distinto al que de verdad estaba ocupado.
  // Centralizado acá para que TODO lo que se guarda en Firestore como
  // totalDuration sea ya el número real bloqueado, sin importar desde
  // qué flujo se genere la reserva.
  const roundDurationToSlots = (rawMinutes) => Math.ceil((Number(rawMinutes) || 0) / SLOT_DURATION) * SLOT_DURATION;

  // ⏱️ A pedido: si el cliente está cubierto por un plan CON duración
  // fija cargada (ej: Eco 30 min, Black 1 hora, Silver 40 min), esa
  // duración manda para TODO el turno — sin importar qué servicio real
  // haya elegido. Sin esto, un socio que elige un servicio de 30 min
  // Y tiene el pseudo-servicio del plan auto-agregado (también con
  // duración propia) terminaba sumando las dos duraciones, bloqueando
  // el doble de tiempo del que correspondía. Sin duracionFija cargada
  // en el plan, sigue el comportamiento de siempre (suma cada servicio
  // elegido) — no rompe nada para planes que no usan esto.
  const planCoberturaConDuracionFija = membershipCoverage?.covered && membershipCoverage?.plan?.duracionFija !== undefined && membershipCoverage?.plan?.duracionFija !== null;
  const totalDuration = planCoberturaConDuracionFija
    ? Number(membershipCoverage.plan.duracionFija)
    : selectedServices.reduce((sum, s) => sum + getServiceDuration(s, selectedBarber), 0);
  // 🔧 A pedido — bug encontrado: el pseudo-servicio del plan
  // ("plan_{id}", que representa la membresía en sí — se auto-selecciona
  // cuando se reconoce a un socio) tiene price = precio de la CUOTA
  // MENSUAL del plan (ej: Gs 199.000), no el precio de un servicio real.
  // Como este cálculo sumaba TODOS los selectedServices sin distinguir,
  // si el socio además tenía un servicio real elegido, el total "normal"
  // (el que se muestra tachado) terminaba sumando cuota + servicio —
  // mostrando un precio tachado inflado y sin sentido. Ahora se excluye
  // el pseudo-servicio de este total: no es un cargo real de esta
  // visita, es solo el marcador de "esta reserva usa la membresía".
  const totalPrice = selectedServices.reduce((sum, s) => s.isMembershipPlan ? sum : sum + (Number(s.price) || 0), 0);
  // ✅ FIX: antes esto era todo-o-nada (0 si covered, precio completo si
  // no) — si el socio agregaba UN servicio fuera del plan, se cobraba
  // TODO de nuevo, ni siquiera lo que sí estaba cubierto. Ahora es
  // parcial: lo que está en el plan sale gratis, y solo se suma el
  // precio de los servicios que NO están en serviciosNoCubiertosIds
  // (calculado en checkMembershipCoverage, mirando servicio por servicio
  // contra allowedServiceIds del plan).
  //
  // 🔧 A pedido: lo cubierto por el plan ya NO sale en 0 — usa el
  // "precio de socio" que se carga por servicio (memberPrice), o el
  // precio normal si ese servicio no tiene uno cargado. Esto es lo que
  // termina guardado como totalPrice del turno, así que de acá sale
  // resuelto, sin cambios en ningún otro lado: el barbero ve el valor
  // real en vez de "Gs 0", y las comisiones se calculan sobre ese
  // mismo valor. El pseudo-servicio del plan en sí (isMembershipPlan)
  // sigue siempre en 0 — no es un servicio real, es solo el marcador de
  // "esta visita usa la membresía".
  const serviciosNoCubiertosIds = membershipCoverage?.serviciosNoCubiertosIds || [];
  // 🔧 A pedido (Cabo): en un plan con duración fija, la visita ya está
  // paga con la cuota mensual — un servicio cubierto por el plan vale
  // Gs 0 para el cliente (no su precio de lista ni un "precio de socio").
  // Solo lo que NO cubre el plan se suma aparte. Los planes sin duración
  // fija (tipo Capelli) siguen igual que siempre, con su memberPrice.
  const planCoberturaFija = membershipCoverage?.plan?.duracionFija !== undefined && membershipCoverage?.plan?.duracionFija !== null;
  const effectiveTotalPrice = !membershipCoverage?.covered
    ? totalPrice
    : selectedServices.reduce((sum, s) => {
        if (s.isMembershipPlan) return sum;
        if (serviciosNoCubiertosIds.includes(s.id)) return sum + Number(s.price || 0);
        if (planCoberturaFija) return sum; // cubierto por plan de duración fija = Gs 0
        const precioSocio = (s.memberPrice !== undefined && s.memberPrice !== null && s.memberPrice !== '')
          ? Number(s.memberPrice)
          : Number(s.price || 0);
        return sum + precioSocio;
      }, 0);
  const slotsNeeded = Math.ceil(totalDuration / SLOT_DURATION);

  const getBookingSlots = (startTime, slotsNeeded) => {
    const slotsToBook = [];
    let currentIndex = timeSlots.indexOf(startTime);
    if (currentIndex === -1) return null;
    while (slotsToBook.length < slotsNeeded) {
      if (currentIndex >= timeSlots.length) return null;
      const currentSlotTime = timeSlots[currentIndex];
     if (shopSettings?.hasBreak && lunchSlots.includes(currentSlotTime)) {
  currentIndex++;
  continue;
}
      slotsToBook.push(currentSlotTime);
      currentIndex++;
    }
    return slotsToBook;
  };

  const getEndTime = (startTime, totalDuration) => {
    const numSlots = Math.ceil(totalDuration / SLOT_DURATION);
    const slotsToBook = getBookingSlots(startTime, numSlots);
    if (!slotsToBook || slotsToBook.length === 0) return "N/A";
    const lastSlot = slotsToBook[slotsToBook.length - 1];
    let [hour, min] = lastSlot.split(':').map(Number);
    min += SLOT_DURATION;
    if (min >= 60) {
      hour += 1;
      min = 0;
    }
    return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  };

  // Asegúrate de que la función ahora reciba 'shopSettings' (o si ya es una variable global en tu App.js, no hace falta pasarla)
const isBlockFree = (date, startTimeIndex, slotsNeeded, barberId) => {
  const startTime = timeSlots[startTimeIndex];
  const slotsToBook = getBookingSlots(startTime, slotsNeeded);

  if (!slotsToBook) return false;

  // --- 🛡️ ESCUDOS DE CONFIGURACIÓN DEL LOCAL (shopSettings) ---
  if (shopSettings) {
    // 1. Validar Día de la Semana (0 = Domingo, 1 = Lunes...)
    const [year, month, day] = date.split('-');
    const dateObj = new Date(year, month - 1, day);
    const dayOfWeek = dateObj.getDay();

    if (shopSettings.openDays && !shopSettings.openDays.includes(dayOfWeek)) {
      return false; // 🚫 Si el día no está en la lista de abiertos, bloqueamos todo
    }

    // ✅ FIX: antes acá se comparaba directo contra shopSettings.openTime/
    // closeTime/lunchStart/lunchEnd (el horario GLOBAL), sin importar el
    // horario variable del día — por eso timeSlots ya generaba bien los
    // horarios desde las 09:00, pero este chequeo los volvía a rechazar
    // igual contra el horario global (10:00). Ahora usa el mismo horario
    // efectivo del día (getScheduleForDate) que ya usan timeSlots/lunchSlots.
    const { openTime: openDelDia, closeTime: closeDelDia, lunchStart: lunchStartDelDia, lunchEnd: lunchEndDelDia } = getScheduleForDate(date);

    // Validar horarios para cada fracción de tiempo del servicio
    for (const slotTime of slotsToBook) {
      // 2. Validar Horario de Apertura y Cierre
      if (openDelDia && slotTime < openDelDia) return false;
      if (closeDelDia && slotTime >= closeDelDia) return false;

      // 3. Validar Horario de Almuerzo (Receso)
      if (shopSettings.hasBreak && lunchStartDelDia && lunchEndDelDia) {
        if (slotTime >= lunchStartDelDia && slotTime < lunchEndDelDia) {
          return false;
        }
      }
    }
  }

  // Convertimos IDs a texto para evitar problemas matemáticos
  const currentLocId = String(selectedLocation?.id || '').trim();
  const currentBarberId = String(barberId || '').trim();

  // 🍽️ ESCUDO 3b: Almuerzo propio del barbero (BarberModal → lunchStart/
  // lunchEnd). Cada slot ocupa [hora, hora + 30 min) — si se pisa aunque
  // sea un minuto con el almuerzo, el horario no está libre.
  const barberoDelTurno = (barbers || []).find(b => String(b.id).trim() === currentBarberId);
  if (barberoDelTurno?.lunchStart && barberoDelTurno?.lunchEnd) {
    const aMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const almIni = aMin(barberoDelTurno.lunchStart);
    const almFin = aMin(barberoDelTurno.lunchEnd);
    const pisaAlmuerzo = slotsToBook.some(t => aMin(t) < almFin && aMin(t) + SLOT_DURATION > almIni);
    if (pisaAlmuerzo) return false;
  }

  for (const slotTime of slotsToBook) {

    // 🛡️ ESCUDO 4: Bloqueos manuales del Administrador
    const isBlockedByAdmin = (blockedTimes || []).some(block => {
      const isSameLocation = String(block.locationId || '').trim() === currentLocId;
      const isSameBarber = String(block.barberId) === currentBarberId;
      const isSameDate = block.date === date;
      const isWithinTime = slotTime >= block.startTime && slotTime < block.endTime;

      return isSameLocation && isSameBarber && isSameDate && isWithinTime;
    });

    if (isBlockedByAdmin) return false;

    // 🛡️ ESCUDO 5: Reservas Normales Ya Ocupadas
    // ✅ FIX: cada slot de 30 min de una reserva es un documento
    // Firestore APARTE, con su propio campo "time" (ese slot puntual:
    // 15:00, 15:30, 16:00...). "startTime" en cambio es SIEMPRE el
    // mismo para todos los slots de una misma reserva — es la hora en
    // que empezó el turno completo. Antes esto comparaba contra
    // "b.startTime", así que solo el PRIMER slot de cada reserva se
    // detectaba como ocupado; el resto (15:30, 16:00, etc. — incluidos
    // los slots nuevos que crea addServiceToBooking al agregar un
    // servicio extra desde el admin) quedaba invisible para este
    // chequeo, y el cliente los veía disponibles aunque ya estuvieran
    // tomados. Ahora compara contra "time", que es el campo correcto.
    const candidatosDelDia = (bookings || []).filter(b => b.date === date && String(b.barber?.id || '').trim() === currentBarberId);
    const isSlotOccupied = candidatosDelDia.some(b => {
      const bLocId = String(b.locationId || '').trim();

      // 🔧 FIX: algunos turnos (probablemente creados por una vía vieja,
      // antes de la arquitectura multi-slot) quedaron con "time" vacío
      // en su documento principal, aunque sí tenían "startTime" guardado
      // correctamente. Como este chequeo comparaba solo contra "time",
      // esos turnos nunca se detectaban como ocupados y el horario se
      // veía libre para el cliente, aunque ya estuviera reservado. Ahora
      // cae en "startTime" si "time" no está.
      const bTime = b.time || b.startTime;

      return (
        bLocId === currentLocId &&
        bTime === slotTime &&
        b.status !== 'cancelled' &&
        b.status !== 'no_show' &&
        // 🔧 A pedido: un servicio "fuera de agenda" (cargado manualmente
        // por el barbero, ya como completado) no debe bloquear ningún
        // horario para reservas futuras — se registra para el reporte y
        // las estadísticas, pero no ocupa lugar en la agenda real.
        !b.isManualEntry
      );
    });

    if (isSlotOccupied) return false;

  }

  // ✅ Si sobrevive a todos los escudos, el horario está libre
  return true;
};

// =====================================================================
// 🔓 isBlockFreeForzado — a pedido, para la función de "agendar fuera
// de horario" del admin (a veces vienen clientes fuera de hora y el
// dueño decide atenderlos igual, cobrando un extra). A propósito NO
// usa timeSlots ni getBookingSlots (esas dos dependen de que el
// horario ya exista adentro del rango normal de apertura/cierre) —
// arma sus propios horarios consecutivos a mano, sin importar si caen
// fuera del horario del local o en el almuerzo. Solo sigue chequeando
// lo que SÍ importa siempre: que no choque con otro bloqueo manual o
// con otra reserva ya existente de ese barbero.
// =====================================================================
const isBlockFreeForzado = (date, startTime, slotsNeeded, barberId) => {
  const slotsToBook = [];
  let [h, m] = startTime.split(':').map(Number);
  for (let i = 0; i < slotsNeeded; i++) {
    slotsToBook.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    m += SLOT_DURATION;
    if (m >= 60) { h += 1; m -= 60; }
  }

  const currentLocId = String(selectedLocation?.id || '').trim();
  const currentBarberId = String(barberId || '').trim();

  for (const slotTime of slotsToBook) {
    // 🛡️ Bloqueos manuales del administrador
    const isBlockedByAdmin = (blockedTimes || []).some(block => {
      const isSameLocation = String(block.locationId || '').trim() === currentLocId;
      const isSameBarber = String(block.barberId) === currentBarberId;
      const isSameDate = block.date === date;
      const isWithinTime = slotTime >= block.startTime && slotTime < block.endTime;
      return isSameLocation && isSameBarber && isSameDate && isWithinTime;
    });
    if (isBlockedByAdmin) return false;

    // 🛡️ Reservas ya existentes de ese barbero
    const candidatosDelDia = (bookings || []).filter(b => b.date === date && String(b.barber?.id || '').trim() === currentBarberId);
    const isSlotOccupied = candidatosDelDia.some(b => {
      const bLocId = String(b.locationId || '').trim();
      const bTime = b.time || b.startTime;
      // 🔧 Mismo criterio que isBlockFree — un servicio fuera de agenda
      // no debe bloquear horarios futuros.
      return bLocId === currentLocId && bTime === slotTime && b.status !== 'cancelled' && b.status !== 'no_show' && !b.isManualEntry;
    });
    if (isSlotOccupied) return false;
  }

  return true;
};

// 🕐 Horarios EXTENDIDOS — el día completo cada 30 min (00:00 a 23:30),
// para el selector de "agendar fuera de horario". Independiente de
// timeSlots (que sigue acotado al horario normal del local, para
// clientes y el uso del día a día) — este es solo para cuando el admin
// activa el modo forzado en "Agregar Turno".
const timeSlotsExtendido = useMemo(() => {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += SLOT_DURATION) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
}, []);

  const handleSelectService = (service) => {
    setSelectedServices((prevServices) => {
      const isSelected = prevServices.some(s => s.id === service.id);
      if (isSelected) return prevServices.filter(s => s.id !== service.id);
      else return [...prevServices, service];
    });
  };

  const showAlert = (title, message) => {
    const type = title.toLowerCase().includes('error') ? 'error' : 'info';
    addToast(title, message, type);
  };

  // 🏠 Guarda solo la configuración de domicilio (desde la vista Servicios),
  // con merge para no pisar el resto de los ajustes del local.
  const saveDomicilio = async (domicilio) => {
    if (!selectedAdminLocationId) return addToast("Atención", "Selecciona un local primero.", "info");
    try {
      await setDoc(doc(db, "locations", selectedAdminLocationId, "settings", "general"), { domicilio, updatedAt: Timestamp.now() }, { merge: true });
      setShopSettings(prev => ({ ...prev, domicilio }));
      addToast("Listo", "Servicio a domicilio guardado.", "success");
    } catch (e) {
      console.error('Error guardando domicilio:', e);
      addToast("Error", "No se pudo guardar el servicio a domicilio.", "error");
    }
  };

  // C. GUARDAR CONFIGURACIÓN (Con Sincronización Automática y Blindaje Anti-Errores)
  const saveSettings = async () => {
    if (!selectedAdminLocationId) return addToast("Atención", "Selecciona un local primero.", "info");

    // 🛡️ 1. VALIDACIÓN PREVENTIVA: Evitamos guardar campos vacíos que rompen la app.
    // Si el local nunca guardó su horario, la pantalla muestra 09:00–21:00 por
    // defecto (o con días variables solo se editan los días): se guarda eso
    // mismo en vez de frenar el guardado con un horario que "ya se ve" cargado.
    const horaApertura = shopSettings.openTime || "09:00";
    const horaCierre = shopSettings.closeTime || "21:00";
    if (horaApertura >= horaCierre) {
      return addToast("Atención", "La hora de cierre tiene que ser después de la de apertura.", "info");
    }

    if (!shopSettings.openDays || shopSettings.openDays.length === 0) {
      return addToast("Atención", "Debes seleccionar al menos un día de trabajo.", "info");
    }

    // 🩺 A pedido: avisar (sin bloquear el guardado) si el nuevo
    // horario dejaría a alguna reserva VIGENTE (de hoy en adelante,
    // sin cancelar) "adentro" de un rango que ahora quedaría bloqueado
    // — sea el nuevo almuerzo, o fuera del nuevo horario de apertura/
    // cierre. Esto es justo lo que generó el turno "oculto" de Cabo:
    // se cambió el almuerzo, una reserva quedó atrapada adentro, y
    // nadie se enteró hasta que un cliente no pudo reservar ahí. La
    // reserva vieja NUNCA se toca — esto es solo un aviso informativo.
    try {
      const hoyStr = format(new Date(), 'yyyy-MM-dd');
      const nuevoOpen = horaApertura;
      const nuevoClose = horaCierre;
      const nuevoHasBreak = !!shopSettings.hasBreak;
      const nuevoLunchStart = nuevoHasBreak ? (shopSettings.lunchStart || "12:00") : null;
      const nuevoLunchEnd = nuevoHasBreak ? (shopSettings.lunchEnd || "13:00") : null;

      const afectadas = (bookings || []).filter(b => {
        if (b.locationId !== selectedAdminLocationId) return false;
        if (b.status === 'cancelled' || b.status === 'no_show') return false;
        if (!b.date || b.date < hoyStr) return false;
        const t = b.time || b.startTime;
        if (!t) return false;
        // 🏠 Los turnos a domicilio cierran a su propia hora (ej: 22:00), no a la del local
        const cierre = b.modalidad === 'domicilio' ? (getDomicilioConfig(shopSettings).horaFin || nuevoClose) : nuevoClose;
        const fueraDeHorario = t < nuevoOpen || t >= cierre;
        const dentroDeAlmuerzo = nuevoHasBreak && t >= nuevoLunchStart && t < nuevoLunchEnd;
        return fueraDeHorario || dentroDeAlmuerzo;
      });

      if (afectadas.length > 0) {
        const nombres = [...new Set(afectadas.map(b => b.client?.name).filter(Boolean))].slice(0, 5);
        window.alert(
          `⚠️ Aviso: con este nuevo horario, ${afectadas.length} turno(s) ya agendado(s) van a quedar dentro del horario bloqueado (almuerzo o fuera de apertura/cierre).\n\n` +
          (nombres.length > 0 ? `Algunos: ${nombres.join(', ')}${afectadas.length > nombres.length ? '...' : ''}\n\n` : '') +
          `Esos turnos NO se van a tocar ni cancelar solos — pero convendría revisarlos manualmente (podés usar "Turnos Ocultos" en el Calendario para encontrarlos rápido). El horario se va a guardar igual.`
        );
      }
    } catch (e) {
      console.error('Error validando conflictos de horario:', e);
      // No cortamos el guardado por esto — es solo un aviso informativo.
    }

    setLoading(true);
    try {
      let finalLogoUrl = shopSettings.logoUrl || null;
      let finalBannerUrl = shopSettings.bannerUrl || null;

      // Subida de imágenes a Cloudinary
      if (fileToUpload) finalLogoUrl = await uploadImage(fileToUpload);
      if (bannerToUpload) finalBannerUrl = await uploadImage(bannerToUpload);

      // 🛡️ 2. CONSTRUCCIÓN DEL OBJETO SEGURO
      // Usamos fallbacks por si acaso algún campo quedó undefined
      // 🔐 AUDITORÍA DE SEGURIDAD — hallazgo grave: shopSettings incluye el
      // objeto "sifen" de la empresa (con el token de la API de facturación),
      // y como settings/general es de lectura PÚBLICA, cada vez que el admin
      // guardaba los ajustes ese token quedaba escrito ahí, legible por
      // cualquiera. Se saca antes de guardar. (Como esto reemplaza el
      // documento completo, también limpia el token de los locales donde ya
      // se hubiera filtrado, la próxima vez que se guarden sus ajustes.)
      // eslint-disable-next-line no-unused-vars
      const { sifen: _sifenNoPublico, ...shopSettingsPublico } = shopSettings;
      const newSettings = {
        ...shopSettingsPublico,
  openTime: horaApertura,
  closeTime: horaCierre,
  openDays: shopSettings.openDays || [1, 2, 3, 4, 5, 6],
  lunchStart: shopSettings.hasBreak ? (shopSettings.lunchStart || "12:00") : "",
  lunchEnd: shopSettings.hasBreak ? (shopSettings.lunchEnd || "13:00") : "",
        logoUrl: finalLogoUrl,
        bannerUrl: finalBannerUrl,
        updatedAt: Timestamp.now()
      };

      // 1. Guardar info pública en el local actual
      await setDoc(doc(db, "locations", selectedAdminLocationId, "settings", "general"), newSettings);

      // 2. Operaciones Multi-Tenant
      const locationDoc = await getDoc(doc(db, "locations", selectedAdminLocationId));

      if (locationDoc.exists()) {
        const locData = locationDoc.data();
        const compId = locData.companyId;
        const locSlug = locData.slug || "";

        // A) Sincronizar Bancard Secret
        if (compId) {
          await setDoc(doc(db, "companies", compId, "secrets", "bancard"), {
            public_key: bancardKeys.public_key || '',
            private_key: bancardKeys.private_key || '',
            environment: bancardKeys.environment || 'staging',
            updatedAt: Timestamp.now()
          });
        }

        // B) Sincronizar Marca en sucursales hermanas
        // 🔧 FIX: mismo criterio estricto que en ClientViewDynamic — acá
        // es más delicado todavía, porque sincroniza cosas sensibles
        // (claves de Bancard, logo, color de marca). Si compId existe,
        // SOLO se compara por compId — nada de "el slug empieza igual",
        // que podía enganchar por error una sucursal de OTRA barbería.
        const baseSlug = locSlug.replace(/-\d+$/, '');
        const siblingLocations = compId
          ? locations.filter(l => l.companyId === compId)
          : locations.filter(l => baseSlug && l.slug && l.slug.startsWith(baseSlug));

        if (siblingLocations.length > 0) {
          const batch = writeBatch(db);
          siblingLocations.forEach((sibling) => {
            const locSettingsRef = doc(db, "locations", sibling.id, "settings", "general");
            batch.set(locSettingsRef, {
              logoUrl: finalLogoUrl,
              bannerUrl: finalBannerUrl,
              brandColor: shopSettings.brandColor || '#F59E0B',
              shopName: shopSettings.shopName || sibling.name
            }, { merge: true });
          });
          await batch.commit();
        }
      }

      setShopSettings(newSettings);
      setFileToUpload(null);
      setBannerToUpload(null);
      addToast("¡Guardado!", "Configuración y Marca actualizadas correctamente.", "success");

    } catch (e) {
      console.error("Error al guardar ajustes:", e);
      addToast("Error", "No se pudieron guardar los cambios.", "error");
    } finally {
      setLoading(false);
    }
  };


  const loadCategories = async (locationId) => {
    if (!locationId) return;
    try {
      const q = query(collection(db, 'locations', locationId, 'categories'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      const catsData = [];
      querySnapshot.forEach((doc) => {
        catsData.push({ id: doc.id, ...doc.data() });
      });

      if (catsData.length > 0) {
        setCategories(catsData);
      } else {
        setCategories([
          { id: 'def1', name: 'Cabello' },
          { id: 'def2', name: 'Barba' },
          { id: 'def3', name: 'Combos' },
          { id: 'def4', name: 'Otros' }
        ]);
      }
    } catch (error) {
      console.error("Error cargando categorías:", error);
    }
  };

  // --- GUARDAR / ACTUALIZAR CATEGORÍA ---
  const saveCategory = async () => {
    if (!newCategoryName.trim()) return;
    if (!selectedAdminLocationId) return;

    setLoading(true);
    try {
      if (editingCategory) {
        // ACTUALIZAR
        const catRef = doc(db, 'locations', selectedAdminLocationId, 'categories', editingCategory.id);
        await updateDoc(catRef, { name: newCategoryName.trim() });
        addToast("¡Actualizado!", "Categoría modificada correctamente.", "success");
      } else {
        // CREAR
        await addDoc(collection(db, 'locations', selectedAdminLocationId, 'categories'), {
          name: newCategoryName.trim(),
          createdAt: Timestamp.now()
        });
        addToast("¡Creado!", "Categoría agregada con éxito.", "success");
      }

      setNewCategoryName('');
      setEditingCategory(null);
      await loadCategories(selectedAdminLocationId);

    } catch (error) {
      console.error(error);
      addToast("Error", "No se pudo guardar la categoría.", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm("¿Eliminar categoría? Los servicios quedarán como 'General'.")) return;

    setLoading(true);
    try {
      if (catId.startsWith('def')) {
        setCategories(prev => prev.filter(c => c.id !== catId));
      } else {
        await deleteDoc(doc(db, 'locations', selectedAdminLocationId, 'categories', catId));
        await loadCategories(selectedAdminLocationId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async (userParam = currentUser) => {
    // 🛡️ ELIMINAMOS el "if (!userParam) return" porque los clientes 
    // necesitan cargar los locales para que el link funcione.

    // 🔧 Marca esta llamada como la más reciente — cualquier llamada
    // anterior que todavía esté en vuelo, al llegar, va a notar que ya
    // no es la última y se va a descartar sola (ver más abajo).
    const myRequestId = ++locationsRequestRef.current;

    setLoadingLocations(true);
    try {
      let q;

      // 1. 👑 SUPERADMIN (Ve todos los locales)
      if (userParam?.role === 'superadmin' || userParam?.email === 'joelgomez451@gmail.com') {
        q = query(collection(db, 'locations'), orderBy('name', 'asc'));
      }
      // 2. 👤 DUEÑO / BARBERO (Ve solo los locales de su empresa)
      else if (userParam?.companyId) {
        q = query(
          collection(db, 'locations'),
          where('companyId', '==', userParam.companyId)
        );
      }
      // 3. 🌍 VISITANTE / CLIENTE (Carga locales públicos para encontrar el slug)
      else {
        q = query(collection(db, 'locations'), orderBy('name', 'asc'));
      }

      const querySnapshot = await getDocs(q);

      // 🔧 Si mientras esta consulta estaba en vuelo se disparó OTRA
      // llamada más nueva a loadLocations, esta respuesta ya quedó
      // vieja — se descarta sin tocar ningún estado, para no pisar el
      // resultado de la llamada correcta y más reciente.
      if (myRequestId !== locationsRequestRef.current) return;

      const locationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      locationsData.sort((a, b) => (a.position || 99) - (b.position || 99));
      setLocations(locationsData);

      // ✅ FIX: Incluir 'barber' para que también setee el local automáticamente
      if (userParam && (userParam.role === 'owner' || userParam.role === 'admin' || userParam.role === 'barber')) {
        if (locationsData.length > 0) {
          const isStillMine = locationsData.some(l => l.id === selectedAdminLocationId);
          if (!isStillMine) {
            setSelectedAdminLocationId(locationsData[0].id);
          }
        }
      }

    } catch (error) {
      console.error("Error cargando locales:", error);
      locationsPublicasPrecargadasRef.current = false; // permite un reintento desde el listener de auth
    } finally {
      if (myRequestId === locationsRequestRef.current) setLoadingLocations(false);
    }
  };
  // 💸 FIX DE COSTOS: loadBookings antes volvía a bajar TODO el historial
  // de reservas de la empresa (miles de documentos, cada uno cobrado como
  // lectura) después de cada acción del panel: cambiar un estado, iniciar
  // o terminar un trabajo, agregar/quitar un servicio, reagendar, iniciar
  // sesión... Era redundante: el escuchador en tiempo real de más abajo
  // ("ESCUCHADOR DE RESERVAS EN TIEMPO REAL") ya recibe cada alta, cambio
  // y baja al instante — incluidas las escrituras hechas desde este mismo
  // navegador — y actualiza "bookings" solo. Se deja la función (sin
  // hacer nada) para no tocar todos los lugares que la llaman.
  const loadBookings = async () => {};

  const handleManualSaveLocation = async () => {
    if (!newLocationInfo.name || !newLocationInfo.address) {
      return showAlert("Faltan datos", "El nombre y la dirección son obligatorios.");
    }

    setLoading(true);

    try {
      let finalImageUrl = newLocationInfo.image;

      if (fileToUpload) {
        try {
          finalImageUrl = await uploadImage(fileToUpload);
        } catch (uploadError) {
          console.error("Error subiendo imagen:", uploadError);
          setLoading(false);
          return showAlert("Error", "No se pudo subir el logo.");
        }
      }

      let finalBannerUrl = shopSettings.bannerUrl || null;
      if (bannerToUpload) {
        try {
          finalBannerUrl = await uploadImage(bannerToUpload);
        } catch (uploadError) {
          console.error("Error subiendo banner:", uploadError);
          setLoading(false);
          return showAlert("Error", "No se pudo subir la imagen del banner.");
        }
      }

      const generatedSlug = newLocationInfo.name
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-');

      // 🔍 DEBUG: Verificar companyId antes de guardar

      const locationData = {
        name: newLocationInfo.name,
        slug: generatedSlug,
        address: newLocationInfo.address,
        mapUrl: newLocationInfo.mapUrl || '',
        googleReviewsUrl: newLocationInfo.googleReviewsUrl || '',
        googlePlaceId: newLocationInfo.googlePlaceId || '',
        googlePlaceName: newLocationInfo.googlePlaceName || '',
        image: finalImageUrl || '',
        companyId: currentUser?.companyId || null,
        updatedAt: Timestamp.now()
      };


      // ⭐ Trae al instante la calificación y reseñas de Google del local
      // recién vinculado (si no, recién aparecían con la actualización
      // diaria de las 05:00). Si falla, no frena el guardado del local.
      const refrescarResenasGoogle = async (locId) => {
        try {
          await httpsCallable(functions, 'refrescarResenasGoogle')({ locationId: locId });
        } catch (eResenas) {
          console.error('⚠️ No se pudieron traer las reseñas de Google:', eResenas.message);
        }
      };

      if (editingLocationId) {
        const docRef = doc(db, "locations", editingLocationId);
        await updateDoc(docRef, locationData);
        await refrescarResenasGoogle(editingLocationId);
        addToast("Actualizado", "Local modificado correctamente.", "success");
      } else {
        const nuevoDoc = await addDoc(collection(db, "locations"), {
          ...locationData,
          createdAt: Timestamp.now()
        });
        if (locationData.googlePlaceId) await refrescarResenasGoogle(nuevoDoc.id);

        // Limpiar y cerrar
        setShowLocationModal(false);
        setEditingLocationId(null);
        setNewLocationInfo({ name: '', address: '', image: '', mapUrl: '', googleReviewsUrl: '' });
        setFileToUpload(null);

        // Recargar locales
        await loadLocations(currentUser);

        // Seleccionar el nuevo local directamente (sin query extra)
        setSelectedAdminLocationId(nuevoDoc.id);
        setBarbers([]);
        setServices([]);
        setCategories([]);
        return;
      }

      // Solo para edición llega acá
      setShowLocationModal(false);
      setEditingLocationId(null);
      setNewLocationInfo({ name: '', address: '', image: '', mapUrl: '', googleReviewsUrl: '' });
      setFileToUpload(null);
      await loadLocations(currentUser);

    } catch (error) {
      console.error("❌ Error guardando local:", error);
      showAlert("Error", "Ocurrió un problema al guardar en la base de datos.");
    } finally {
      setLoading(false);
    }
  };

  const loadPlan = async () => {
    setIsPlanLoading(true);
    try {
      const docRef = doc(db, "config", "plan");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        // Normalizamos a minúsculas para evitar errores si escribiste "Premium" o "PREMIUM"
        const level = data.level ? data.level.toLowerCase() : 'basic';

        setCurrentPlan({ ...data, level: level });
      } else {
        console.warn("⚠️ No se encontró el documento 'config/plan'. Usando Básico por defecto.");
        // Si no existe el documento, creamos uno básico automáticamente para evitar errores futuros
        const basicPlan = { level: 'basic', maxBarbers: 3 };
        await setDoc(docRef, basicPlan);
        setCurrentPlan(basicPlan);
      }
    } catch (error) {
      console.error("❌ Error cargando el plan:", error);
      setCurrentPlan({ level: 'basic', maxBarbers: 3 });
    } finally {
      setIsPlanLoading(false);
    }
  };



  // ⚡ A pedido: opts.desdeCachePublico = carga ADELANTADA para un visitante
  // que ya entró antes a este link (ver "Precarga de locales" más abajo) —
  // en ese caso el id del local viene de lo guardado en el navegador, no
  // de la lista "locations" (que todavía no llegó), así que se saltea el
  // chequeo de pertenencia. Devuelve true si salió bien (o si otra llamada
  // más nueva tomó la posta), false si falló.
  const loadBarbers = async (locationId, opts = {}) => {
    if (!locationId) { setBarbers([]); return false; }

    // 🔧 FIX DE SEGURIDAD: antes, mientras "locations" todavía no había
    // cargado, este chequeo se saltaba por completo ("dejamos pasar") —
    // eso significaba que, en esa ventana breve justo después del login,
    // un locationId equivocado (de OTRA barbería) podía colarse sin
    // validación, y se llegaban a mostrar barberos que no eran de esta
    // empresa. Ahora el chequeo es SIEMPRE estricto: si "locations"
    // todavía no cargó, isMyLocation da false (ninguna coincidencia en un
    // array vacío) y simplemente no se muestra nada todavía — más seguro
    // que mostrar datos de otro local, aunque sea por un instante. El
    // useEffect que llama a esta función ahora también reacciona a
    // "locations" (ver más abajo), así que apenas termine de cargar,
    // se reintenta solo con los datos ya validados.
    const isMyLocation = locations.some(l => l.id === locationId);
    if (!opts.desdeCachePublico && !isMyLocation && currentUser?.role !== 'superadmin') {
      setBarbers([]);
      return false;
    }

    // 🔧 FIX DE CARRERA: mismo criterio que loadLocations — si esta
    // llamada queda "vieja" mientras está en vuelo (porque se disparó
    // otra más nueva mientras tanto, por ejemplo al restaurar la
    // sesión), su respuesta se descarta al llegar en vez de pisar los
    // barberos correctos que ya se habían cargado.
    const myRequestId = ++barbersRequestRef.current;

    setBarbers([]);
    try {
      const q = query(collection(db, 'locations', locationId, 'barbers'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      if (myRequestId !== barbersRequestRef.current) return true; // respuesta vieja, se ignora (otra llamada más nueva se encarga)
      let barbersData = querySnapshot.docs.map(doc => ({
        docId: doc.id,
        id: doc.id,
        ...doc.data()
      }));
      setBarbers(barbersData);
      setBarberosListosDe(locationId);
      return true;
    } catch (error) { console.error('Error barberos:', error); setBarberosListosDe(locationId); return false; }
  };
  const loadServices = async (locationId, opts = {}) => {
    // 🔧 Mismo fix de seguridad que loadBarbers — sin bypass mientras
    // locations todavía no cargó (salvo la carga adelantada para visitantes
    // que ya entraron antes: opts.desdeCachePublico, ver loadBarbers).
    const isMyLocation = locations.some(l => l.id === locationId);
    if (!opts.desdeCachePublico && !isMyLocation && currentUser?.role !== 'superadmin') {
      setServices([]);
      return false;
    }

    if (!locationId) { setServices([]); return false; }

    setServices([]);
    try {
      // 🔧 FIX: antes esto usaba orderBy('name','asc') en la consulta de
      // Firestore — pero para poder ordenar por un campo nuevo "order"
      // (numérico, que carga el admin a mano), hay que ordenar del lado
      // del cliente, no en la consulta. Si usáramos orderBy('order',...)
      // directo en Firestore, CUALQUIER servicio que todavía no tenga
      // ese campo cargado (todos los servicios existentes, antes de este
      // cambio) directamente NO aparecería en los resultados — Firestore
      // excluye los documentos que no tienen el campo por el que se
      // ordena. Por eso se trae todo sin orderBy, y se ordena acá:
      // primero los que tienen "order" (por ese número), después los que
      // no lo tienen, en orden alfabético — así nadie pierde servicios
      // de la nada.
      const q = query(collection(db, 'locations', locationId, 'services'));
      const querySnapshot = await getDocs(q);
      const servicesData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      servicesData.sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : Infinity;
        const orderB = typeof b.order === 'number' ? b.order : Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
      });
      setServices(servicesData);
      return true;
    } catch (error) { console.error("Error servicios:", error); return false; }
  };

  // =====================================================================
  // 🔀 REORDENAR SERVICIOS (arrastrar y soltar) — recibe la lista YA
  // reordenada (Framer Motion la arma sola al soltar, en cualquier
  // posición nueva, no solo intercambiando vecinos) y guarda el
  // "order" de CADA servicio según su posición nueva, en un solo batch.
  // Actualización optimista: la pantalla ya se ve reordenada antes de
  // que termine de guardar.
  // =====================================================================
  const reorderServicesFull = async (locationId, nuevaListaOrdenada) => {
    if (!locationId) return;
    setServices(nuevaListaOrdenada);
    try {
      const batch = writeBatch(db);
      nuevaListaOrdenada.forEach((s, index) => {
        if (!s?.id) return;
        batch.update(doc(db, 'locations', locationId, 'services', s.id), { order: index });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error reordenando servicios:", error);
      addToast("Error", "No se pudo guardar el nuevo orden.", "error");
    }
  };

  const loadProducts = async (locationId) => {
    if (!locationId) { setProducts([]); return; }

    // 🛡️ FIX: este chequeo bloqueaba productos si el locationId no
    // aparecía en el estado "locations" — pero el flujo de login de
    // 🔧 FIX: antes, mientras "locations" todavía no cargaba, este
    // chequeo se saltaba (bajo el argumento de que locationId siempre
    // viene de código interno, nunca de un input del usuario). Aunque
    // eso es cierto, igual dejaba una ventana breve donde podían
    // mostrarse productos de OTRA empresa — el mismo problema que
    // loadBarbers/loadServices, solo que acá con productos. Se saca el
    // bypass por consistencia: mientras locations no cargó, no se
    // muestra nada todavía, en vez de arriesgar mostrar datos ajenos.
    const isMyLocation = locations.some(l => l.id === locationId);
    if (!isMyLocation && currentUser?.role !== 'superadmin') {
      setProducts([]);
      return;
    }

    setProducts([]);
    try {
      const q = query(collection(db, 'locations', locationId, 'products'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      const productsData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setProducts(productsData);
    } catch (error) { console.error("Error cargando productos:", error); }
  };

  // ⚡ A pedido (velocidad de carga): antes esta función hacía CINCO lecturas
  // a Firebase una atrás de otra (local → empresa → ajustes → llaves de
  // Bancard → la empresa DE NUEVO solo para el dato de SIFEN), y además se
  // llamaba dos veces al entrar. Ahora:
  //  - si quien llama ya tiene los datos del local (locationData — lo
  //    tienen handleSelectLocation y ClientViewDynamic, que lo sacan de la
  //    lista de locales), no se vuelve a leer el local;
  //  - la empresa, los ajustes del local y las llaves de Bancard son
  //    independientes entre sí, así que se piden JUNTAS (Promise.all);
  //  - la empresa se lee UNA sola vez (antes dos) y de ahí salen tanto el
  //    nombre como el dato de SIFEN.
  // Resultado: de 5 viajes seguidos a 1 (o 2 si no se pasa locationData).
  // Mismo resultado final que antes en shopSettings y bancardKeys.
  const loadSettings = async (locationId, locationData = null) => {
    if (!locationId) return;
    try {
      let locData = locationData;
      if (!locData) {
        const locationDoc = await getDoc(doc(db, "locations", locationId));
        if (!locationDoc.exists()) return;
        locData = locationDoc.data();
      }
      const compId = locData.companyId;

      const [compSnap, docSnap, secretSnap] = await Promise.all([
        // Si alguna de estas dos falla (ej: permisos), no tira abajo a las
        // demás: antes una falla en la empresa cortaba TODO y no cargaba
        // ni los ajustes del local.
        compId ? getDoc(doc(db, "companies", compId)).catch(() => null) : Promise.resolve(null),
        getDoc(doc(db, "locations", locationId, "settings", "general")),
        // 🔐 AUDITORÍA DE SEGURIDAD: las llaves de Bancard solo se piden en
        // sesiones del panel (dueño/admin). Antes las leía TODO visitante, aunque
        // la vista del cliente nunca las usa — y quedaban en la memoria de su
        // navegador (y legibles por cualquiera, ya que la regla era abierta).
        (compId && isAdminAuth) ? getDoc(doc(db, "companies", compId, "secrets", "bancard")).catch(() => null) : Promise.resolve(null),
      ]);

      // 1. 🔍 Nombre de la empresa (prioridad alta)
      let companyName = "Mi Barbería";
      if (compSnap && compSnap.exists()) {
        companyName = compSnap.data().name;
      }

      // 2. Configuración personalizada del local
      if (docSnap.exists()) {
        const data = docSnap.data();
        setShopSettings({
          ...data,
          // ✅ Prioridad: settings.shopName → companies.name → locData.name
          shopName: data.shopName || companyName || locData.name || 'Mi Barbería',
          logoUrl: data.logoUrl || '',
          openDays: Array.isArray(data.openDays) ? data.openDays : [1, 2, 3, 4, 5, 6]
        });
        if (data.theme) setIsDarkMode(data.theme === 'dark');
      } else {
        // Si no hay settings, usamos el nombre de la empresa o el nombre del local
        setShopSettings({
          shopName: companyName || locData.name || 'Mi Barbería',
          address: locData.address || 'Dirección pendiente',
          openDays: [1, 2, 3, 4, 5, 6],
          logoUrl: '',
          openTime: '09:00',
          closeTime: '19:00',
          lunchStart: '12:00',
          lunchEnd: '13:00',
          requirePayment: false
        });
        setIsDarkMode(true);
      }

      // 3. Llaves de Bancard
      if (secretSnap && secretSnap.exists()) setBancardKeys(secretSnap.data());

      // 4. SIFEN (sale de la misma lectura de la empresa de arriba)
      if (compSnap && compSnap.exists()) {
        const compData = compSnap.data();
        // 🔐 AUDITORÍA DE SEGURIDAD: antes se mezclaba el objeto sifen COMPLETO
        // (con el token de la API de facturación) en shopSettings para TODO
        // visitante. Un visitante solo necesita saber si está habilitado;
        // el objeto completo queda para las sesiones del panel.
        // 🔐 Revisado con FacturacionModal: el navegador (incluido el panel)
        // solo usa ruc y razonSocial para armar el PDF — el token de la API
        // (apiKey) NUNCA se usa desde acá, lo usa el servidor de facturación.
        // Por eso se saca para todos, también para el panel.
        const { apiKey: _apiKeySifen, ...sifenSinToken } = compData.sifen || {};
        setShopSettings(prev => ({
          ...prev,
          sifen: isAdminAuth ? { habilitado: false, ...sifenSinToken } : { habilitado: !!compData.sifen?.habilitado }
        }));
      }

    } catch (error) {
      console.error("Error al cargar ajustes:", error);
    }
  };




  const {
    handleSaveLocation, handleDeleteLocation,
    handleSaveService, handleDeleteService,
    handleSaveBarber, handleDeleteBarber
  } = useCrudActions(setLoading, addToast, () => {
    // 🔧 A pedido — bug encontrado: esto antes solo recargaba
    // servicios/barberos/locales si el admin estaba PARADO en esa
    // pestaña exacta en el momento de guardar. Si por cualquier motivo
    // esa condición no se cumplía (ej: guardando la duración
    // personalizada de un barbero desde otro lugar, o cualquier
    // desajuste de timing), el estado local (services/barbers) se
    // quedaba con el valor VIEJO — y como las reservas nuevas se
    // calculan a partir de ese estado en memoria (no leyendo Firestore
    // de nuevo cada vez), terminaban usando la duración antigua sin
    // que hubiera ningún error visible. Ahora se recargan los tres
    // siempre, sin condición — son lecturas livianas, no hay motivo
    // real para restringirlo por pestaña.
    loadLocations();
    loadServices(selectedAdminLocationId);
    loadBarbers(selectedAdminLocationId);
  });

  // 1. Hook de pago (limpio y blindado con Doble Escudo)
  usePaymentCheck(setLoading, addToast, loadBookings, (data) => {
    // Nos aseguramos de tener un ID de ticket válido
    const ticketId = data?.bookingGroupId || data?.id;
    if (!ticketId) return;

    // 🛡️ ESCUDO DOBLE (Memoria RAM + Disco) 🛡️
    // Si el ID ya está en nuestra memoria RAM o en el navegador, cortamos de raíz.
    // (try/catch: en navegadores restringidos como Instagram/Facebook in-app,
    // sessionStorage puede tirar error en vez de simplemente no persistir —
    // sin esto, la app se cae acá antes de poder mostrar nada)
    let yaMarcadoEnDisco = false;
    try { yaMarcadoEnDisco = !!sessionStorage.getItem(`wa_bancard_${ticketId}`); } catch (e) { /* navegador restringido */ }
    if (processedBancardRef.current.has(ticketId) || yaMarcadoEnDisco) {
      return;
    }

    // Lo marcamos como enviado instantáneamente en ambas memorias
    processedBancardRef.current.add(ticketId);
    try { sessionStorage.setItem(`wa_bancard_${ticketId}`, 'true'); } catch (e) { /* navegador restringido */ }

    // A. Limpiamos la URL INMEDIATAMENTE
    if (window.location.search.includes('payment_status=success')) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    setSelectedDate(data.date);
    setSelectedTime(data.time);
    setClientInfo(data.client);
    setSelectedBarber(data.barber);
    setSelectedServices(data.services || []);
    setIsPaid(true);
    setLastBookingId(ticketId);

    setShowSuccess(true); // Activa el modal de confeti
    try { localStorage.removeItem('paymentOrigin'); localStorage.removeItem('paymentTicket'); } catch (e) { /* navegador restringido */ }

    // B. Mandamos a la sala de espera para correos
    if (data.client?.email) {
      setPendingEmailData(data);
    }

    // 🔕 Ya no se manda WhatsApp de confirmación al aprobarse el pago: el
    // cliente ve el turno confirmado en la pantalla de éxito, y los
    // servidores mandan solo el recordatorio con botones.
  });

  // 👇 ✉️ EL FRANCOTIRADOR DE CORREOS (Pon esto justo debajo del bloque anterior) 👇
  useEffect(() => {
    // SOLO ACTÚA SI hay un correo en espera Y el logo de la empresa ya cargó en la página
    if (pendingEmailData && shopSettings && shopSettings.logoUrl) {
      const ticket = pendingEmailData.bookingGroupId;

      // ESCUDO DEFINITIVO: Si la memoria del navegador dice que ya lo enviamos, se detiene.
      let yaEnviado = false;
      try {
        yaEnviado = !!sessionStorage.getItem(`email_sent_${ticket}`);
        if (!yaEnviado) sessionStorage.setItem(`email_sent_${ticket}`, 'true');
      } catch (e) { /* navegador restringido — sin este escudo, pero no rompe el envío */ }
      if (yaEnviado) return;

      // Calculamos totales
      const tDuration = (pendingEmailData.services || []).reduce((sum, s) => sum + Number(s.duration), 0);
      const tPrice = (pendingEmailData.services || []).reduce((sum, s) => sum + (Number(s.price) || 0), 0);

      // Buscamos la sucursal correcta
      const loc = (locations && locations.length > 0)
        ? (locations.find(l => l.id === pendingEmailData.locationId) || locations[0])
        : {};

      // Disparamos el correo con el 100% de la información lista (incluido el logo dorado)
      enviarConfirmacion({
        clientInfo: pendingEmailData.client,
        ticketId: ticket,
        shopSettings: shopSettings,
        selectedLocation: loc,
        selectedBarber: pendingEmailData.barber,
        selectedDate: pendingEmailData.date,
        selectedTime: pendingEmailData.time,
        totalDuration: tDuration,
        selectedServices: pendingEmailData.services || [],
        totalPrice: tPrice,
        type: 'confirmed'
      }).catch(err => console.error("Error enviando email tras pago online:", err));

      // Vaciamos la sala de espera
      setPendingEmailData(null);
    }
  }, [pendingEmailData, shopSettings, locations]);

  const notificarNuevaReserva = async (booking) => {
    try {
      const token = myFCMTokenRef.current;

      if (!token) {
        console.warn('⚠️ Sin token FCM en memoria — activá notificaciones primero');
        return;
      }


      const response = await fetch('https://barbergo-whatsapp-api.onrender.com/api/notificar-reserva', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tokens: [token],
          title: '¡Nueva Reserva! 💈',
          body: `${booking.client?.name || 'Cliente'} - ${booking.startTime || booking.time}`,
          data: {
            bookingId: booking.bookingGroupId || booking.id || '',
            locationId: booking.locationId || ''
          }
        })
      });

      const result = await response.json();

    } catch (error) {
      console.error('❌ Error en notificarNuevaReserva:', error);
    }
  };


  const confirmBooking = async () => {
    if (!clientInfo.name || !clientInfo.phone) return showAlert("Faltan Datos", "Completa nombre y teléfono.");
    if (phoneError) return showAlert("Error", "Teléfono inválido.");
    // 🔢 Resguardo final: aunque el teléfono haya llegado por otro camino
    // (autocompletado desde una ficha de socio, etc.), tiene que ser un
    // número de largo razonable (PY o extranjero, ej. Brasil).
    if (!telefonoValido(clientInfo.phone)) return showAlert("Teléfono inválido", "Revisá el número de teléfono (ej: 0981234567).");
    if (!selectedLocation || !selectedBarber) return showAlert("Error", "Falta seleccionar local o barbero.");

    // 🏠 A domicilio: ciudad habilitada + dirección obligatorias; la ubicación
    // (GPS o link pegado) es opcional, pero tiene que caer dentro de Gran Asunción.
    let domicilioParaGuardar = null;
    if (modoDomicilio) {
      if (!domicilioDisponible(enabledFeatures, shopSettings)) return showAlert("No disponible", "Esta barbería no está tomando servicios a domicilio.");
      const { ciudades } = getDomicilioConfig(shopSettings);
      const ciudad = datosDomicilio.ciudad;
      const direccion = (datosDomicilio.direccion || '').trim();
      if (!ciudad || !ciudades.includes(ciudad)) return showAlert("Faltan datos", "Elegí tu ciudad. Solo atendemos a domicilio en Gran Asunción.");
      if (!direccion) return showAlert("Faltan datos", "Escribí la dirección donde te atendemos.");
      const tieneGps = typeof datosDomicilio.lat === 'number' && typeof datosDomicilio.lng === 'number';
      if (tieneGps && !dentroDeGranAsuncion(datosDomicilio.lat, datosDomicilio.lng)) {
        return showAlert("Fuera de zona", "Tu ubicación está fuera de Gran Asunción. Solo atendemos a domicilio dentro de esa zona.");
      }
      domicilioParaGuardar = {
        ciudad,
        direccion,
        referencia: (datosDomicilio.referencia || '').trim(),
        lat: tieneGps ? datosDomicilio.lat : null,
        lng: tieneGps ? datosDomicilio.lng : null,
        // 'gps' = ubicación actual del celular · 'link' = link que pegó el cliente
        origenUbicacion: (tieneGps || datosDomicilio.linkUbicacion) ? (datosDomicilio.origenUbicacion || 'gps') : null,
        linkUbicacion: datosDomicilio.linkUbicacion || '',
      };
      domicilioParaGuardar.mapsUrl = linkMapaDomicilio(domicilioParaGuardar);
    }

    const slotsToBook = getBookingSlots(selectedTime, slotsNeeded);

    if (!slotsToBook || slotsToBook.length === 0) {
      return showAlert("Error", "El horario seleccionado excede la hora de cierre.");
    }

    setClientLoading(true);
    try {
      // =====================================================================
      // 🛡️ RADAR ANTI-COLISIONES
      // =====================================================================
      try {
        const timeToMins = (t) => {
          if (!t) return 0;
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };

        const currentBarberId = String(selectedBarber.id).trim();
        const currentLocId = String(selectedLocation.id).trim();

        // ⚡ FIX: las dos consultas (bloqueos y reservas) son
        // independientes entre sí, pero antes se esperaba una COMPLETA
        // antes de arrancar la otra — pagando el costo fijo de ida y
        // vuelta a Firestore dos veces seguidas. Ahora salen las dos
        // juntas con Promise.all, así solo se espera el tiempo de la
        // más lenta de las dos, no la suma de ambas. También filtradas
        // por sucursal directo en la consulta (ver fix anterior), en vez
        // de traer todos los registros de la plataforma para filtrar
        // después en el navegador.
        const [blocksSnap, bookingsSnap] = await Promise.all([
          getDocs(query(
            collection(db, "blocked_times"),
            where("date", "==", selectedDate),
            where("locationId", "==", currentLocId)
          )),
          getDocs(query(
            collection(db, "bookings"),
            where("date", "==", selectedDate),
            where("locationId", "==", currentLocId)
          ))
        ]);

        let chocaConBloqueo = false;
        blocksSnap.forEach(docSnap => {
          const block = docSnap.data();
          if (block.deleted) return; // desbloqueado (queda solo como registro)

          const blockBarbId = block.barberId ? String(block.barberId).trim() :
            (block.barber?.id ? String(block.barber.id).trim() : null);
          if (blockBarbId && blockBarbId !== currentBarberId) return;

          const blockStart = timeToMins(block.startTime);
          const blockEnd = timeToMins(block.endTime);

          if (blockEnd <= blockStart) {
            console.warn("⚠️ Bloqueo con tiempos inválidos, ignorado:", block);
            return;
          }

          const choca = slotsToBook.some(slot => {
            const slotMins = timeToMins(slot);
            return slotMins >= blockStart && slotMins < blockEnd;
          });

          if (choca) {
            console.warn("🛑 COLISIÓN CON BLOQUEO:", block);
            chocaConBloqueo = true;
          }
        });

        if (chocaConBloqueo) {
          setClientLoading(false);
          return showAlert("Horario No Disponible", "Este horario se encuentra bloqueado por la barbería.");
        }

        let chocaConReserva = false;
        bookingsSnap.forEach(docSnap => {
          const b = docSnap.data();

          // 🔧 A pedido (revisión de doble reserva): antes se ignoraban los
          // bloqueos guardados como reserva tipo 'block' (los del modal
          // "Bloquear horario" del calendario) y los turnos viejos sin
          // campo "time" (solo startTime) — la pantalla sí los muestra como
          // ocupados, pero este chequeo final no, así que una página abierta
          // de antes podía reservar encima.
          if (b.status === 'cancelled' || b.status === 'no_show' || b.isManualEntry) return;

          const bBarbId = b.barber?.id ? String(b.barber.id).trim() : (b.barberId ? String(b.barberId).trim() : null);
          if (bBarbId && bBarbId !== currentBarberId) return;

          const choca = slotsToBook.includes(b.time || b.startTime);
          if (choca) {
            console.warn("🛑 COLISIÓN CON RESERVA EXISTENTE:", b);
            chocaConReserva = true;
          }
        });

        if (chocaConReserva) {
          setClientLoading(false);
          return showAlert("Horario Ocupado", "Alguien más acaba de reservar este horario.");
        }

      } catch (error) {
        console.error("❌ ERROR EN EL RADAR:", error);
        setClientLoading(false);
        return showAlert("Error", "No pudimos verificar la disponibilidad. Intenta nuevamente.");
      }
      // =====================================================================
      // FIN DEL RADAR
      // =====================================================================

      const targetCompanyId = selectedLocation?.companyId || currentUser?.companyId || 'default_tenant';

      // 🎫 Re-chequeamos cobertura de membresía justo antes de confirmar
      // (no confiamos ciegamente en el estado ya calculado, por si algo
      // cambió justo antes de tocar "Confirmar"). Si aplica, el turno
      // queda sin costo y se marca con paymentMethod:'membership' — así
      // el resto de la lógica de abajo (que ya distinguía 'online' de
      // todo lo demás) funciona sin tener que tocarla.
      let coverage = null;
      if (shopSettings?.enableMemberships) {
        coverage = await checkMembershipCoverage(clientInfo.phone, selectedLocation.id, selectedServices.map(s => s.id), selectedDate, clientInfo.ci, clientInfo.email);
      }
      const isMembershipCovered = !!coverage?.covered;
      // 🔒 Un plan elegido como "servicio" sale Gs. 0: solo se puede reservar
      // si es socio activo y este turno queda cubierto (las altas las hace
      // el admin, ya no se inscribe nadie desde acá).
      if (selectedServices.some(s => s.isMembershipPlan) && !isMembershipCovered) {
        setClientLoading(false);
        return showAlert("Plan solo para socios", "Ese plan es solo para socios con la membresía activa. Sacalo de tu turno o consultá en el local para sumarte.");
      }
      // ✅ Mismo criterio parcial que effectiveTotalPrice arriba, pero
      // recalculado acá con el coverage FRESCO de este chequeo puntual
      // (no el que ya estaba en pantalla, por si algo cambió justo antes
      // de tocar "Confirmar").
      //
      // 🔧 A pedido — bug encontrado: este es el cálculo que REALMENTE
      // se guarda en la reserva (effectiveTotalPrice de más arriba solo
      // se usa para lo que se MUESTRA en pantalla antes de confirmar).
      // Se me había pasado actualizar este, así que seguía guardando
      // "0" para lo cubierto — el descuento nunca llegaba a quedar
      // guardado de verdad, aunque en pantalla se viera bien. Mismo
      // criterio ahora: precio de socio por servicio en vez de 0, y el
      // pseudo-servicio del plan en sí siempre en 0.
      const noCubiertosIdsFrescos = coverage?.serviciosNoCubiertosIds || [];
      // 🔧 A pedido (Cabo): plan con duración fija → lo cubierto vale Gs 0
      // (mismo criterio que effectiveTotalPrice, con el coverage fresco).
      const planFijaFresca = coverage?.plan?.duracionFija !== undefined && coverage?.plan?.duracionFija !== null;
      // 🎫 Y si el socio eligió un servicio REAL (ej: Corte Clásico) en vez
      // de la tarjeta "Reservar con Plan X", la reserva igual tiene que
      // llevar el marcador del plan (isMembershipPlan, con el valor de
      // comisión del plan) — si no, esa visita nunca aparecería en la Caja
      // de Membresías ni en el "Plus" del barbero. No cambia el total: el
      // marcador siempre queda fuera de la suma.
      const serviciosParaGuardar = (isMembershipCovered && planFijaFresca && !selectedServices.some(s => s.isMembershipPlan))
        ? [...selectedServices, {
            id: `plan_${coverage.plan.id}`,
            name: coverage.plan.name,
            price: coverage.plan.comisionPorVisita || 0,
            duration: Number(coverage.plan.duracionFija),
            category: 'Membresía',
            isMembershipPlan: true,
            planId: coverage.plan.id,
          }]
        : selectedServices;
      const finalTotalPrice = !isMembershipCovered
        ? totalPrice
        : selectedServices.reduce((sum, s) => {
            if (s.isMembershipPlan) return sum;
            if (noCubiertosIdsFrescos.includes(s.id)) return sum + Number(s.price || 0);
            if (planFijaFresca) return sum; // cubierto por plan de duración fija = Gs 0
            const precioSocio = (s.memberPrice !== undefined && s.memberPrice !== null && s.memberPrice !== '')
              ? Number(s.memberPrice)
              : Number(s.price || 0);
            return sum + precioSocio;
          }, 0);
      // 'membership' solo cuando el turno queda TOTALMENTE cubierto y no
      // hay nada que cobrar — si es cobertura parcial, sigue habiendo
      // plata real de por medio, así que el método de pago sigue siendo
      // el que el cliente eligió (local u online).
      const effectivePaymentMethod = (isMembershipCovered && finalTotalPrice === 0) ? 'membership' : paymentMethod;

      // 🎫 Nombre del plan a guardar EN el turno — para que
      // BarberPortal/BarberMobileView lo puedan mostrar directo, sin
      // consultar el plan de nuevo.
      const membershipPlanNameParaGuardar = isMembershipCovered ? (coverage?.plan?.name || null) : null;

      // 🕰️ Si el admin eligió una fecha/hora que ya pasó, es carga de
      // historial (turnos que ya sucedieron y nunca se registraron).
      // Cubre dos casos: un día anterior a hoy, O el mismo día de hoy
      // pero con un horario que ya pasó (ej: son las 20:00 y se carga
      // un servicio de las 09:00 de hoy). Se usa en dos lugares: acá
      // abajo, para que el turno nazca DIRECTO como "completado" (así
      // ya cuenta para Comisiones y estadísticas sin un paso extra de
      // marcarlo completado a mano); y más abajo, para el checkbox de
      // WhatsApp.
      const hoyStr = format(new Date(), 'yyyy-MM-dd');
      const esFechaPasada = isAdminAuth && (
        selectedDate < hoyStr ||
        (selectedDate === hoyStr && selectedTime && selectedTime < format(new Date(), 'HH:mm'))
      );

      // 💰 A pedido: seña por transferencia — si el barbero elegido la
      // requiere (y el turno no está cubierto en 0 por membresía), el
      // turno SIEMPRE nace "pending" (necesita que el admin confirme
      // que llegó el comprobante) y se guarda con un monto/porcentaje
      // propio, sin pisar el paymentMethod normal (local/online).
      const requiereSeniaCheck = !!selectedBarber?.requiereSeniaTransferencia && !(isMembershipCovered && finalTotalPrice === 0);
      const porcentajeSeniaCheck = Number(shopSettings?.senaTransferencia?.porcentaje) || 50;
      const montoSeniaCheck = requiereSeniaCheck ? Math.round((finalTotalPrice * porcentajeSeniaCheck) / 100) : 0;

      const initialStatus = requiereSeniaCheck
        ? 'pending'
        : esFechaPasada
        ? 'completed'
        : (isAdminAuth && effectivePaymentMethod !== 'online') ? 'confirmed' : 'pending';

      const finalTicketId = await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, "counters", `bookings_${targetCompanyId}`);
        const counterDoc = await transaction.get(counterRef);

        const newTicketNumber = counterDoc.exists() ? counterDoc.data().currentValue + 1 : 1000;

        // 🔒 A pedido (revisión de doble reserva): el chequeo de arriba (radar)
        // corre ANTES de la transacción, así que dos personas que confirman casi
        // a la vez (o un doble toque en "Confirmar") podían pasar las dos y
        // quedar con el mismo horario. Como TODAS las reservas de la empresa
        // leen y actualizan este mismo contador, Firestore las procesa de a una:
        // si otra reserva se guardó mientras tanto, esta transacción se repite
        // sola — y ahí este re-chequeo ve el turno recién creado y la corta.
        {
          const snapRecheck = await getDocs(query(
            collection(db, "bookings"),
            where("date", "==", selectedDate),
            where("locationId", "==", String(selectedLocation.id).trim())
          ));
          const barbIdRecheck = String(selectedBarber.id).trim();
          const ocupado = snapRecheck.docs.some(d => {
            const b = d.data();
            if (b.status === 'cancelled' || b.status === 'no_show' || b.isManualEntry) return false;
            const bBarb = b.barber?.id ? String(b.barber.id).trim() : (b.barberId ? String(b.barberId).trim() : null);
            if (bBarb && bBarb !== barbIdRecheck) return false;
            return slotsToBook.includes(b.time || b.startTime);
          });
          if (ocupado) throw new Error('HORARIO_OCUPADO');
        }

        if (!counterDoc.exists()) transaction.set(counterRef, { currentValue: 1000 });
        else transaction.update(counterRef, { currentValue: newTicketNumber });

        const shortCompanyId = targetCompanyId === 'default_tenant' ? 'TICKET' : targetCompanyId.slice(0, 5).toUpperCase();
        const paddedTicket = String(newTicketNumber).padStart(5, '0');
        const generatedId = `${shortCompanyId}_${paddedTicket}`;

        const clientData = {
          name: clientInfo.name,
          phone: clientInfo.phone,
          email: clientInfo.email ? clientInfo.email.toLowerCase().trim() : '',
          ci: clientInfo.ci ? clientInfo.ci.replace(/\D/g, '') : ''
        };

        slotsToBook.forEach((slot, i) => {
          const ref = doc(collection(db, "bookings"));
          transaction.set(ref, {
            bookingGroupId: generatedId,
            isPrimary: i === 0,
            companyId: targetCompanyId === 'default_tenant' ? null : targetCompanyId,
            client: i === 0 ? clientData : { name: clientData.name },
            barber: { id: selectedBarber.id, name: selectedBarber.name },
            locationId: selectedLocation.id,
            date: selectedDate,
            time: slot,
            startTime: selectedTime,
            services: i === 0 ? serviciosParaGuardar : [],
            totalDuration: i === 0 ? totalDuration : 0,
            totalPrice: i === 0 ? finalTotalPrice : 0,
            status: initialStatus,
            paymentMethod: requiereSeniaCheck ? 'transferencia_sena' : effectivePaymentMethod,
            // 💰 Solo se guardan estos campos si aplica — así no ensucia
            // ningún turno normal que no use este flujo.
            ...(requiereSeniaCheck ? { senaTransferenciaPendiente: true, senaMonto: montoSeniaCheck, senaPorcentaje: porcentajeSeniaCheck } : {}),
            membershipId: isMembershipCovered ? coverage.member.id : null,
            membershipPlanName: membershipPlanNameParaGuardar,
            membershipPlanId: isMembershipCovered ? (coverage?.plan?.id || null) : null,
            // 🏠 Dirección y ubicación del cliente — solo en el turno principal
            ...(domicilioParaGuardar ? { modalidad: 'domicilio', ...(i === 0 ? { domicilio: domicilioParaGuardar } : {}) } : {}),
            userId: (currentUser?.role === 'client') ? currentUser.uid : null,
            createdAt: Timestamp.now(),
            reminderSent: false
          });
        });

        return generatedId;
      });


      // 🎫 El descuento de la visita YA NO pasa acá — se movió a
      // updateBookingStatus() y al guardado del modal de Editar Reserva,
      // para que solo se descuente cuando el turno se marca "Completado"
      // de verdad, no con solo crear la reserva (si el socio falta o se
      // cancela, no se le debe gastar la visita).

      if (effectivePaymentMethod === 'local' && clientInfo.email) {
        enviarConfirmacion({
          clientInfo,
          ticketId: finalTicketId,
          shopSettings,
          selectedLocation,
          selectedBarber,
          selectedDate,
          selectedTime,
          totalDuration,
          selectedServices,
          totalPrice: finalTotalPrice,
          type: 'request'
        }).catch(err => console.error("Error envío fondo:", err));
      }

      setLastBookingId(finalTicketId);
      setClientLoading(false);

      // 🔕 Nuevo flujo: al reservar NO se manda ningún WhatsApp, ni desde la
      // web ni desde el panel. Los servidores (server.js y server_capelli.js)
      // autoconfirman los turnos de último momento (< 3 hs) y mandan un solo
      // recordatorio con botones (3 hs antes si es el mismo día, 6 hs antes
      // si es otro día) leyendo la reserva directo de Firestore.

      if (effectivePaymentMethod === 'online') {
        if (isAdminAuth) {
          addToast("Turno Pendiente", "Reserva guardada. Recuerda enviarle el link de pago por WhatsApp.", "info");
          setAdminSubView('calendar');
          setStep(0);
        } else {
          // 🔐 AUDITORÍA: además del origen, se guarda QUÉ ticket inició el pago en este
          // navegador — usePaymentCheck solo da por bueno un retorno (?payment_status=success)
          // si coincide con este ticket (antes cualquiera podía confirmar la reserva de otro
          // visitando esa URL a mano).
          try { localStorage.setItem('paymentTicket', String(finalTicketId)); } catch (e) { /* navegador restringido */ }
          try { localStorage.setItem('paymentOrigin', 'client'); } catch (e) { /* navegador restringido — el modal de pago igual se abre */ }
          setShowPaymentModal(true);
        }
      } else {
        if (isAdminAuth) {
          addToast(isMembershipCovered ? "Turno Agendado (Membresía)" : "Turno Agendado", isMembershipCovered ? `Cubierto por el plan ${coverage.plan.name} — no se cobró nada.` : "La reserva se guardó y confirmó correctamente.", "success");
          setAdminSubView('calendar');
          setStep(0);
        } else {
          setShowSuccess(true);
        }
      }

    } catch (error) {
      console.error(error);
      if (error?.message === 'HORARIO_OCUPADO') {
        showAlert("Horario Ocupado", "Alguien más acaba de reservar este horario. Elegí otro, por favor.");
      } else {
        showAlert("Error", "No se pudo crear la reserva: " + error.message);
      }
      setClientLoading(false);
    }
  };

  // =====================================================================
  // 🧑‍🔧 SERVICIO FUERA DE AGENDA — a pedido, para que el barbero pueda
  // cargar desde su propio portal un servicio que hizo a un cliente que
  // llegó sin turno reservado. Se crea directo como "completed", sin
  // pasar por el flujo normal de reserva/confirmación — no hay teléfono
  // real del cliente en la mayoría de los casos, así que se marca
  // isReviewed/ratingTemplateSent en true para que nunca dispare ningún
  // mensaje de WhatsApp (ni calificación, ni nada) sobre este turno.
  // isManualEntry queda como marca clara de que no vino del flujo normal,
  // por si en algún reporte hace falta distinguirlo.
  // =====================================================================
  const handleAddManualService = async ({ barberId, barberName, locationId, selectedServices: serviciosElegidos, clientName, clientPhone, paymentMethod, initialStatus, targetDate }) => {
    if (!barberId || !Array.isArray(serviciosElegidos) || serviciosElegidos.length === 0) {
      addToast('Faltan datos', 'Elegí al menos un servicio para cargar.', 'error');
      return false;
    }
    // 🔧 A pedido: el barbero elige si esto arranca "Confirmado" (el
    // cliente ya llegó y va a empezar a trabajar, sin cobrar todavía)
    // o directo "Completado" (ya terminó y cobró). Antes era siempre
    // completado, sin opción. 'confirmed' es lo único distinto a
    // 'completed' que soporta esta pantalla — cualquier otra cosa cae
    // en 'completed' por seguridad.
    const estadoFinal = initialStatus === 'confirmed' ? 'confirmed' : 'completed';
    const esCompletadoDeEntrada = estadoFinal === 'completed';
    setLoading(true);
    try {
      const ahora = new Date();
      // 🔧 A pedido: antes esto se guardaba siempre con la fecha de HOY
      // (format(ahora, ...)), sin importar en qué día estaba parado el
      // barbero en el navegador de fechas de su agenda — un barbero
      // viendo el día de ayer (para cargar algo que se le pasó) o de
      // mañana terminaba con el turno guardado en la fecha equivocada.
      // Ahora usa targetDate (la fecha que tenía seleccionada en su
      // pantalla) si viene, y solo cae en la fecha real de hoy como
      // respaldo si por algún motivo no llega.
      const dateStr = targetDate || format(ahora, 'yyyy-MM-dd');
      const timeStr = format(ahora, 'HH:mm');

      // 🎫 A pedido: chequeo de socio por teléfono — mismo mecanismo que
      // ya usa el resto de los flujos de reserva (checkMembershipCoverage).
      // Si el teléfono coincide con un socio activo, el precio final usa
      // el precio de socio de cada servicio en vez del precio normal.
      const cleanPhoneManual = (clientPhone || '').replace(/\D/g, '');
      let membershipCoverageManual = null;
      if (cleanPhoneManual && locationId && typeof checkMembershipCoverage === 'function') {
        try {
          membershipCoverageManual = await checkMembershipCoverage(cleanPhoneManual, locationId, serviciosElegidos.map(s => s.id), dateStr);
        } catch (e) {
          console.error('Error chequeando membresía en servicio manual:', e.message);
        }
      }

      const serviciosNoCubiertosIdsManual = membershipCoverageManual?.serviciosNoCubiertosIds || [];
      const totalPrice = membershipCoverageManual?.covered
        ? serviciosElegidos.reduce((sum, s) => {
            if (serviciosNoCubiertosIdsManual.includes(s.id)) return sum + Number(s.price || 0);
            const precioSocio = (s.memberPrice !== undefined && s.memberPrice !== null && s.memberPrice !== '')
              ? Number(s.memberPrice)
              : Number(s.price || 0);
            return sum + precioSocio;
          }, 0)
        : serviciosElegidos.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

      // 🔧 A pedido: usar la duración PERSONALIZADA del barbero si la
      // tiene cargada para alguno de estos servicios (mismo criterio
      // que el resto de los flujos de reserva) — antes esto usaba
      // siempre la duración estándar del servicio, ignorando el
      // override por barbero.
      //
      // ⏱️ A pedido: mismo criterio que el resto de los flujos — si el
      // cliente está cubierto por un plan CON duración fija cargada
      // (ej: Eco 30 min, Black 1 hora, Silver 40 min), esa duración
      // manda para todo el turno, sin sumarla a la del servicio elegido.
      const barberCompletoManual = (barbers || []).find(b => String(b.id) === String(barberId)) || null;
      const planCoberturaConDuracionFijaManual = membershipCoverageManual?.covered && membershipCoverageManual?.plan?.duracionFija !== undefined && membershipCoverageManual?.plan?.duracionFija !== null;
      const totalDuration = planCoberturaConDuracionFijaManual
        ? Number(membershipCoverageManual.plan.duracionFija)
        : serviciosElegidos.reduce((sum, s) => sum + getServiceDuration(s, barberCompletoManual), 0);

      const nuevoDocRef = doc(collection(db, 'bookings'));
      await setDoc(nuevoDocRef, {
        // Desde la vista del admin la empresa sale de la sucursal (igual que el turno rápido)
        companyId: (locations || []).find(l => l.id === locationId)?.companyId || currentUser?.companyId || null,
        locationId: locationId || null,
        date: dateStr,
        startTime: timeStr,
        time: timeStr,
        totalDuration,
        totalPrice,
        services: serviciosElegidos.map(s => ({ id: s.id, name: s.name, price: s.price, duration: s.duration })),
        barber: { id: barberId, name: barberName || '' },
        client: { name: (clientName || '').trim() || 'Cliente sin turno', phone: cleanPhoneManual },
        status: estadoFinal,
        isPrimary: true,
        paymentMethod: paymentMethod || 'efectivo',
        // 🔧 Estos dos solo tienen sentido si ya arranca completado —
        // si arranca "confirmed", quedan en false: el flujo normal de
        // "Cobrar y Finalizar"/"Sentar Cliente" los va a resolver bien
        // solo cuando el barbero lo termine de verdad más tarde.
        isReviewed: esCompletadoDeEntrada,
        ratingTemplateSent: esCompletadoDeEntrada,
        isManualEntry: true,
        ...(membershipCoverageManual?.covered
          ? {
              membershipId: membershipCoverageManual.member.id,
              membershipPlanName: membershipCoverageManual.plan?.name || null,
              membershipPlanId: membershipCoverageManual.plan?.id || null,
              // 🛡️ Si ya se descuenta la visita ACÁ ABAJO (porque
              // arrancó completado), lo marcamos para que
              // updateBookingStatus no la vuelva a descontar si el
              // turno se vuelve a guardar/completar más adelante.
              ...(esCompletadoDeEntrada ? { membershipVisitDeducted: true } : {}),
            }
          : {}),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // 🎫 Igual que en el resto de los flujos: si estuvo cubierto por
      // membresía Y arrancó ya completado, descuenta la visita del
      // cupo del socio de una. Si arrancó "confirmed", NO se descuenta
      // acá — updateBookingStatus ya lo hace solo (con su propio
      // seguro anti-duplicado) cuando el barbero lo complete después.
      if (esCompletadoDeEntrada && membershipCoverageManual?.covered && membershipCoverageManual.member?.id) {
        try {
          await incrementarVisitaMembresia(locationId, membershipCoverageManual.member.id, serviciosElegidos, nuevoDocRef.id);
        } catch (e) {
          console.error('Error incrementando visita de membresía en servicio manual:', e.message);
        }
      }

      addToast(
        membershipCoverageManual?.covered ? 'Servicio agregado (Socio)' : 'Servicio agregado',
        esCompletadoDeEntrada ? 'El turno fuera de agenda se cargó correctamente.' : 'El turno quedó cargado como confirmado — marcalo como completado cuando termines.',
        'success'
      );
      return true;
    } catch (error) {
      console.error('Error agregando servicio manual:', error);
      addToast('Error', 'No se pudo cargar el servicio: ' + error.message, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };



  const updateBookingStatus = async (idOrGroupId, newStatus, options = null) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      let docsToUpdate = [];

      // 1. Intentamos buscar el documento directamente por su ID único
      if (idOrGroupId && idOrGroupId !== "undefined") {
        const directDocRef = doc(db, "bookings", idOrGroupId);
        const directDocSnap = await getDoc(directDocRef);
        if (directDocSnap.exists()) {
          docsToUpdate.push(directDocSnap);
        }
      }

      // 2. Si no es un ID directo o queremos asegurar cambios en el grupo, buscamos por campo bookingGroupId
      if (idOrGroupId && idOrGroupId !== "undefined") {
        // Identificamos si el tercer parámetro es una fecha (Admin) o la data de productos (Barberos)
        let bookingDate = typeof options === 'string' ? options : null;

        let q = query(collection(db, "bookings"), where("bookingGroupId", "==", idOrGroupId));

        if (bookingDate) {
          q = query(
            collection(db, "bookings"),
            where("bookingGroupId", "==", idOrGroupId),
            where("date", "==", bookingDate)
          );
        }

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          // Evitamos duplicar el documento si ya se agregó en el paso 1
          if (!docsToUpdate.some(d => d.id === doc.id)) {
            docsToUpdate.push(doc);
          }
        });
      }

      // 3. Control de seguridad: Si no se encontró absolutamente nada, no avanzamos en falso
      if (docsToUpdate.length === 0) {
        console.warn("No se encontró ningún documento para actualizar con el ID:", idOrGroupId);
        addToast("Aviso", "No se localizó la cita en la base de datos.", "warning");
        setLoading(false);
        return;
      }

      // 🔧 A pedido — bug encontrado: al auto-cancelarse un turno por
      // falta de seña (a los 15 min, en server_capelli.js), se apagaba
      // senaTransferenciaPendiente — la MISMA bandera de la que depende
      // el botón "Confirmar Seña" para aparecer. Resultado: una vez
      // cancelado, el botón desaparecía del todo, dejando al admin sin
      // forma de confirmar la seña aunque el comprobante sí hubiera
      // llegado (solo un poco tarde). Ahora, si se está "recuperando"
      // un turno que quedó cancelado específicamente por esto
      // (cancelReason === 'sena_no_confirmada'), se verifica PRIMERO
      // que nadie haya reservado ese mismo horario mientras tanto —
      // si el horario ya no está libre, se corta acá, sin tocar nada.
      const primaryDocParaVerificar = docsToUpdate.find(d => d.data().isPrimary === true) || docsToUpdate[0];
      const dataPrimaria = primaryDocParaVerificar.data();
      // 🔧 A pedido: se amplía a CUALQUIER turno cancelado (no solo el
      // cancelado automáticamente por el cron de 15 min) — un admin
      // puede haber cancelado a mano un turno que todavía tenía la
      // seña pendiente, y recuperarlo debe pasar por el mismo chequeo
      // de horario libre, sin importar por qué se había cancelado.
      const esRecuperacionDeSenaCancelada = options?.confirmarSenia && dataPrimaria.status === 'cancelled';
      // 🔧 A pedido: el mismo chequeo de horario libre ahora aplica a CUALQUIER
      // reactivación de un turno cancelado / "No asistió" (ej: el cliente cancela,
      // otro cliente toma ese horario, y el admin intenta volver a ponerlo
      // Confirmado) — antes solo se verificaba al confirmar una seña, y se podía
      // terminar con dos turnos activos en el mismo horario.
      const esReactivacion =
        ['cancelled', 'no_show'].includes(dataPrimaria.status) &&
        ['pending', 'confirmed', 'in_progress', 'completed'].includes(newStatus);
      if (esRecuperacionDeSenaCancelada || esReactivacion) {
        try {
          const horariosOcupados = docsToUpdate.map(d => d.data().time).filter(Boolean);
          const qConflicto = query(
            collection(db, 'bookings'),
            where('locationId', '==', dataPrimaria.locationId),
            where('barber.id', '==', dataPrimaria.barber?.id),
            where('date', '==', dataPrimaria.date)
          );
          const snapConflicto = await getDocs(qConflicto);
          const hayConflicto = snapConflicto.docs.some(d => {
            const b = d.data();
            if (String(b.bookingGroupId) === String(dataPrimaria.bookingGroupId)) return false; // es el mismo turno que se está recuperando
            if (!['pending', 'confirmed', 'in_progress', 'completed'].includes(b.status)) return false;
            return horariosOcupados.includes(b.time);
          });
          if (hayConflicto) {
            addToast("No se pudo reactivar el turno", "Ese horario ya fue reservado por otro cliente (ya hay un turno activo ahí).", "error");
            setLoading(false);
            return;
          }
        } catch (eConflicto) {
          console.error('Error chequeando conflicto al recuperar turno:', eConflicto);
          addToast("Error", "No se pudo verificar el horario. Probá de nuevo.", "error");
          setLoading(false);
          return;
        }
      }

      // Preparar los campos básicos a actualizar
      const updateData = { status: newStatus };
      if (newStatus === 'confirmed') updateData.reminderSent = false;
      if (newStatus === 'in_progress') updateData.startedAt = Timestamp.now();

      // 💰 A pedido: confirmar que llegó la seña por transferencia — el
      // admin la marca a mano después de revisar su banco/WhatsApp. Con
      // esto, el turno deja de mostrar el badge "Seña Pendiente". Si
      // viene de una recuperación (ver arriba), también se limpia
      // cancelReason — ya no está cancelado por eso.
      if (options?.confirmarSenia) {
        updateData.senaTransferenciaPendiente = false;
        if (esRecuperacionDeSenaCancelada) updateData.cancelReason = null;
      }

      // 💳 Método de pago elegido por el barbero al cobrar (efectivo,
      // transferencia o pos) — se guarda solo al completar el turno,
      // que es cuando realmente se cobró. Control de Caja lo usa para
      // calcular la comisión de la posnet en las ventas por POS.
      if (newStatus === 'completed' && options?.paymentMethod) {
        updateData.paymentMethod = options.paymentMethod;
      }

      // 4. Inyección de productos extras si se está finalizando el turno
      // ✅ Solo se declara UNA VEZ aquí (eliminada la declaración duplicada de arriba)
      const extraProducts = options?.extraProducts || [];
      const newTotal = options?.newTotal || null;

      if (newStatus === 'completed' && extraProducts.length > 0) {
        if (newTotal) updateData.totalPrice = newTotal;

        const productsAsServices = extraProducts.map(p => ({
          name: "🛍️ " + p.name,
          price: Number(p.price),
          duration: 0
        }));

        // Recuperamos los servicios originales del primer documento encontrado para fusionarlos
        const currentServices = docsToUpdate[0].data().services || [];
        updateData.services = [...currentServices, ...productsAsServices];
      }

      // Añadir todas las actualizaciones acumuladas al lote
      docsToUpdate.forEach((docSnap) => {
        batch.update(docSnap.ref, updateData);
      });

      // Guardar de verdad en Firebase
      await batch.commit();

      // 💰 A pedido: al confirmar la seña, avisarle al cliente que su
      // turno ya quedó confirmado — el servidor de Capelli manda la
      // plantilla de confirmación directo (el cliente manda el
      // comprobante a un número sin el bot conectado, así que la
      // ventana de 24hs del bot nunca se abre). No manda recordatorio
      // aparte — este ES el único aviso que recibe para estos turnos.
      if (options?.confirmarSenia && docsToUpdate.length > 0) {
        const primaryDocSenia = docsToUpdate.find(d => d.data().isPrimary === true) || docsToUpdate[0];
        const rSenia = { ...primaryDocSenia.data(), id: primaryDocSenia.id };
        try {
          const { serverUrl } = await resolverServidorWhatsApp(rSenia.locationId, rSenia.companyId);
          await fetch(`${serverUrl}/api/confirmar-sena`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bookingId: rSenia.id })
          });
        } catch (eSenia) {
          console.error('❌ Error avisando confirmación de seña:', eSenia.message);
        }
      }

      // 🧪 A pedido: este ahorro (no avisar por WhatsApp al cancelar
      // desde el admin) queda atrás del feature flag "pruebaFlujoWhatsapp"
      // — mientras esa empresa no lo tenga activado, se manda el aviso
      // de siempre (comportamiento viejo, sin cambios). Si el CLIENTE
      // cancela respondiendo por WhatsApp, ese aviso siempre funciona
      // igual, sin importar este flag — vive del lado del servidor.
      if (newStatus === 'cancelled' && docsToUpdate.length > 0 && !enabledFeatures?.pruebaFlujoWhatsapp) {
        const primaryDoc = docsToUpdate.find(d => d.data().isPrimary === true) || docsToUpdate[0];
        const r = { ...primaryDoc.data(), id: primaryDoc.id };
        const tId = r.bookingGroupId ? String(r.bookingGroupId).slice(-5) : String(r.id).slice(-5);
        const nombresServicios = r.services?.length > 0 ? r.services.map(s => s.name).join(', ') : 'Servicio de barbería';

        await enviarWhatsAppAutomatico(
          r.client?.phone, r.client?.name, r.date, r.startTime || r.time, tId, 'cancelled',
          r.paymentMethod || 'local', r.barber?.name, nombresServicios, r.totalPrice || 0, r.locationId
        );
      }

      // 👇=== NUEVO: DISPARAR WHATSAPP DE CALIFICACIÓN AL COMPLETAR ===👇
      if (newStatus === 'completed' && docsToUpdate.length > 0) {
        const primaryDoc = docsToUpdate.find(d => d.data().isPrimary === true) || docsToUpdate[0];
        const r = { ...primaryDoc.data(), id: primaryDoc.id };

        // 🎁 El descuento de la visita se decide adentro de
        // incrementarVisitaMembresia — mira los servicios reales de la
        // reserva contra el plan, para descontar el cupo principal y/o
        // el bonus, cada uno el que corresponda.
        // 🛡️ SEGURO ANTI-DUPLICADO: chequeamos membershipVisitDeducted
        // antes de descontar. Sin esto, marcar "Completado" dos veces (o
        // guardar el modal de Editar Reserva varias veces sobre un turno
        // ya completado) le robaría visitas de más al socio cada vez.
        if (r.membershipId && r.locationId && !r.membershipVisitDeducted) {
          await incrementarVisitaMembresia(r.locationId, r.membershipId, r.services, r.bookingGroupId || r.id);
          // Marcamos ESTE booking (y todos sus slots) para que no se
          // vuelva a descontar si se completa/guarda de nuevo
          docsToUpdate.forEach((docSnap) => {
            updateDoc(docSnap.ref, { membershipVisitDeducted: true }).catch(() => {});
          });
        }

        await enviarWhatsAppAutomatico({
          client: r.client,
          date: r.date,
          startTime: r.startTime || r.time,
          locationId: r.locationId || selectedAdminLocationId,
          companyId: r.companyId || currentUser?.companyId || null,
          barber: r.barber,
          services: r.services,
          totalPrice: r.totalPrice,
          bookingGroupId: r.bookingGroupId,
          id: r.id, // ← CRÍTICO: Se requiere el ID del documento de Firestore
          status: 'completed'
        });
      }
      // 👆==============================================================👆

      const message = newStatus === 'confirmed'
        ? "Turno confirmado. Recordatorio programado."
        : "Estado actualizado con éxito.";

      addToast("Actualizado", message, "success");

      // Refrescar estado global de la app
      await loadBookings();

    } catch (error) {
      console.error("Error crítico al actualizar el estado:", error);
      addToast("Error", "No se pudo cambiar el estado en el servidor.", "error");
    }
    setLoading(false);
  };

  // =====================================================================
  // 🔄 REENVIAR PLANTILLA DE CONFIRMACIÓN (WHATSAPP API)
  // =====================================================================
  // 📨 El botón "Enviar auto" (reenvío manual de la solicitud) se sacó:
  // con el flujo nuevo el servidor manda solo el recordatorio con botones
  // y omite solicitud_reserva_v3, así que el reenvío no le llegaba a nadie.

  const deleteBooking = async (idToDelete) => {
    if (!idToDelete) return;
    const targetId = String(idToDelete);

    if (window.confirm('⚠️ ¿Estás seguro de eliminar esta reserva? Se liberará el horario.')) {
      setLoading(true);
      try {
        const batch = writeBatch(db);
        const qGroup = query(collection(db, "bookings"), where("bookingGroupId", "==", targetId));
        const groupSnapshot = await getDocs(qGroup);

        // 🧪 A pedido: este ahorro (no avisar por WhatsApp al eliminar
        // desde el admin) queda atrás del feature flag
        // "pruebaFlujoWhatsapp" — mientras esa empresa no lo tenga
        // activado, se manda el aviso de siempre.
        if (!enabledFeatures?.pruebaFlujoWhatsapp && !groupSnapshot.empty) {
          const r = { ...groupSnapshot.docs[0].data(), id: groupSnapshot.docs[0].id };
          // Solo enviamos si es un cliente real (ignoramos bloqueos de
          // agenda) Y si este turno puntual no se marcó como "sin
          // WhatsApp" al crearlo (ej: desde el modal de Agregar Turno,
          // con el checkbox destildado) — no tiene sentido avisarle a
          // alguien de una cancelación de algo que nunca se le avisó.
          if (r.type !== 'block' && !r.sinWhatsApp) {
            const tId = r.bookingGroupId ? String(r.bookingGroupId).slice(-5) : String(r.id).slice(-5);
            const nombresServicios = r.services?.length > 0 ? r.services.map(s => s.name).join(', ') : 'Servicio de barbería';

            await enviarWhatsAppAutomatico(
              r.client?.phone,
              r.client?.name,
              r.date,
              r.startTime || r.time,
              tId,
              'cancelled',
              r.paymentMethod || 'local',
              r.barber?.name,
              nombresServicios,
              r.totalPrice || 0,
              r.locationId
            );
          }
        }

        let found = false;
        groupSnapshot.forEach((doc) => {
          batch.delete(doc.ref);
          found = true;
        });

        if (!found) {
          const docRef = doc(db, "bookings", targetId);
          batch.delete(docRef);
        }

        await batch.commit();

        setBookings(prev => prev.filter(b => {
          const gId = b.bookingGroupId ? String(b.bookingGroupId) : "---";
          const dId = b.id ? String(b.id) : "---";
          return gId !== targetId && dId !== targetId;
        }));

        if (bookingToEdit) {
          const editGId = bookingToEdit.bookingGroupId ? String(bookingToEdit.bookingGroupId) : "";
          if (editGId === targetId || String(bookingToEdit.id) === targetId) {
            setBookingToEdit(null);
          }
        }

        addToast("Eliminado", "Horario liberado correctamente.", "success");

      } catch (error) {
        console.error("Error:", error);
        showAlert("Error", "No se pudo eliminar.");
      } finally {
        setLoading(false);
      }
    }
  };

  // =====================================================================
  // ⭐ SISTEMA DE CALIFICACIONES (RESEÑAS DE BARBEROS)
  // =====================================================================
  // ✅ FIX: se agregó `bookingId` (opcional, para no romper otros llamados
  // existentes). Antes, una reseña dejada desde la APP (este modal) solo
  // quedaba guardada en la subcolección reviews del barbero — nunca en la
  // reserva en sí. Una reseña dejada por WHATSAPP (server.js) sí guarda
  // reviewStars/reviewComment directo en el booking. Con este fix, los dos
  // caminos dejan el mismo rastro en la reserva, para poder mostrarlo
  // consistente en AdminBookings sin importar por dónde llegó.
  const submitReview = async (locationId, barberId, clientId, clientName, stars, comment, bookingId = null) => {
    // Aseguramos que las rutas sean siempre texto para que Firebase no colapse
    const locIdStr = String(locationId).trim();
    const barbIdStr = String(barberId).trim();


    if (!locIdStr || !barbIdStr || barbIdStr === "undefined") {
      addToast("Error Interno", "Faltan datos del local o del barbero.", "error");
      return;
    }

    setClientLoading(true);
    try {
      const barberRef = doc(db, "locations", locIdStr, "barbers", barbIdStr);

      // 1. ACTUALIZAR NÚMEROS DEL BARBERO — pero si ya no existe (por
      // ejemplo, lo sacaste del equipo después de esta reserva), no
      // frenamos TODA la calificación por eso. Seguimos igual guardando
      // el comentario y las estrellas del cliente — solo se salta la
      // actualización del promedio de ESE barbero puntual, porque ya no
      // tiene sentido (no está más).
      let barberExiste = true;
      try {
        await runTransaction(db, async (transaction) => {
          const barberDoc = await transaction.get(barberRef);
          if (!barberDoc.exists()) {
            barberExiste = false;
            return;
          }

          const currentRating = barberDoc.data().rating || 0;
          const currentReviewsCount = barberDoc.data().reviewsCount || 0;

          const newReviewsCount = currentReviewsCount + 1;
          const newRating = ((currentRating * currentReviewsCount) + stars) / newReviewsCount;

          transaction.update(barberRef, {
            rating: parseFloat(newRating.toFixed(1)),
            reviewsCount: newReviewsCount
          });
        });
      } catch (eBarber) {
        console.error("⚠️ No se pudo actualizar el promedio del barbero:", eBarber.message);
        barberExiste = false;
      }

      if (!barberExiste) {
        console.warn(`⚠️ El barbero ${barbIdStr} ya no existe en locations/${locIdStr}/barbers — se guarda igual la reseña, sin actualizar su promedio.`);
      }

      // 2. CREAR LA CARPETA Y GUARDAR EL TEXTO (esto funciona aunque el
      // barbero ya no exista como documento — las subcolecciones de
      // Firestore no dependen de que el documento padre siga existiendo)
      const reviewsRef = collection(db, "locations", locIdStr, "barbers", barbIdStr, "reviews");
      await addDoc(reviewsRef, {
        clientId: clientId || 'anonimo',
        clientName: clientName || 'Anónimo',
        stars: Number(stars),
        comment: comment || '',
        createdAt: new Date() // Usamos new Date() nativo de JS, así no falla si olvidas importar Timestamp
      });

      // 3. 🆕 GUARDAR TAMBIÉN EN LA RESERVA (mismos campos que usa server.js
      // cuando la reseña llega por WhatsApp) — así se puede mostrar en
      // AdminBookings sin importar el camino por el que llegó.
      if (bookingId) {
        try {
          await updateDoc(doc(db, "bookings", bookingId), {
            isReviewed: true,
            reviewStars: Number(stars),
            reviewComment: comment || ''
          });
        } catch (eBooking) {
          console.error("⚠️ No se pudo guardar la reseña en la reserva:", eBooking.message);
        }
      }

      addToast("¡Gracias!", "Tu calificación ha sido guardada con éxito.", "success");

      // Recargar los barberos para actualizar la vista
      if (typeof loadBarbers === 'function') {
        await loadBarbers(locIdStr);
      }

    } catch (error) {
      // AHORA SÍ VEREMOS EL ERROR REAL SI ALGO FALLA
      console.error("❌ Error CRÍTICO al guardar la calificación:", error);
      addToast("Error al Guardar", error.message || "No se pudo enviar la calificación.", "error");
    } finally {
      setClientLoading(false);
    }
  };

  const handleBlockTime = async (barber, dateStr, startTime, endTime) => {
    // 👇 1. EL RADAR: Función rápida para convertir "HH:mm" a minutos (ej. 09:30 -> 570)
    const timeToMins = (t) => {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    const blockStartMins = timeToMins(startTime);
    const blockEndMins = timeToMins(endTime);

    // 👇 2. VERIFICACIÓN: ¿Hay alguna reserva activa que choque con este bloqueo?
    const hasOverlap = (bookings || []).some(b => {
      // Si es otro barbero, otra fecha, otro local, o está cancelada, la ignoramos
      if (String(b.barber?.id) !== String(barber.id)) return false;
      if (b.date !== dateStr) return false;
      if (String(b.locationId) !== String(selectedAdminLocationId)) return false;
      if (b.status === 'cancelled' || b.status === 'no_show') return false;

      // Calculamos cuándo empieza y termina esta reserva
      // Nota: Uso b.startTime o b.time dependiendo de cómo lo guardes
      const bookingStartMins = timeToMins(b.startTime || b.time);
      const bookingEndMins = bookingStartMins + Number(b.totalDuration || 30); // Sumamos su duración

      // Lógica matemática de choque: ¿El inicio de uno ocurre antes del fin del otro y viceversa?
      return bookingStartMins < blockEndMins && bookingEndMins > blockStartMins;
    });

    // 👇 3. EL FRENO: Si hay choque, no guardamos nada y avisamos
    if (hasOverlap) {
      // Puedes cambiar este alert por tu función de notificaciones (ej. showAlert o addToast)
      alert(`⚠️ No puedes bloquear este horario. ${barber.name} ya tiene reservas agendadas entre las ${startTime} y las ${endTime}.`);
      return; // 🛑 El "return" hace que la función se detenga aquí mismo
    }

    // --- Si pasó el radar sin chocar, continuamos guardando en Firebase normalmente ---

    // Si tienes setLoading, úsalo, si no, quítalo
    // setLoading(true); 
    try {
      const ref = doc(collection(db, "blocked_times"));
      await setDoc(ref, {
        id: ref.id,
        barberId: barber.id,
        barberName: barber.name,
        date: dateStr,
        startTime: startTime,
        endTime: endTime,
        locationId: selectedAdminLocationId,
        createdAt: Timestamp.now()
      });

      // Si tienes una función para lanzar notificaciones, úsala aquí
      // addToast("Horario Bloqueado", `Se bloqueó a ${barber.name}.`, "success");

    } catch (error) {
      console.error("Error REAL de Firebase:", error);
      // addToast("Error", "No se pudo bloquear el horario.", "error");
    } finally {
      // setLoading(false);
    }
  };

  // 3. FUNCIÓN PARA QUITAR EL BLOQUEO
  // 🔍 A pedido: antes se borraba el documento y no quedaba rastro de
  // quién desbloqueó ni cuándo. Ahora se marca como desbloqueado y se
  // guarda quién fue — todas las lecturas de blocked_times lo ignoran.
  const handleDeleteBlock = async (blockId) => {
    const ref = doc(db, "blocked_times", blockId);
    try {
      await updateDoc(ref, {
        deleted: true,
        deletedAt: Timestamp.now(),
        deletedBy: {
          uid: currentUser?.uid || null,
          email: currentUser?.email || null,
          name: currentUser?.name || null,
          role: currentUser?.role || null,
        },
      });
      addToast("Desbloqueado", "El horario vuelve a estar disponible.", "success");
    } catch (error) {
      // Si las reglas de Firestore no dejan editar el bloqueo, se borra
      // como antes para no trabar al usuario (en ese caso sin registro).
      console.error("No se pudo marcar el bloqueo como desbloqueado, se borra:", error);
      try {
        await deleteDoc(ref);
        addToast("Desbloqueado", "El horario vuelve a estar disponible.", "success");
      } catch (errorBorrar) {
        console.error("Error al eliminar bloqueo:", errorBorrar);
        addToast("Error", "No se pudo desbloquear el horario.", "error");
      }
    }
  };


  // --- 🚫 GUARDAR EL BLOQUEO DE AGENDA DESDE EL MODAL ---
  const confirmBlockTime = async () => {
    const { barber, dateStr, startTime, endTime } = blockModalConfig;
    if (!startTime || !endTime) return showAlert("Error", "Debes seleccionar inicio y fin.");

    setLoading(true);
    try {
      const targetCompanyId = selectedLocation?.companyId || currentUser?.companyId || 'default_tenant';
      const blockGroupId = `BLOCK_${Date.now()}`;

      const refDate = new Date();
      let current = parse(startTime, 'HH:mm', refDate);
      const end = parse(endTime, 'HH:mm', refDate);

      if (isBefore(end, current) || startTime === endTime) {
        addToast("Error", "La hora de fin debe ser mayor a la de inicio.", "error");
        setLoading(false);
        return;
      }

      const batch = writeBatch(db);
      let count = 0;

      while (isBefore(current, end)) {
        const slotTime = format(current, 'HH:mm');

        const ref = doc(collection(db, "bookings"));
        batch.set(ref, {
          bookingGroupId: blockGroupId,
          isPrimary: count === 0,
          type: 'block',
          companyId: targetCompanyId === 'default_tenant' ? null : targetCompanyId,
          client: { name: '🚫 HORARIO BLOQUEADO', phone: '0000000000', email: '' },
          barber: { id: barber.id, name: barber.name },
          locationId: selectedAdminLocationId,
          date: dateStr,
          time: slotTime,
          startTime: startTime,
          endTime: endTime,
          services: [{ name: 'Bloqueo / Salida', duration: 30, price: 0 }],
          status: 'confirmed', // Lo marca como ocupado
          createdAt: Timestamp.now()
        });

        current = addMinutes(current, 30);
        count++;
        if (count > 24) break; // Límite de seguridad de 12 horas
      }

      await batch.commit();
      addToast("Horario Bloqueado", `Se bloqueó la agenda de ${barber.name}.`, "success");
      setBlockModalConfig({ isOpen: false, barber: null, dateStr: '', startTime: '', endTime: '' });

    } catch (error) {
      console.error("Error bloqueando rango:", error);
      addToast("Error", "No se pudo bloquear el horario.", "error");
    } finally {
      setLoading(false);
    }
  };

  // 📅 "barber" es OPCIONAL — si se pasa, además de los días que la
  // barbería tiene abiertos, también filtra por los días que ESE
  // barbero puntual trabaja (barber.workingDays, array de números de
  // día: 0=domingo, 1=lunes... igual que openDays). Si el barbero no
  // tiene workingDays configurado (barberos ya existentes, sin tocar),
  // se asume que trabaja todos los días abiertos de la barbería, como
  // siempre — así no se rompe nada para nadie que no use esta función.
  // 🎫 A pedido: cuando el cliente reserva CON SU PLAN (ej: "Reservar con
  // Plan Eco"), y ese plan tiene días permitidos cargados (allowedDays —
  // ej: solo lunes, martes y miércoles), el calendario debe ocultar
  // cualquier otro día. allowedDaysPlan es opcional — null/vacío = sin
  // restricción extra, se comporta exactamente igual que antes.
  // 📅 A pedido: chequeo de un día PUNTUAL (para el popup de calendario
  // por mes) — getAvailableDates de abajo deja de buscar apenas junta 8
  // fechas (optimizado para la fila de chips, que no necesita más), así
  // que nunca llega a cubrir meses lejanos. Esta función replica los
  // mismos filtros (barbería abierta ese día, el barbero trabaja ese
  // día, y si aplica, el plan elegido lo permite) pero para UN día a la
  // vez, sin ningún límite de cuántos resultados juntar — así el
  // calendario puede marcar cualquier mes, por lejos que esté.
  const isDateBookable = (dateStr, barber = null, allowedDaysPlan = null) => {
    if (!shopSettings || !shopSettings.openDays) return false;
    const todayStr2 = format(startOfDay(new Date()), 'yyyy-MM-dd');
    if (dateStr < todayStr2) return false; // nunca días pasados
    const dayOfWeek = getDay(parse(dateStr, 'yyyy-MM-dd', new Date()));
    const openDays = Array.isArray(shopSettings.openDays) ? shopSettings.openDays : [];
    const diasDelBarbero = (barber && Array.isArray(barber.workingDays) && barber.workingDays.length > 0) ? barber.workingDays : null;
    const barberiaAbierta = openDays.includes(dayOfWeek);
    const barberoTrabaja = diasDelBarbero === null || diasDelBarbero.includes(dayOfWeek);
    const planPermiteEsteDia = !allowedDaysPlan || allowedDaysPlan.length === 0 || allowedDaysPlan.includes(dayOfWeek);
    return barberiaAbierta && barberoTrabaja && planPermiteEsteDia;
  };

  const getAvailableDates = (barber = null, allowedDaysPlan = null) => {
    // 🛡️ 1. Validación de seguridad inicial
    // Si no hay configuración cargada, devolvemos un array vacío de inmediato
    if (!shopSettings || !shopSettings.closeTime || !shopSettings.openDays) {
      return [];
    }

    const dates = [];
    const today = startOfDay(new Date());
    let startDateOffset = 0;
    const now = new Date();

    // 🛡️ 2. Fallback de seguridad para el split
    // ✅ FIX (mismo bug que timeSlots/lunchSlots): antes esto SIEMPRE
    // usaba shopSettings.closeTime global para decidir si "ya cerró hoy"
    // y saltar al día siguiente — ignorando que HOY podría tener un
    // horario variable distinto (ej: cierra más temprano un sábado).
    // Ahora busca el cierre específico de hoy si hay horario variable.
    const todayDayOfWeek = getDay(today);
    let closeTimeStr = shopSettings.closeTime || "20:00";
    if (shopSettings.hasVariableDays && shopSettings.variableSchedule?.[todayDayOfWeek]?.closeTime) {
      closeTimeStr = shopSettings.variableSchedule[todayDayOfWeek].closeTime;
    }
    // 🏠 A domicilio se atiende hasta más tarde (22:00 por defecto)
    if (modoDomicilio) closeTimeStr = getDomicilioConfig(shopSettings).horaFin || closeTimeStr;
    const [closeHour, closeMinute] = closeTimeStr.split(':').map(Number);

    // Lógica para saltar al día siguiente si la barbería ya cerró hoy
    // — 🛡️ SOLO aplica para un cliente reservando de verdad (no tiene
    // sentido que reserve para "hoy" si ya cerró). Para el admin, HOY
    // siempre queda disponible — antes esto lo hacía desaparecer de la
    // lista por completo (ni en pasado ni en futuro), porque el tramo
    // de fechas pasadas arranca en "ayer", nunca en "hoy".
    if (!isAdminAuth && (now.getHours() > closeHour || (now.getHours() === closeHour && now.getMinutes() > closeMinute))) {
      startDateOffset = 1;
    }

    // 🛡️ 3. Aseguramos que openDays sea un array para evitar errores con .includes
    const openDays = Array.isArray(shopSettings.openDays) ? shopSettings.openDays : [];

    // 📅 Días que trabaja ESTE barbero puntual — null significa "sin
    // restricción propia", trabaja cualquier día que la barbería esté
    // abierta (comportamiento de siempre, para barberos sin configurar).
    const diasDelBarbero = (barber && Array.isArray(barber.workingDays) && barber.workingDays.length > 0)
      ? barber.workingDays
      : null;

    // 🔍 Ventana de búsqueda ADAPTATIVA — antes buscaba siempre 14 días
    // fijos hacia adelante, lo cual alcanza de sobra para un barbero que
    // trabaja todos los días, pero se queda corto para uno que solo
    // trabaja, por ejemplo, los lunes (apenas 2 lunes entran en 14 días).
    // Ahora sigue buscando hasta juntar al menos 8 fechas disponibles,
    // sin importar cuántos días haga falta mirar — con un tope de
    // seguridad de 120 días para nunca quedar en un bucle eterno si un
    // barbero quedara mal configurado sin ningún día válido. Para un
    // barbero sin restricción, esto se comporta exactamente igual que
    // antes (con 14 días ya sobran de sobra los 8 mínimos).
    const MIN_FECHAS_DISPONIBLES = 8;
    const MAX_DIAS_A_BUSCAR = 120;

    let i = startDateOffset;
    while (
      (i < startDateOffset + 14 || dates.length < MIN_FECHAS_DISPONIBLES) &&
      i < startDateOffset + MAX_DIAS_A_BUSCAR
    ) {
      const date = addDays(today, i);
      const dayOfWeek = getDay(date);

      // Solo agregamos el día si la barbería está abierta ESE día, Y
      // (si aplica) el barbero elegido trabaja ESE día puntual, Y (si
      // aplica) el plan que el cliente eligió reservar lo permite ESE día.
      const barberiaAbierta = openDays.includes(dayOfWeek);
      const barberoTrabaja = diasDelBarbero === null || diasDelBarbero.includes(dayOfWeek);
      const planPermiteEsteDia = !allowedDaysPlan || allowedDaysPlan.length === 0 || allowedDaysPlan.includes(dayOfWeek);
      if (barberiaAbierta && barberoTrabaja && planPermiteEsteDia) {
        dates.push(format(date, 'yyyy-MM-dd'));
      }
      i++;
    }

    // 🗓️ MODO ADMIN — además de los días normales (hoy en adelante),
    // se suman los últimos 60 días también, para poder cargar turnos
    // que ya pasaron y nunca se registraron en la app (backfill de
    // historial real). Un cliente reservando normalmente NUNCA ve
    // esto — isAdminAuth solo es true cuando es el dueño/admin
    // logueado creando el turno a mano desde su panel.
    const fechasPasadas = [];
    if (isAdminAuth) {
      for (let i = 1; i <= 60; i++) {
        const date = subDays(today, i);
        const dayOfWeek = getDay(date);
        const barberiaAbierta = openDays.includes(dayOfWeek);
        const barberoTrabaja = diasDelBarbero === null || diasDelBarbero.includes(dayOfWeek);
        if (barberiaAbierta && barberoTrabaja) {
          fechasPasadas.push(format(date, 'yyyy-MM-dd'));
        }
      }
      fechasPasadas.reverse(); // que queden en orden cronológico (más vieja primero)
    }

    return [...fechasPasadas, ...dates];
  };

  const sendWhatsApp = async (booking) => {
    if (!booking.client?.phone) return;

    // 1. Limpiamos el número y preparamos datos
    // Adaptamos el número para el enlace wa.me (Paraguay 595 o Brasil 55)
    const phone = telefonoInternacional(booking.client.phone);

    const ticketId = booking.bookingGroupId ? booking.bookingGroupId.slice(-5) : '---';

    let dateStr = booking.date;
    try {
      const parsedDate = parse(booking.date, 'yyyy-MM-dd', new Date());
      dateStr = format(parsedDate, "EEE d MMM", { locale: es });
    } catch (e) {
      console.error("Error fecha", e);
    }

    // 2. Buscamos datos del local y de la reserva
    const bookingLocation = locations.find(l => l.id === booking.locationId);
    const localName = bookingLocation?.name || 'nuestra barbería';
    const mapLink = bookingLocation?.mapUrl ? `📍 Ubicación: ${bookingLocation.mapUrl}\n` : '';

    // Extraemos servicios y precio
    const serviceNames = booking.services?.length > 0
      ? booking.services.map(s => s.name).join(', ')
      : 'Servicio General';

    // Formato simple de Gs como en tu plantilla
    const totalPriceFormatted = booking.totalPrice ? `Gs ${booking.totalPrice}` : 'Gs 0';

    // 3. Construimos el mensaje unificado según tus plantillas de Meta
    let msg = `¡Hola ${booking.client.name} ! 💈\n\n`;

    if (booking.status === 'pending') {
      // MENSAJE 1: PENDIENTE (Requiere confirmación)
      msg += `Tu turno en ${localName} fue agendado con éxito 🙌\n`;
      msg += `Solo falta que lo confirmes respondiendo a este mensaje.\n\n`;
      msg += `🗓 Fecha: ${dateStr}\n`;
      msg += `⏰ Hora: ${booking.startTime}\n`;
      msg += `👨‍🦱 Barbero: ${booking.barber?.name || 'Por orden de llegada'}\n`;
      msg += `✂️ Servicio: ${serviceNames}\n`;
      msg += `💰 Precio: ${totalPriceFormatted}\n`;
      msg += `🎫 Ticket: #${ticketId}\n\n`;
      msg += `${mapLink}\n`;
      msg += `¡Te esperamos! 🔥`;
    }
    else {
      // MENSAJE 2: CONFIRMADO (Ya sea pago local o bancard)
      msg += `Tu turno en ${localName} fue agendado con éxito 🙌\n\n`;
      msg += `🗓 Fecha: ${dateStr}\n`;
      msg += `⏰ Hora: ${booking.startTime}\n`;
      msg += `👨‍🦱 Barbero: ${booking.barber?.name || 'Por orden de llegada'}\n`;
      msg += `✂️ Servicio: ${serviceNames}\n`;
      msg += `💰 Precio: ${totalPriceFormatted}\n`;
      msg += `🎫 Ticket: #${ticketId}\n\n`;
      msg += `${mapLink}\n`;
      msg += `¡Te esperamos! 🙌`;
    }

    // 4. ¡EL TOQUE MANUAL! Forzar la apertura de la App nativa de WhatsApp
    const encodedMsg = encodeURIComponent(msg);
    const waUrl = `whatsapp://send?phone=${phone}&text=${encodedMsg}`;

    window.location.href = waUrl;
  };


  // =====================================================================
  // 🔧 HELPER CENTRALIZADO: Resolver servidor correcto para cualquier booking
  // Versión blindada: reconoce a Capelli/Cabo Barberclub por companyId O
  // por locationId, sin depender de que el companyId esté guardado en
  // la reserva.
  // =====================================================================
  const CAPELLI_COMPANY_ID = 'nI6ilcu8qPbH3xiXXsM7';
  const CAPELLI_LOCATION_IDS = ['20aikKXImqJbfPaqXfG6'];

  // 🆕 Cabo Barberclub — mismo patrón que Capelli. El locationId de acá
  // abajo es un placeholder: reemplazarlo por el locationId real de Cabo
  // Barberclub en Firestore (locations/{id}) apenas se tenga a mano. El
  // atajo por companyId (más abajo, en resolverServidorWhatsApp) ya
  // funciona igual sin este dato — es solo un respaldo extra.
  const CABO_COMPANY_ID = '7H6FwtKVEpRz380mpRsB';
  const CABO_LOCATION_IDS = ['REEMPLAZAR_CON_LOCATION_ID_DE_CABO'];

  // 🆕 Celbenz — mismo patrón. A pedido: esto faltaba, por eso las
  // reservas de Celbenz se seguían yendo al servidor compartido
  // ('default') en vez de a su servidor dedicado — el código nunca
  // llegaba a enterarse de que Celbenz existía.
  const CELBENZ_COMPANY_ID = 'Mo3F8xvssKoyO61oIH2p';
  const CELBENZ_LOCATION_IDS = ['008EVefV4KaMBjI1gkJO'];

  const WHATSAPP_SERVERS = {
    [CAPELLI_COMPANY_ID]: 'https://barbergo-whatsapp-api-capelli-production.up.railway.app',
    [CABO_COMPANY_ID]: 'https://cabo-whatsapp-bot-production.up.railway.app',
    [CELBENZ_COMPANY_ID]: 'https://celbenz-production.up.railway.app',
    'default': 'https://barbergo-whatsapp-api-production.up.railway.app'
  };

  const resolverServidorWhatsApp = async (locId, companyIdDirecto) => {
    const locStr = String(locId || '').trim();

    // 🥇 0. ATAJO INFALIBLE: si el locationId es de Capelli, Cabo
    // Barberclub o Celbenz, va directo a su servidor propio. No depende
    // de que la reserva o la location tengan companyId guardado.
    if (CAPELLI_LOCATION_IDS.includes(locStr)) {
      return { serverUrl: WHATSAPP_SERVERS[CAPELLI_COMPANY_ID], companyId: CAPELLI_COMPANY_ID };
    }
    if (CABO_LOCATION_IDS.includes(locStr)) {
      return { serverUrl: WHATSAPP_SERVERS[CABO_COMPANY_ID], companyId: CABO_COMPANY_ID };
    }
    if (CELBENZ_LOCATION_IDS.includes(locStr)) {
      return { serverUrl: WHATSAPP_SERVERS[CELBENZ_COMPANY_ID], companyId: CELBENZ_COMPANY_ID };
    }

    // 1. Usar el companyId que nos llegó directo
    let companyId = companyIdDirecto;

    // 2. Si no hay companyId, buscar en el array de locations en memoria
    if (!companyId && locStr && locations?.length > 0) {
      const locMatch = locations.find(l => String(l.id) === locStr);
      if (locMatch?.companyId) companyId = locMatch.companyId;
    }

    // 3. Si todavía no hay, hacer fetch a Firestore (último recurso)
    if (!companyId && locStr) {
      try {
        const locSnap = await getDoc(doc(db, "locations", locStr));
        if (locSnap.exists()) companyId = locSnap.data().companyId || null;
      } catch (e) {
        console.error("resolverServidor: error fetch Firestore", e);
      }
    }

    // 4. Limpiar valores inválidos
    if (companyId === 'default_tenant') companyId = null;

    const serverUrl = WHATSAPP_SERVERS[companyId] || WHATSAPP_SERVERS['default'];
    return { serverUrl, companyId };
  };

  // =====================================================================
  // 📤 ENVIAR WHATSAPP AUTOMÁTICO — versión blindada
  // =====================================================================
  const enviarWhatsAppAutomatico = async (clientPhoneOrBooking, clientName, dateStr, timeStr, ticketId, status, paymentMethod, barberName, serviceName, servicePrice, locationId) => {

    // 🕵️ DETECTOR: objeto completo o parámetros sueltos
    let booking = null;
    if (clientPhoneOrBooking && typeof clientPhoneOrBooking === 'object') {
      booking = clientPhoneOrBooking;
    }

    // 🔇 Guardia centralizado — si ESTE turno puntual se marcó como
    // "sin WhatsApp" al crearlo (checkbox destildado en el modal de
    // Agregar Turno), ningún llamado posterior con el objeto completo
    // (marcar completado, reenviar plantilla, etc.) le manda nada a
    // este cliente. Solo aplica cuando se llama con el objeto —
    // llamados con teléfono/parámetros sueltos (como el de borrar) se
    // filtran aparte, en el lugar donde se originan.
    if (booking?.sinWhatsApp === true) {
      return;
    }

    // Mapeo seguro de datos
    const phone = booking ? booking.client?.phone : clientPhoneOrBooking;
    const name = booking ? booking.client?.name : clientName;
    const dStr = booking ? booking.date : dateStr;
    const tStr = booking ? booking.startTime : timeStr;
    const tId = booking ? (booking.bookingGroupId ? booking.bookingGroupId.slice(-5) : '---') : ticketId;
    const currentStatus = booking ? booking.status : status;
    const barber = booking ? booking.barber?.name : barberName;
    const locId = booking ? booking.locationId : locationId;
    const companyIdDirecto = booking?.companyId || null;
    // ✅ FIX (2026-07-10): bookingGroupId COMPLETO (sin recortar), lo usa
    // el servidor para clavar pending_confirmations sin ambigüedad al
    // mandar solicitud_reserva_v3. Solo existe cuando llamamos con el
    // objeto booking completo (confirmBooking).
    const fullBookingGroupId = booking?.bookingGroupId || null;
    // 📊 Para que el servidor pueda distinguir "reenvío manual desde
    // Enviar Auto" de una solicitud fresca — ambas usan la misma
    // plantilla, así que sin esta marca serían indistinguibles del
    // lado del servidor.
    const esReenvioManual = booking?.esReenvioManual === true;

    let sName = serviceName;
    if (booking) {
      sName = booking.services?.length > 0
        ? booking.services.map(s => s.name).join(', ')
        : 'Servicio General';
    }
    let sPrice = servicePrice;
    if (booking) {
      sPrice = booking.totalPrice ?? 0;
    }

    // ============================================================
    // CASO ESPECIAL: completed — delegar SIEMPRE al servidor correcto
    // ============================================================
    if (currentStatus === 'completed') {
      // ✅ FIX: /api/reserva-completada es específicamente el que dispara
      // la plantilla de "calificanos tu visita" — antes esto se mandaba
      // sin importar el switch de Reseñas del SuperAdmin (solo miraba el
      // plan). Como los tres lugares que completan una reserva pasan por
      // esta misma función, alcanza con este único chequeo para tapar
      // el envío en los tres a la vez.
      if (!enabledFeatures?.reviews) {
        return;
      }

      // Necesitamos el ID de documento Firestore (no el bookingGroupId)
      const docId = booking?.id;
      if (!docId) {
        console.error("⚠️ enviarWhatsAppAutomatico(completed): falta booking.id (doc ID de Firestore)");
        return;
      }

      try {
        // 1. Resolver el servidor y extraer el companyId
        const { serverUrl, companyId } = await resolverServidorWhatsApp(locId, companyIdDirecto);

        await fetch(`${serverUrl}/api/reserva-completada`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingId: docId,
            companyId: companyId // 👈 NUEVO: Le pasamos la empresa al backend
          })
        });
      } catch (e) {
        console.error('❌ Error en reserva-completada:', e);
      }
      return;
    }

    // ============================================================
    // RESTO DE ESTADOS: pending, confirmed, cancelled
    // ============================================================
    const { serverUrl, companyId } = await resolverServidorWhatsApp(locId, companyIdDirecto);

    // Datos del local (nombre, mapa, slug)
    let shopName = selectedLocation?.name;
    let mapLink = selectedLocation?.mapUrl;
    let shopSlug = selectedLocation?.slug;

    // Si el local activo no coincide con el de la reserva, fetch desde Firestore
    if (locId && selectedLocation?.id !== locId) {
      try {
        const locSnap = await getDoc(doc(db, "locations", String(locId)));
        if (locSnap.exists()) {
          const data = locSnap.data();
          shopName = data.name || shopName;
          mapLink = data.mapUrl || mapLink;
          shopSlug = data.slug || data.id || locId;
        }
      } catch (error) {
        console.error("Error buscando info del local:", error);
      }
    }

    // Fallbacks
    shopName = shopName || shopSettings?.shopName || 'la barbería';
    mapLink = mapLink || 'https://maps.app.goo.gl/tu-local';
    const shopUrl = shopSlug
      ? `https://app.barbergo.com.py/${shopSlug}`
      : 'https://app.barbergo.com.py';

    // Paraguay (595) o Brasil (55) — ver telefonoInternacional en utils/phone.
    const numeroLimpio = telefonoInternacional(phone);

    // Formatear fecha
    let formattedDate = dStr;
    try {
      const parsedDate = parse(dStr, 'yyyy-MM-dd', new Date());
      formattedDate = format(parsedDate, "EEE d MMM", { locale: es });
    } catch (e) { /* mantener dStr */ }

    /// Elegir plantilla según estado y empresa
    let templateName = '';
    let variablesPlantilla = [];

    // 👇 DETECTAMOS SI ES CAPELLI PARA CAMBIAR LOS NOMBRES 👇
    const isCapelli = companyId === 'nI6ilcu8qPbH3xiXXsM7';

    if (currentStatus === 'pending') {
      templateName = isCapelli ? 'solicitud_reserva_capelli_v1' : 'solicitud_reserva_v3';
      variablesPlantilla = [name || 'Cliente', shopName, formattedDate, tStr, barber || 'Barbero asignado', sName || 'Servicio agendado', String(sPrice), tId, mapLink];
    } else if (currentStatus === 'confirmed') {
      templateName = isCapelli ? 'reserva_confirmada_capelli_v1' : 'reserva_confirmada_v2';
      variablesPlantilla = [name || 'Cliente', shopName, formattedDate, tStr, barber || 'Barbero asignado', sName || 'Servicio agendado', String(sPrice), tId, mapLink];
    } else if (currentStatus === 'cancelled') {
      templateName = isCapelli ? 'reserva_cancelada_capelli_v1' : 'reserva_cancelada_v3';
      variablesPlantilla = [name || 'Cliente', shopName, formattedDate, tStr, barber || 'Barbero asignado', sName || 'Servicio agendado', String(sPrice), tId, shopUrl];
    }

    if (!templateName) return;


    try {
      const response = await fetch(`${serverUrl}/api/enviar-mensaje`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: numeroLimpio,
          templateName: templateName,
          params: variablesPlantilla,
          locationId: locId,
          companyId: companyId,   // ← enviamos companyId explícito al servidor
          bookingGroupId: fullBookingGroupId,  // 👈 para clavar pending_confirmations sin ambigüedad
          esReenvioManual // 👈 para que el servidor categorice bien el desglose de uso
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ Error enviando '${templateName}':`, errorData);
      } else {
        // ✅ FIX: el server responde 200 OK incluso cuando BLOQUEA un envío
        // por falta de cupo (manda { success: false, blocked: motivo } con
        // status 200, no un error HTTP). response.ok solo mira el status,
        // así que antes esto imprimía "✅ enviada" aunque en realidad no se
        // haya mandado nada — la única señal de que algo se bloqueó estaba
        // rota. Ahora se revisa el campo `success` del JSON antes de loguear.
        const data = await response.json().catch(() => ({}));
        if (!data.success) {
          console.error(`🚫 '${templateName}' NO se envió a ${numeroLimpio} — bloqueado:`, data.blocked || data.reason || 'motivo desconocido');
        } else if (data.skipped) {
        } else {
        }
      }
    } catch (error) {
      console.error("❌ Error de red:", error);
    }
  };

  const handleAdminLogin = async () => {
    setLoading(true);
    const auth = getAuth();
    try {
      // ✅ Forzar persistencia para que la sesión sobreviva al cerrar la PWA
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

      // 🔧 FIX: "barbers", "locations" y "selectedLocation" son estados
      // COMPARTIDOS entre la vista de cliente (reserva pública) y el
      // panel de admin — si en esta misma pestaña se había navegado o
      // probado la vista de cliente de CUALQUIER barbería antes de
      // loguearse acá, esos datos quedaban pegados y se veían por un
      // instante en el panel de admin (barberos de otro local), hasta
      // que la carga propia del admin los terminaba pisando. Se limpian
      // acá a mano, ANTES de que arranque la carga real del admin, para
      // que nunca puedan quedar datos de otro contexto a la vista.
      setBarbers([]);
      setLocations([]);
      setSelectedLocation(null);
      setServices([]);
      setProducts([]);
      setBookings([]);
      setBookingsAntiguas([]);
      setHistorialListoDe(null);
      setVentanaRecientes(null);

      setIsPlanLoading(true);
      setIsAdminAuth(true);
      localStorage.setItem('admin-auth', 'true');
      setView('admin');
      // ✅ FIX: acá faltaba apagar el loading cuando el login SALE BIEN —
      // antes solo se apagaba en el catch (si fallaba). Si las
      // credenciales eran correctas, el botón se quedaba con el spinner
      // pegado para siempre, sin ningún error en consola (porque
      // técnicamente no hubo ningún error — el login funcionó).
      setLoading(false);

    } catch (error) {
      console.error("Error login:", error);
      showAlert("Acceso Denegado", "Correo o contraseña incorrectos.");
      setAdminPassword('');
      setLoading(false);
      setIsPlanLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    // 1. Cerrar sesión en Firebase de forma segura
    const auth = getAuth();
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error al cerrar sesión en Firebase:", error);
    }

    // 2. 🧹 LIMPIEZA TOTAL DE ESTADOS (Reset a valores iniciales)
    // Esto vacía la memoria RAM del navegador inmediatamente
    setCurrentUser(null);
    setIsAdminAuth(false);
    setAdminEmail('');
    setAdminPassword('');

    // 👇 Datos del Negocio (Los que se estaban mezclando)
    setLocations([]);
    setBarbers([]);
    setServices([]);
    setCategories([]); // 👈 ¡CRÍTICO! También hay que vaciar las categorías
    setBookings([]);
    setBookingsAntiguas([]);
    setHistorialListoDe(null);
    setVentanaRecientes(null);
    setMyBookings([]);

    // 👇 Ajustes y Selección
    setSelectedAdminLocationId(null); // Usar null es más seguro que '' para validaciones
    setSelectedLocation(null);
    setShopSettings({});
    setBancardKeys({ public_key: '', private_key: '' });

    // 👇 Planes y Permisos
    if (typeof setCurrentPlan === 'function') setCurrentPlan(null);
    setEnabledFeatures({});
    setIsPlanLoading(false);
    setIsShopSuspended(false);

    // 3. 🗑️ LIMPIEZA DE PERSISTENCIA (LocalStorage / SessionStorage)
    try {
      localStorage.removeItem('admin-auth');
      localStorage.removeItem('paymentOrigin');
      localStorage.removeItem('paymentTicket');
      // Si usas sessionStorage para algún escudo de duplicados, límpialo aquí
      sessionStorage.clear();
    } catch (e) { /* navegador restringido — el logout sigue igual, solo no se limpia esto */ }

    // 4. 🔄 RESET DE NAVEGACIÓN
    // Forzamos la vista de admin para que muestre el Login limpio
    setView('admin');
    setAdminSubView('calendar'); // Resetear la subvista por defecto
    setStep(0); // Resetear el wizard si quedó a medias

    // 5. Notificar al usuario
    addToast("Sesión Cerrada", "Los datos del negocio han sido protegidos y liberados.", "info");

    // Opcional: Forzar un pequeño delay y recargar si quieres estar 100% seguro
    // window.location.reload(); 
  };

  // 🔢 Sin largo fijo de 10: algunos clientes reservan con número
  // brasilero (11 díg., o 13 con el 55). Solo se limita a 15 dígitos.
  const MAX_PHONE_LEN = 15;

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    if (/^\d*$/.test(phone) && phone.length <= MAX_PHONE_LEN) {
      setClientInfo({ ...clientInfo, phone: phone });

      if (phone.length === 0) {
        setPhoneError('El número de teléfono es obligatorio.');
      } else if (phone.length < 8) {
        setPhoneError('Número incompleto (ej: 0981234567).');
      } else {
        setPhoneError('');
      }
    }
  };

  const formatOpenDays = (daysArray) => {
    if (!daysArray || daysArray.length === 0) return "Cerrado";
    if (daysArray.length === 7) return "Todos los días";
    const sortedDays = [...daysArray].sort((a, b) => a - b);
    if (JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4, 5, 6])) return "Lunes a Sábados";
    if (JSON.stringify(sortedDays) === JSON.stringify([1, 2, 3, 4, 5])) return "Lunes a Viernes";
    if (JSON.stringify(sortedDays) === JSON.stringify([0, 1, 2, 3, 4, 5])) return "Domingo a Viernes";
    return sortedDays.map(d => weekDayNames[d]).join(', ');
  };

  // =====================================================================
  // 💱 CONVERSOR RÁPIDO PARA EL ADMIN (misma lógica que ClientView.jsx)
  // ---------------------------------------------------------------------
  // Usa shopSettings.altCurrencyCode + exchangeRates (ya cacheado, base
  // en guaraníes) para convertir cualquier monto a la moneda que esa
  // barbería eligió en Configuración. Se usa tanto en el modal de
  // Editar Reserva (para cobrar puntual) como en el Conversor Rápido
  // flotante de ModernAdminLayout (para cualquier monto suelto).
  // =====================================================================
  const CURRENCY_SYMBOLS = { BRL: 'R$', ARS: 'AR$', BOB: 'Bs.', USD: 'US$' };
  const CURRENCY_FLAGS = { BRL: '🇧🇷', ARS: '🇦🇷', BOB: '🇧🇴', USD: '🇺🇸' };
  const altCurrencyEnabled = !!(shopSettings?.showAltCurrency && exchangeRates?.[shopSettings?.altCurrencyCode || 'BRL']);
  const formatAltCurrency = (gsAmount) => {
    const code = shopSettings?.altCurrencyCode || 'BRL';
    const rate = exchangeRates?.[code];
    if (!rate || !gsAmount) return null;
    const monto = Number(gsAmount) * rate;
    const simbolo = CURRENCY_SYMBOLS[code] || code;
    const bandera = CURRENCY_FLAGS[code] || '';
    return `${bandera} ${simbolo} ${monto.toFixed(2).replace('.', ',')}`;
  };


  // --------------------------------------------------------------------------
  // ESCUCHADOR DE RESERVAS EN TIEMPO REAL (CON CANDADO MULTI-TENANT)
  // --------------------------------------------------------------------------
  // --------------------------------------------------------------------------
  // 🚀 ESCUCHADOR DE RESERVAS EN TIEMPO REAL (ÚNICO Y DEFINITIVO)
  // --------------------------------------------------------------------------
  const bookingsListenerLocId = esStaffConEmpresa ? null : (selectedLocation?.id || null);

  useEffect(() => {


    // 1. Evitar que escuche el superadmin
    if (currentUser?.role === 'superadmin') return;

    let q;
    let desdeRecientes = null;

    // 2. Si es DUEÑO o BARBERO, carga la agenda de su empresa desde hace 7
    // días en adelante (⚡ etapa 1 de la CARGA EN DOS ETAPAS — lo anterior lo
    // trae el escuchador del historial, más abajo).
    if (currentUser && (currentUser.role === 'owner' || currentUser.role === 'barber')) {
      if (!currentUser.companyId) return;
      desdeRecientes = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      q = query(collection(db, 'bookings'),
        where('companyId', '==', currentUser.companyId),
        where('date', '>=', desdeRecientes));
    }
    // 3. 🟢 Si es CLIENTE o VISITANTE, carga SOLO la agenda del local que está mirando
    // 💸 FIX DE COSTOS: y solo desde HOY en adelante. Antes bajaba todo
    // el historial del local en cada visita a la página de reservas (miles
    // de documentos, cada uno cobrado como lectura) — y el cliente solo lo
    // usa para saber qué horarios futuros están ocupados (isBlockFree,
    // DateTimeSelector). Usa el índice locationId + date que ya existe.
    // Cualquier otro rol (ej. un "admin" viejo) sigue con el historial completo.
    else if (!currentUser || currentUser.role === 'client') {
      if (!selectedLocation?.id) return;
      q = query(collection(db, 'bookings'),
        where('locationId', '==', selectedLocation.id),
        where('date', '>=', format(new Date(), 'yyyy-MM-dd')));
    }
    else {
      if (!selectedLocation?.id) return;
      q = query(collection(db, 'bookings'), where('locationId', '==', selectedLocation.id));
    }

    // 🛡️ LLAVE DE PASO LOCAL: Se reinicia correctamente cada vez que cambia el usuario o local
    let isInitialLoad = true;

    // ⚡ Al arrancar, Firestore puede entregar primero los documentos que ya
    // tiene en memoria (una foto parcial) y recién después la respuesta del
    // servidor. La "primera carga" es la del servidor; includeMetadataChanges
    // asegura enterarse de ese paso aunque no cambie ningún documento.
    const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (querySnapshot) => {

      // --- PRIMERA CARGA (100% Silenciosa) ---
      if (isInitialLoad) {
        if (querySnapshot.metadata.fromCache) return;
        isInitialLoad = false;
        // Los barberos reciben toda la agenda de la empresa (como hacía
        // loadBookings) — BarberPortal ya filtra lo suyo con un criterio
        // más flexible (id, email o nombre) que un barberId exacto.
        const initialData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

        initialData.sort((a, b) => {
          let timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (new Date(a.createdAt).getTime() || 0);
          let timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (new Date(b.createdAt).getTime() || 0);
          return timeB - timeA;
        });

        setBookings(initialData);
        // ⚡ Etapa 1 lista: recién ahora arranca el escuchador del historial.
        if (desdeRecientes) setVentanaRecientes(`${currentUser.companyId}|${desdeRecientes}`);
        return; // Retornamos para cortar la ejecución y evitar notificaciones
      }

      // --- CAMBIOS POSTERIORES (Nuevas reservas reales) ---
      querySnapshot.docChanges().forEach((change) => {
        const data = { ...change.doc.data(), id: change.doc.id };

        setBookings(prev => {
          if (change.type === 'added') {
            const exists = prev.some(b => b.id === data.id);
            if (exists) return prev;
            return [data, ...prev];
          }
          if (change.type === 'modified') {
            return prev.map(b => b.id === data.id ? data : b);
          }
          if (change.type === 'removed') {
            return prev.filter(b => b.id !== data.id);
          }
          return prev;
        });

        // 🔔 NOTIFICACIONES - VERSIÓN CON RASTREADOR (DEBUG)
        if (change.type === 'added' && data.isPrimary) {

          let bookingTime = 0;
          if (data.createdAt) {
            if (typeof data.createdAt.toMillis === 'function') bookingTime = data.createdAt.toMillis();
            else if (data.createdAt.seconds) bookingTime = data.createdAt.seconds * 1000;
            else bookingTime = new Date(data.createdAt).getTime() || 0;
          }

          const isGenuinelyNew = bookingTime > (Date.now() - 300000);

          const currentLocId = selectedAdminLocationId || selectedLocation?.id || locations[0]?.id;

          const isSameLocation = String(data.locationId) === String(currentLocId);

          const isNotMe = data.userId !== currentUser?.uid;

          // Un barbero solo recibe aviso de SUS turnos (antes eso lo hacía
          // el filtro por barberId que tenía este escuchador).
          const esDeOtroBarbero = currentUser?.role === 'barber' && String(data.barber?.id) !== String(currentUser.barberId);

          if (isGenuinelyNew && isSameLocation && isNotMe && !esDeOtroBarbero) {

            if (!isAdminAuth) return;
            const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner';
            const isBarber = currentUser?.role === 'barber';
            const isUnassigned = !data.barber?.id;

            if (isAdmin || isBarber || isUnassigned) {
              const groupId = data.bookingGroupId || data.id;

              if (notifiedBookingsRef.current.has(groupId)) {
                return;
              }

              notifiedBookingsRef.current.add(groupId);
              setTimeout(() => notifiedBookingsRef.current.delete(groupId), 30000);

              // 📳 VIBRACIÓN
              if (navigator.vibrate) navigator.vibrate([500, 200, 500]);

              // 🔔 TOAST (solo visual, sin sonido duplicado)
              // 🔧 FIX: usaba "data.time", un campo que ninguna reserva
              // tiene — en toda la app se guarda como "data.startTime".
              // Por eso salía "undefined" en vez de la hora real.
              const titleToast = isBarber ? "¡Nuevo Turno para TI!" : "¡Nueva Reserva!";
              addToast(titleToast, `Cliente: ${data.client?.name} - ${data.startTime}`, "success", false);
            }
          } else {
          }
        }
      });
    }, (error) => console.error('Error escuchando reservas:', error));

    return () => unsubscribe();
    // 💸 FIX DE COSTOS: antes dependía del objeto "selectedLocation"
    // entero. Cada vez que cambiaba (ej. al abrir "Nuevo turno" desde el
    // Calendario, que hace setSelectedLocation), el escuchador se
    // reiniciaba y volvía a bajar TODO el historial de la empresa, aunque
    // la consulta del dueño/barbero ni siquiera usa el local. Ahora solo
    // se reinicia si cambia algo que de verdad cambia la consulta.
  }, [currentUser, bookingsListenerLocId, addToast]);

  // ⚡ ETAPA 2 de la CARGA EN DOS ETAPAS — solo dueño/barbero: los turnos
  // ANTERIORES a la ventana de la etapa 1 (misma fecha de corte, así no se
  // repite ningún documento). Arranca recién cuando la etapa 1 ya llegó,
  // para no competirle el ancho de banda. Se queda escuchando: si se edita
  // o borra una reserva vieja, también se actualiza.
  useEffect(() => {
    if (!esStaffConEmpresa || !ventanaRecientes) return;
    const [empresa, desde] = ventanaRecientes.split('|');
    if (empresa !== currentUser?.companyId) return;

    let yaCargado = false;
    const q = query(collection(db, 'bookings'),
      where('companyId', '==', empresa),
      where('date', '<', desde));
    const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
      if (snap.metadata.fromCache) return;
      if (yaCargado && snap.docChanges().length === 0) return; // solo cambió metadata
      yaCargado = true;
      setBookingsAntiguas(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      setHistorialListoDe(empresa);
    }, (error) => console.error('Error cargando el historial de reservas:', error));
    return () => unsubscribe();
  }, [esStaffConEmpresa, currentUser?.companyId, ventanaRecientes]);



  // 👇 2. NUEVA FUNCIÓN searchMyBookings (Con candado de empresa) 👇
  // 🎫 MEMBRESÍA PROPIA — para que el cliente pueda ver su estado (plan,
  // visitas usadas/restantes, renovación) desde "Mis Reservas", sin tener
  // que arrancar una reserva nueva solo para enterarse. Versión más
  // simple que checkMembershipCoverage: acá no importa qué servicio o
  // qué día — es solo "¿soy socio, y cómo vengo este mes?".
  const [myMembership, setMyMembership] = useState(null); // { member, plan } o null

  // ✅ Ahora acepta teléfono O email — se detecta solo por si tiene "@".
  // Antes solo reconocía por teléfono; esto le permite al cliente
  // identificarse con cualquiera de los dos (útil si cambió de número
  // pero sigue usando el mismo correo de siempre).
  // ✅ Ahora acepta teléfono, CI, o email — se detecta el email solo por
  // si tiene "@". Para el resto (todo dígitos), no hay forma 100% segura
  // de saber si es teléfono o CI con solo mirarlo, así que probamos
  // primero como teléfono y, si no aparece nadie, probamos de nuevo como
  // CI — sin pedirle al cliente que aclare cuál de los dos escribió.
  const checkMyMembership = async (identifier, locId) => {
    if (!identifier || !locId) { setMyMembership(null); return null; }
    try {
      const esEmail = String(identifier).includes('@');
      let member = null;

      if (esEmail) {
        const cleanEmail = String(identifier).trim().toLowerCase();
        if (!cleanEmail) { setMyMembership(null); return null; }
        const q = query(
          collection(db, 'locations', locId, 'members'),
          where('clientEmail', '==', cleanEmail),
          where('status', '==', 'active')
        );
        const snap = await getDocs(q);
        if (!snap.empty) member = { id: snap.docs[0].id, ...snap.docs[0].data() };
      } else {
        const cleanDigits = String(identifier).replace(/\D/g, '');
        if (!cleanDigits) { setMyMembership(null); return null; }

        const qPhone = query(
          collection(db, 'locations', locId, 'members'),
          where('clientPhone', 'in', variantesTelefono(cleanDigits)),
          where('status', '==', 'active')
        );
        const snapPhone = await getDocs(qPhone);
        if (!snapPhone.empty) {
          member = { id: snapPhone.docs[0].id, ...snapPhone.docs[0].data() };
        } else {
          const qCi = query(
            collection(db, 'locations', locId, 'members'),
            where('clientCi', '==', cleanDigits),
            where('status', '==', 'active')
          );
          const snapCi = await getDocs(qCi);
          if (!snapCi.empty) member = { id: snapCi.docs[0].id, ...snapCi.docs[0].data() };
        }
      }

      if (!member) { setMyMembership(null); return null; }
      member = visitasDelPeriodo(member);
      const planSnap = await getDoc(doc(db, 'locations', locId, 'membershipPlans', member.planId));
      if (!planSnap.exists()) { setMyMembership(null); return null; }
      const plan = { id: planSnap.id, ...planSnap.data() };
      // 🔧 A pedido: mismo criterio de vencimiento que checkMembershipCoverage
      // — si ya pasó renewalDate y no pagó, queda marcado como vencida.
      // Sigue devolviendo los datos del socio (para poder avisarle que
      // debe, con su plan y todo) pero con expired:true, para que
      // ClientView NO le auto-seleccione el plan como si estuviera al
      // día — el descuento no debe aplicar hasta que pague.
      const hoyParaExpiracion = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
      const expired = member.cicloPagado === false || !!(member.renewalDate && member.renewalDate < hoyParaExpiracion);
      const result = { member, plan, expired };
      setMyMembership(result);
      return result;
    } catch (e) {
      console.error('❌ Error chequeando mi membresía:', e.message);
      setMyMembership(null);
      return null;
    }
  };

  // Fallback: si el cliente está logueado y ya cargaron sus reservas (por
  // el listener en tiempo real más abajo), usamos el teléfono de su
  // primera reserva para chequear membresía sin que tenga que buscar a
  // mano. La búsqueda manual (guest) se dispara aparte, en searchMyBookings.
  useEffect(() => {
    if (!selectedLocation?.id || myBookings.length === 0) return;
    const phone = myBookings[0]?.client?.phone;
    if (phone) checkMyMembership(phone, selectedLocation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBookings, selectedLocation?.id]);


  const searchMyBookings = async () => {
    const searchTerm = searchPhone.trim();
    if (!searchTerm) {
      setMyBookings([]);
      return;
    }

    setClientLoading(true);
    try {
      const phoneSearch = searchTerm.replace(/\D/g, '');

      // 🔍 DETECTOR DE EMPRESA INTELIGENTE
      let currentCompanyId = selectedLocation?.companyId;
      if (!currentCompanyId && locations.length > 0) {
        const pathSlug = window.location.pathname.replace('/', '');
        if (pathSlug) {
          const match = locations.find(l => l.slug === pathSlug || l.name.toLowerCase().replace(/\s+/g, '-') === pathSlug || (l.slug && l.slug.startsWith(pathSlug)));
          if (match) currentCompanyId = match.companyId;
        }
        // Respaldo por si entra a la ruta raíz (localhost:3000/) sin link específico
        if (!currentCompanyId) currentCompanyId = locations[0].companyId;
      }

      let q = collection(db, 'bookings');
      if (currentCompanyId) {
        q = query(collection(db, 'bookings'), where('companyId', '==', currentCompanyId));
      }

      const querySnapshot = await getDocs(q);
      const foundBookings = [];

      querySnapshot.forEach((doc) => {
        const b = { ...doc.data(), id: doc.id };
        if (b.isPrimary === true && b.status !== 'cancelled') {
          // 🔒 DOBLE SEGURIDAD: Descartar si por algún motivo no coincide la empresa
          if (currentCompanyId && b.companyId !== currentCompanyId) return;

          const bPhone = b.client?.phone?.replace(/\D/g, '');
          const bTicket = b.bookingGroupId ? String(b.bookingGroupId).slice(-5) : '';
          // ✅ También se puede buscar por CI — reusamos phoneSearch (ya es
          // la versión solo-dígitos de lo que escribió) para comparar
          // contra el CI guardado, también limpiado de puntos/guiones.
          const bCi = b.client?.ci?.replace(/\D/g, '');

          if (bPhone === phoneSearch || bTicket === searchTerm || (bCi && phoneSearch && bCi === phoneSearch)) {
            foundBookings.push(b);
          }
        }
      });

      // Ordenamiento seguro
      foundBookings.sort((a, b) => {
        let timeA = 0; let timeB = 0;
        if (a.createdAt) {
          if (typeof a.createdAt.toMillis === 'function') timeA = a.createdAt.toMillis();
          else if (a.createdAt.seconds) timeA = a.createdAt.seconds * 1000;
          else timeA = new Date(a.createdAt).getTime() || 0;
        }
        if (b.createdAt) {
          if (typeof b.createdAt.toMillis === 'function') timeB = b.createdAt.toMillis();
          else if (b.createdAt.seconds) timeB = b.createdAt.seconds * 1000;
          else timeB = new Date(b.createdAt).getTime() || 0;
        }
        return timeB - timeA;
      });

      setMyBookings(foundBookings);

      // 🎫 Chequeo de membresía propia — corre siempre que se busca por
      // teléfono, tenga o no reservas previas (un socio nuevo puede no
      // tener ninguna reserva online todavía).
      if (selectedLocation?.id) {
        checkMyMembership(phoneSearch, selectedLocation.id);
      }
    } catch (error) {
      console.error("Error buscando reservas:", error);
      addToast("Error", "No se pudo buscar la reserva.", "error");
    }
    setClientLoading(false);
  };


  // --------------------------------------------------------------------------
  // 🚀 CARGA AUTOMÁTICA DE "MIS RESERVAS" (MUESTRA ANTES DE ELEGIR SUCURSAL)
  // --------------------------------------------------------------------------
  useEffect(() => {
    // 1. Si no hay usuario, limpiamos y salimos.
    if (!currentUser || (!currentUser.uid && !currentUser.email)) {
      setMyBookings([]);
      return;
    }

    // 🛑 2. DETECTOR DE EMPRESA INTELIGENTE (Lee la URL)
    let currentCompanyId = selectedLocation?.companyId || null;
    const currentLocationId = selectedLocation?.id || null;

    // Si no hay local seleccionado, leemos el link para saber en qué barbería estamos
    if (!currentCompanyId && locations && locations.length > 0) {
      const pathSlug = window.location.pathname.replace('/', '');
      if (pathSlug) {
        // Buscamos qué local coincide con la URL (ej: "edu-barber")
        const match = locations.find(l =>
          l.slug === pathSlug ||
          l.name.toLowerCase().replace(/\s+/g, '-') === pathSlug ||
          (l.slug && l.slug.startsWith(pathSlug))
        );
        if (match) {
          currentCompanyId = match.companyId;
        }
      }
    }

    // Si no logramos detectar ni la empresa por URL ni el local, esperamos.
    if (!currentCompanyId && !currentLocationId) {
      return;
    }

    const bookingsMap = new Map();

    const updateBookingsList = () => {
      const userBookings = [];

      bookingsMap.forEach((b) => {
        if (b.status !== 'cancelled' && b.type !== 'block' && b.isPrimary !== false) {

          let isFromThisShop = false;

          // 3. FILTRO POR EMPRESA (Detectada por URL o Selección)
          if (currentCompanyId && b.companyId === currentCompanyId) {
            isFromThisShop = true;
          }
          // 4. FILTRO DE RESPALDO POR SUCURSAL
          else if (currentLocationId && b.locationId === currentLocationId) {
            isFromThisShop = true;
          }

          if (isFromThisShop) {
            userBookings.push(b);
          }
        }
      });

      // Ordenar: Las más nuevas arriba
      userBookings.sort((a, b) => {
        let timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (new Date(a.createdAt).getTime() || 0);
        let timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (new Date(b.createdAt).getTime() || 0);
        return timeB - timeA;
      });

      setMyBookings([...userBookings]);
    };

    let unsubUid;
    let unsubEmail;

    if (currentUser.uid) {
      const qUid = query(collection(db, 'bookings'), where('userId', '==', currentUser.uid));
      unsubUid = onSnapshot(qUid, (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'removed') bookingsMap.delete(change.doc.id);
          else bookingsMap.set(change.doc.id, { ...change.doc.data(), id: change.doc.id });
        });
        updateBookingsList();
      });
    }

    if (currentUser.email) {
      const qEmail = query(collection(db, 'bookings'), where('client.email', '==', currentUser.email));
      unsubEmail = onSnapshot(qEmail, (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'removed') bookingsMap.delete(change.doc.id);
          else bookingsMap.set(change.doc.id, { ...change.doc.data(), id: change.doc.id });
        });
        updateBookingsList();
      });
    }

    return () => {
      if (unsubUid) unsubUid();
      if (unsubEmail) unsubEmail();
    };

    // Dependencias clave para re-evaluar si cambia la URL o terminan de cargar los locales
  }, [currentUser?.uid, currentUser?.email, selectedLocation?.id, selectedLocation?.companyId, locations]);


  useEffect(() => {
    // 🛑 YA NO CARGAMOS DATOS AQUÍ PARA EVITAR MEZCLAR EMPRESAS 🛑

    // Solo dejamos la Lógica de Enrutamiento Inteligente
    const handleRouting = () => {
      const hash = window.location.hash;
      // 🛡️ Esta es la lógica que decide QUÉ VISTA mostrar apenas entra
      // cualquier visitante — si localStorage está bloqueado (navegador
      // embebido de Instagram/Facebook en iPhone) y esto no está
      // protegido, ni siquiera llega a decidir "booking" vs "admin": se
      // cae acá mismo, antes de pintar nada — la pantalla en blanco.
      let paymentOrigin = null;
      let adminAuthGuardado = false;
      try {
        paymentOrigin = localStorage.getItem('paymentOrigin');
        adminAuthGuardado = localStorage.getItem('admin-auth') === 'true';
      } catch (e) { /* navegador restringido — seguimos con los valores por defecto (booking normal) */ }
      const paymentStatus = new URLSearchParams(window.location.search).get('payment_status');

      if (hash === '#admin-secreto-2024') {
        setView('admin');
        if (adminAuthGuardado) {
          setIsAdminAuth(true);
        }
      } else if (paymentStatus && paymentOrigin === 'admin') {
        setView('admin');
        setIsAdminAuth(true);
      } else {
        setView('booking');
      }
    };

    handleRouting();
    window.addEventListener('hashchange', handleRouting);
    return () => window.removeEventListener('hashchange', handleRouting);
  }, []);


  // Agregamos dependencias para que el email tenga datos frescos
  useEffect(() => {
    if (isAdminAuth && locations.length > 0 && !selectedAdminLocationId) {
      setSelectedAdminLocationId(locations[0].id);
    }
  }, [isAdminAuth, locations, selectedAdminLocationId]);

  // 🔧 Se agregó "locations" a las dependencias — antes, si este efecto
  // se disparaba ANTES de que "locations" terminara de cargar, el chequeo
  // de seguridad de adentro (recién corregido en loadBarbers/loadServices/
  // loadProducts) bloqueaba todo y nunca se reintentaba solo. Ahora, en
  // cuanto "locations" cambia (por ejemplo, termina de cargar), este
  // efecto se vuelve a ejecutar y los datos aparecen sin que haga falta
  // recargar la página a mano.
  useEffect(() => {
    if (isAdminAuth && selectedAdminLocationId) {
      loadBarbers(selectedAdminLocationId);
      loadServices(selectedAdminLocationId);
      loadProducts(selectedAdminLocationId);
      loadSettings(selectedAdminLocationId);
      loadCategories(selectedAdminLocationId);
    }
  }, [isAdminAuth, selectedAdminLocationId, locations]);

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);







const removeServiceFromBooking = async (booking, serviceIndex) => {
  if (booking.services.length <= 1) { showAlert("Error", "No puedes eliminar el último servicio. Mejor cancela la reserva."); return; }
  if (!window.confirm("¿Seguro que quieres quitar este servicio?")) return;
  setLoading(true);
  try {
    const q = query(collection(db, "bookings"), where("bookingGroupId", "==", booking.bookingGroupId));
    const querySnapshot = await getDocs(q);
    const allSlots = [];
    querySnapshot.forEach(doc => allSlots.push({ id: doc.id, ...doc.data() }));

    // 🛡️ FIX: sort defensivo — algún slot puede no traer "time" (por
    // eso crasheaba con "Cannot read properties of undefined") — y el
    // slot primario se identifica por el flag "isPrimary", no por el
    // orden cronológico (más confiable si el orden por horario falla).
    allSlots.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    const primarySlot = allSlots.find(s => s.isPrimary === true) || allSlots[0];

    const serviceToRemove = booking.services[serviceIndex];
    const newPrice = Number(primarySlot.totalPrice) - Number(serviceToRemove.price);
    const newServicesList = primarySlot.services.filter((_, index) => index !== serviceIndex);
    // 🔧 A pedido: la duración real (totalDuration) ya NO se redondea a
    // múltiplos de 30 — solo la cantidad de SLOTS a bloquear en la
    // agenda se calcula redondeando hacia arriba, para que la reserva
    // pueda durar, por ejemplo, 50 min reales, sin inflarla a 60.
    const newDuration = newServicesList.reduce((sum, s) => sum + Number(s.duration || 0), 0);
    const slotsToKeepCount = Math.ceil(newDuration / SLOT_DURATION);

    const batch = writeBatch(db);
    const primaryRef = doc(db, "bookings", primarySlot.id);
    batch.update(primaryRef, { services: newServicesList, totalDuration: newDuration, totalPrice: newPrice });

    // Recorremos solo los slots NO primarios para decidir cuáles borrar
    // (el primario ya se actualiza arriba, nunca se borra).
    const otherSlots = allSlots.filter(s => s.id !== primarySlot.id);
    otherSlots.forEach((slot, index) => {
      if (index >= (slotsToKeepCount - 1)) {
        batch.delete(doc(db, "bookings", slot.id));
      }
    });

    await batch.commit();
    showAlert("Actualizado", `Se eliminó ${serviceToRemove.name}. Tiempo liberado.`);
    // 🔧 FIX: mismo criterio que addServiceToBooking — antes cerraba el
    // modal apenas se sacaba UN servicio, obligando a reabrirlo para
    // seguir editando. Ahora se queda abierto, ya refrescado.
    setBookingToEdit(prev => prev ? { ...prev, services: newServicesList, totalDuration: newDuration, totalPrice: newPrice } : prev);
    await loadBookings();
  } catch (error) {
    console.error("Error eliminando servicio:", error);
    showAlert("Error", "No se pudo eliminar el servicio.");
  } finally {
    setLoading(false);
  }
};

// 🩹 RECALCULAR DURACIÓN — herramienta de auto-reparación para turnos
// que quedaron con menos horarios bloqueados de los que en realidad
// necesitan (por ejemplo: se creó mientras un barbero tenía una
// duración personalizada cargada mal para ese servicio, y ya se
// corrigió el valor, pero el turno viejo quedó con el bloqueo corto).
// Recalcula la duración real de HOY (con los datos actuales de
// servicios/barbero), y si hace falta, reserva los slots que faltan —
// misma lógica que addServiceToBooking al agregar tiempo extra, pero
// sin necesitar agregar ningún servicio nuevo.
// =====================================================================
// 📅 REAGENDAR — mueve un turno COMPLETO (todos sus slots) a otra
// fecha/hora, con el MISMO profesional. calcularHorariosReagendar arma
// la lista de horarios donde el turno entero entra libre; consulta
// Firestore directo para esa fecha (no depende de qué reservas estén
// cargadas en memoria) e ignora los slots del propio turno, así se puede
// correr dentro del mismo día.
// =====================================================================
const calcularHorariosReagendar = async (booking, dateStr) => {
  const barberId = String(booking.barber?.id || '').trim();
  const barber = barbers.find(b => String(b.id).trim() === barberId) || booking.barber || {};
  const dia = parse(dateStr, 'yyyy-MM-dd', new Date()).getDay();

  if (Array.isArray(shopSettings?.openDays) && !shopSettings.openDays.includes(dia)) {
    return { slots: [], motivo: 'El local está cerrado ese día.' };
  }
  if (Array.isArray(barber.workingDays) && barber.workingDays.length > 0 && !barber.workingDays.includes(dia)) {
    return { slots: [], motivo: `${barber.name || 'El profesional'} no trabaja ese día.` };
  }

  const aMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + m; };
  const aHora = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  const { openTime, closeTime, lunchStart, lunchEnd } = getScheduleForDate(dateStr);
  const apertura = aMin(openTime || '09:00');
  const cierre = aMin(closeTime || '19:00');

  // Horario propio del barbero (mismo criterio que ClientView: el horario
  // del día en workSchedule, si tiene horarios variables, manda)
  let bStart = barber.workStartTime || '';
  let bEnd = barber.workEndTime || '';
  if (barber.hasVariableHours && barber.workSchedule) {
    const h = barber.workSchedule[dia] || barber.workSchedule[String(dia)];
    if (h && (h.start || h.end)) { bStart = h.start || bStart; bEnd = h.end || bEnd; }
  }
  const desde = Math.max(apertura, bStart ? aMin(bStart) : 0);
  const hasta = Math.min(cierre, bEnd ? aMin(bEnd) : 24 * 60);

  const enAlmuerzoLocal = (m) => shopSettings?.hasBreak && lunchStart && lunchEnd && m >= aMin(lunchStart) && m < aMin(lunchEnd);
  const enAlmuerzoBarbero = (m) => barber.lunchStart && barber.lunchEnd && m < aMin(barber.lunchEnd) && m + SLOT_DURATION > aMin(barber.lunchStart);

  const locId = String(booking.locationId || '').trim();
  const groupId = booking.bookingGroupId ? String(booking.bookingGroupId) : null;
  const [blocksSnap, bookingsSnap] = await Promise.all([
    getDocs(query(collection(db, 'blocked_times'), where('date', '==', dateStr), where('locationId', '==', locId))),
    getDocs(query(collection(db, 'bookings'), where('date', '==', dateStr), where('locationId', '==', locId))),
  ]);

  const bloqueos = blocksSnap.docs.map(d => d.data())
    .filter(bl => !bl.deleted)
    .filter(bl => !bl.barberId || String(bl.barberId).trim() === barberId);
  const ocupados = new Set(bookingsSnap.docs
    .filter(d => {
      const b = d.data();
      if (d.id === booking.id || (groupId && String(b.bookingGroupId || '') === groupId)) return false; // el propio turno
      if (b.status === 'cancelled' || b.status === 'no_show' || b.isManualEntry) return false;
      return String(b.barber?.id || '').trim() === barberId;
    })
    .map(d => d.data().time || d.data().startTime));

  const slotsNeeded = Math.max(1, Math.ceil((Number(booking.totalDuration) || SLOT_DURATION) / SLOT_DURATION));

  // Grilla del día (salta el receso del local, igual que getBookingSlots)
  const grilla = [];
  for (let m = apertura; m < cierre; m += SLOT_DURATION) if (!enAlmuerzoLocal(m)) grilla.push(m);

  const ahora = new Date();
  const esHoy = dateStr === format(ahora, 'yyyy-MM-dd');
  const minAhora = ahora.getHours() * 60 + ahora.getMinutes();

  const slots = [];
  grilla.forEach((m, idx) => {
    if (m < desde || (esHoy && m <= minAhora)) return;
    const tramo = grilla.slice(idx, idx + slotsNeeded);
    if (tramo.length < slotsNeeded) return;
    const libre = tramo.every(t => {
      const hora = aHora(t);
      return t + SLOT_DURATION <= hasta &&
        !enAlmuerzoBarbero(t) &&
        !ocupados.has(hora) &&
        !bloqueos.some(bl => hora >= bl.startTime && hora < bl.endTime);
    });
    if (libre) slots.push({ time: aHora(m), slots: tramo.map(aHora) });
  });

  return { slots, motivo: slots.length ? null : 'No hay horarios libres ese día.' };
};

const reagendarReserva = async (booking, dateStr, opcion) => {
  try {
    // Re-chequeo justo antes de guardar, por si alguien tomó el horario recién
    const { slots } = await calcularHorariosReagendar(booking, dateStr);
    const elegido = slots.find(s => s.time === opcion.time);
    if (!elegido) {
      addToast('Horario ocupado', 'Ese horario se acaba de ocupar. Elegí otro.', 'error');
      return false;
    }

    let docs = [];
    if (booking.bookingGroupId) {
      const snap = await getDocs(query(collection(db, 'bookings'), where('bookingGroupId', '==', booking.bookingGroupId)));
      docs = snap.docs;
    }
    if (docs.length === 0) {
      const unico = await getDoc(doc(db, 'bookings', String(booking.id)));
      if (unico.exists()) docs = [unico];
    }
    if (docs.length === 0) throw new Error('No se encontró la reserva.');

    const primary = docs.find(d => d.data().isPrimary === true) || docs.find(d => d.id === String(booking.id)) || docs[0];
    const pdata = primary.data();
    const nuevoInicio = elegido.slots[0];
    const cambios = {
      date: dateStr,
      startTime: nuevoInicio,
      reminderSent: false, // que el recordatorio salga de nuevo para la fecha nueva
      rescheduledFrom: { date: pdata.date || null, time: pdata.startTime || pdata.time || null },
      rescheduledAt: Timestamp.now(),
    };

    const batch = writeBatch(db);
    batch.update(primary.ref, { ...cambios, time: nuevoInicio });
    docs.filter(d => d.id !== primary.id).forEach(d => batch.delete(d.ref));
    elegido.slots.slice(1).forEach(slotTime => {
      batch.set(doc(collection(db, 'bookings')), {
        ...pdata,
        ...cambios,
        isPrimary: false,
        time: slotTime,
        services: [],
        totalPrice: 0,
        createdAt: Timestamp.now(),
      });
    });
    await batch.commit();

    setBookingToEdit(prev => prev ? { ...prev, date: dateStr, time: nuevoInicio, startTime: nuevoInicio } : prev);
    await loadBookings();
    addToast('Reagendado', `Turno movido al ${dateStr.split('-').reverse().join('/')} a las ${nuevoInicio}.`, 'success');
    return true;
  } catch (error) {
    console.error('❌ Error reagendando:', error);
    addToast('Error', 'No se pudo reagendar el turno.', 'error');
    return false;
  }
};

const recalcularDuracionBooking = async (booking) => {
  setLoading(true);
  let datosActualizados = null;
  try {
    const q = query(collection(db, "bookings"), where("bookingGroupId", "==", booking.bookingGroupId));
    const querySnapshot = await getDocs(q);
    const allSlots = [];
    querySnapshot.forEach(d => allSlots.push({ id: d.id, ...d.data() }));
    allSlots.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    const primarySlot = allSlots.find(s => s.isPrimary === true) || allSlots[0];
    if (!primarySlot) throw new Error("No se encontró la reserva.");

    // 🕐 Hora real de inicio — algunos turnos viejos tienen "time" vacío
    // en el documento principal, pero sí conservan "startTime".
    const startTimeReal = primarySlot.startTime || primarySlot.time;
    if (!startTimeReal) throw new Error("Este turno no tiene ninguna hora de inicio guardada — no se puede recalcular.");
    const faltaTime = !primarySlot.time;

    // 🔧 Barbero ACTUAL completo (con sus overrides al día de hoy), no
    // solo el {id, name} chico que trae guardado el turno.
    const barberCompleto = barbers.find(b => String(b.id) === String(primarySlot.barber?.id)) || primarySlot.barber;
    const newDuration = (primarySlot.services || []).reduce((sum, s) => sum + getServiceDuration(s, barberCompleto), 0);
    const newSlotsNeeded = Math.max(1, Math.ceil(newDuration / SLOT_DURATION));

    const { openTime: openDelDia, closeTime: closeDelDia, lunchStart: lunchStartDelDia, lunchEnd: lunchEndDelDia } =
      getScheduleForDate(primarySlot.date);

    const localSlots = [];
    const refDate = new Date();
    let current = parse(openDelDia || "09:00", 'HH:mm', refDate);
    const end = parse(closeDelDia || "21:00", 'HH:mm', refDate);
    let safety = 0;
    while (isBefore(current, end) && safety < 100) {
      localSlots.push(format(current, 'HH:mm'));
      current = addMinutes(current, SLOT_DURATION);
      safety++;
    }

    const startSlotIndex = localSlots.indexOf(startTimeReal);
    if (startSlotIndex === -1) throw new Error("Hora de inicio no válida en el horario actual.");

    const buildSlotsFrom = (startIdx, count) => {
      const result = [];
      let idx = startIdx;
      while (result.length < count) {
        if (idx >= localSlots.length) return null;
        const t = localSlots[idx];
        const enAlmuerzo = shopSettings?.hasBreak && lunchStartDelDia && lunchEndDelDia &&
          t >= lunchStartDelDia && t < lunchEndDelDia;
        if (enAlmuerzo) { idx++; continue; }
        result.push(t);
        idx++;
      }
      return result;
    };

    // 📋 Horarios que ESTE turno DEBERÍA estar ocupando, según su
    // duración real recalculada — no importa cuántos documentos existan
    // hoy, esta es la lista "correcta" contra la que se compara.
    const horariosEsperados = buildSlotsFrom(startSlotIndex, newSlotsNeeded);
    if (!horariosEsperados) throw new Error("No se puede completar: supera el horario de cierre. Revisá manualmente si hay otro turno pisado.");

    // 🔍 FIX CLAVE: en vez de comparar CANTIDADES (cuántos documentos
    // hay vs. cuántos deberían haber — eso fallaba si el turno original
    // nunca llegó a crear todos los bloques que le correspondían, sin
    // importar cuál fuera el motivo), ahora se compara horario por
    // horario: de la lista de horarios esperados, ¿cuáles NO tienen
    // todavía un documento propio de ESTE turno bloqueándolos?
    const horariosYaBloqueados = new Set(allSlots.map(s => s.time).filter(Boolean));
    const horariosFaltantes = horariosEsperados.filter(t => !horariosYaBloqueados.has(t));

    if (horariosFaltantes.length === 0 && !faltaTime) {
      addToast("Sin cambios", "Este turno ya está correctamente bloqueado.", "info");
      setLoading(false);
      return;
    }

    // Chequeo de conflicto solo contra los horarios que de verdad faltan.
    // 🔧 En vez de fallar TODO si hay un choque, ahora bloquea lo que
    // SÍ se puede y deja afuera (con aviso) los horarios que chocan con
    // otro turno ya existente — así, si el dueño decide "dejar como
    // está" ese otro turno, al menos el resto del tiempo de ESTE turno
    // queda protegido, en vez de quedar todo sin bloquear por un solo
    // choque puntual.
    let horariosConChoque = [];
    let detalleChoques = [];
    if (horariosFaltantes.length > 0) {
      const bookingsEnConflicto = bookings.filter(b =>
        b.locationId === primarySlot.locationId &&
        b.date === primarySlot.date &&
        b.barber?.id === primarySlot.barber?.id &&
        b.status !== 'cancelled' &&
        b.bookingGroupId !== primarySlot.bookingGroupId &&
        horariosFaltantes.includes(b.time)
      );
      horariosConChoque = bookingsEnConflicto.map(b => b.time);
      detalleChoques = bookingsEnConflicto.map(b => `${b.time} (${b.client?.name || 'sin nombre'}, estado: ${b.status})`);
    }

    const horariosABloquear = horariosFaltantes.filter(t => !horariosConChoque.includes(t));

    if (horariosABloquear.length === 0 && !faltaTime) {
      throw new Error(`No se pudo bloquear nada — todos los horarios que faltaban chocan con otro/s turno/s: ${detalleChoques.join(' · ')}. Hay que resolverlo manualmente.`);
    }

    const batch = writeBatch(db);
    const primaryRef = doc(db, "bookings", primarySlot.id);
    batch.update(primaryRef, faltaTime ? { totalDuration: newDuration, time: startTimeReal } : { totalDuration: newDuration });
    horariosABloquear.forEach((slotTime) => {
      const newSlotRef = doc(collection(db, "bookings"));
      batch.set(newSlotRef, {
        ...primarySlot,
        isPrimary: false,
        time: slotTime,
        services: [],
        totalPrice: 0,
        createdAt: Timestamp.now(),
      });
    });
    await batch.commit();

    datosActualizados = { totalDuration: newDuration };
    const mensajePartes = [];
    if (horariosABloquear.length > 0) mensajePartes.push(`se bloquearon ${horariosABloquear.length} horario(s) (${horariosABloquear.join(', ')})`);
    if (faltaTime) mensajePartes.push('se corrigió la hora de inicio');
    let mensajeFinal = mensajePartes.join(' y ') + '.';
    if (detalleChoques.length > 0) {
      mensajeFinal += ` ⚠️ No se pudo bloquear: ${detalleChoques.join(' · ')} — queda pisado por ese otro turno, revisalo manualmente si hace falta.`;
    }
    showAlert("¡Listo!", mensajeFinal);
    setBookingToEdit(prev => prev ? { ...prev, ...datosActualizados } : prev);
    await loadBookings();
  } catch (error) {
    console.error("Error recalculando duración:", error);
    showAlert("No se pudo recalcular", error.message || "Error desconocido.");
  } finally {
    setLoading(false);
  }
};

const addServiceToBooking = async (booking, newService) => {
  setLoading(true);
  let datosActualizados = null; // 🔧 se llena adentro de la transacción, para poder refrescar el modal después sin cerrarlo
  try {
    await runTransaction(db, async (transaction) => {
      const bookingRef = doc(db, "bookings", booking.id);
      const bookingSnap = await transaction.get(bookingRef);
      if (!bookingSnap.exists()) throw new Error("La reserva ya no existe.");
      const currentData = bookingSnap.data();
      const currentDuration = Number(currentData.totalDuration);
      // 🔧 FIX: se recalcula sumando TODOS los servicios (los que ya
      // estaban + el nuevo) desde sus duraciones crudas y se redondea
      // a slots — antes se sumaba la duración cruda del nuevo servicio
      // sobre un totalDuration que podía no coincidir con la suma real,
      // arrastrando el desfasaje.
      const newDuration = [...(currentData.services || []), newService].reduce((sum, s) => sum + Number(s.duration || 0), 0);
      const currentSlots = Math.ceil(currentDuration / SLOT_DURATION);
      const newSlotsNeeded = Math.ceil(newDuration / SLOT_DURATION);
      const extraSlots = newSlotsNeeded - currentSlots;

      if (extraSlots > 0) {
        // 🔧 FIX: horario específico de ESTE día de la reserva (respeta
        // horario variable), en vez del horario global de la barbería.
        const { openTime: openDelDia, closeTime: closeDelDia, lunchStart: lunchStartDelDia, lunchEnd: lunchEndDelDia } =
          getScheduleForDate(currentData.date);

        const localSlots = [];
        const refDate = new Date();
        let current = parse(openDelDia || "09:00", 'HH:mm', refDate);
        const end = parse(closeDelDia || "21:00", 'HH:mm', refDate);
        let safety = 0;
        while (isBefore(current, end) && safety < 100) {
          localSlots.push(format(current, 'HH:mm'));
          current = addMinutes(current, SLOT_DURATION);
          safety++;
        }

        const startSlotIndex = localSlots.indexOf(currentData.startTime);
        if (startSlotIndex === -1) throw new Error("Error: Hora de inicio no válida en el horario actual.");

        // 🔧 FIX: avanza saltando el horario de almuerzo (si aplica),
        // en vez de tomar N slots consecutivos sin chequear nada — antes
        // esto podía "reservar" un horario que en realidad está cerrado
        // por receso, desalineando el fin real del turno con lo que
        // después usa isBlockFree y el resto de la agenda.
        const buildSlotsFrom = (startIdx, count) => {
          const result = [];
          let idx = startIdx;
          while (result.length < count) {
            if (idx >= localSlots.length) return null;
            const t = localSlots[idx];
            const enAlmuerzo = shopSettings?.hasBreak && lunchStartDelDia && lunchEndDelDia &&
              t >= lunchStartDelDia && t < lunchEndDelDia;
            if (enAlmuerzo) { idx++; continue; }
            result.push(t);
            idx++;
          }
          return result;
        };

        // Reconstruye los slots que YA ocupa la reserva (mismo
        // criterio), para saber desde dónde seguir agregando.
        const currentSlotsList = buildSlotsFrom(startSlotIndex, currentSlots);
        if (!currentSlotsList) throw new Error("No se pudo determinar los horarios actuales de la reserva.");
        const lastIndex = localSlots.indexOf(currentSlotsList[currentSlotsList.length - 1]);

        const slotsToBook = buildSlotsFrom(lastIndex + 1, extraSlots);
        if (!slotsToBook) {
          throw new Error("No se puede agregar: Supera el horario de cierre.");
        }

        const hasConflict = bookings.some(b =>
          b.locationId === currentData.locationId &&
          b.date === currentData.date &&
          b.barber?.id === currentData.barber?.id &&
          b.status !== 'cancelled' &&
          slotsToBook.includes(b.time)
        );
        if (hasConflict) {
          throw new Error(`Sin espacio: El horario ${slotsToBook[0]} ya está ocupado.`);
        }

        slotsToBook.forEach((slotTime) => {
          const newSlotRef = doc(collection(db, "bookings"));
          transaction.set(newSlotRef, {
            ...currentData,
            isPrimary: false,
            time: slotTime,
            services: [],
            totalPrice: 0,
            createdAt: Timestamp.now(),
            bookingGroupId: currentData.bookingGroupId
          });
        });
      }

      const updatedServices = [...(currentData.services || []), newService];
      const updatedPrice = Number(currentData.totalPrice) + Number(newService.price);
      transaction.update(bookingRef, { services: updatedServices, totalDuration: newDuration, totalPrice: updatedPrice });
      datosActualizados = { services: updatedServices, totalDuration: newDuration, totalPrice: updatedPrice };
    });
    showAlert("¡Éxito!", `Se agregó ${newService.name}.`);
    // 🔧 FIX: antes esto cerraba el modal (setBookingToEdit(null)) apenas
    // se agregaba UN servicio — si el admin quería sumar un SEGUNDO
    // servicio, tenía que volver a abrir "Gestionar Reserva" desde cero
    // cada vez, lo que parecía "no estar actualizando". Ahora el modal
    // se queda abierto, refrescado con los datos nuevos (servicios,
    // duración y precio ya actualizados), para poder seguir agregando
    // de corrido.
    if (datosActualizados) {
      setBookingToEdit(prev => prev ? { ...prev, ...datosActualizados } : prev);
    }
    await loadBookings();
  } catch (error) {
    console.error("Error agregando servicio:", error);
    showAlert("No se pudo agregar", error.message || "Error desconocido.");
  } finally {
    setLoading(false);
  }
};

  const handleStartJob = async (bookingGroupId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "bookings"), where("bookingGroupId", "==", bookingGroupId));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          status: 'in_progress',
          startedAt: Timestamp.now()
        });
      });
      await batch.commit();
      loadBookings();
    } catch (error) {
      console.error("Error al iniciar trabajo:", error);
      showAlert("Error", "No se pudo iniciar el trabajo.");
    }
    setLoading(false);
  };

  const handleEndJob = async (bookingGroupId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "bookings"), where("bookingGroupId", "==", bookingGroupId));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);

      let firstBooking = null;

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (!firstBooking || data.isPrimary === true) {
          firstBooking = { id: docSnap.id, ...data };
        }
        batch.update(docSnap.ref, { status: 'completed' });
      });

      await batch.commit();

      if (firstBooking) {
        await enviarWhatsAppAutomatico({
          client: firstBooking.client,
          date: firstBooking.date,
          startTime: firstBooking.startTime || firstBooking.time,
          locationId: firstBooking.locationId,
          companyId: firstBooking.companyId || currentUser?.companyId || null,
          barber: firstBooking.barber,
          services: firstBooking.services,
          totalPrice: firstBooking.totalPrice,
          bookingGroupId: firstBooking.bookingGroupId,
          id: firstBooking.id,
          status: 'completed'
        });
      }

      loadBookings();
    } catch (error) {
      console.error("Error al finalizar trabajo:", error);
      showAlert("Error", "No se pudo finalizar el trabajo.");
    }
    setLoading(false);
  };


  const renderAdminPanel = () => {
    // 1. PANTALLA DE CARGA (Corregida)
    if (isPlanLoading) {
      return (
        <div className="h-[100dvh] w-full flex items-center justify-center" style={{ background: isDarkMode ? '#1C1B19' : '#EDEFF1' }}>
          <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', borderTopColor: '#d4af37' }} />
        </div>
      );
    }

    // 🚨 2. SECUESTRO DE VISTA PARA BARBEROS (NUEVO) 🚨
    if (currentUser?.role === 'barber') {
      return (
        <BarberPortal
          bookings={bookings}
          historialListo={historialListo}
          currentUser={currentUser}
          onStatusChange={updateBookingStatus}
          currentPlan={currentPlan}
          sendWhatsApp={sendWhatsApp}
          isDarkMode={isDarkMode}
          toggleTheme={() => setIsDarkMode(!isDarkMode)}
          barbers={barbers}
          products={products}
          services={services}
          onAddManualService={handleAddManualService}
          onLogout={handleClientLogout}
          addToast={addToast}
          uploadGalleryImage={uploadGalleryImage}
          enabledFeatures={enabledFeatures}
          checkMembershipCoverage={checkMembershipCoverage}
          membershipsEnabled={!!shopSettings?.enableMemberships}
        />
      );
    }



    // 3. VISTA DE ADMINISTRADOR / DUEÑO (Tu código original empieza aquí)
    const isPremium = currentPlan?.level === 'premium' || currentPlan?.level === 'empresarial';
    const esEmpresarial = currentPlan?.level === 'empresarial';

    const handleViewChange = (viewId) => {
      setAdminSubView(viewId);
      if (viewId === 'bookings') setNotificationCount(0);
    };

    // 📅 Ahora abre el modal nuevo, propio del Calendario — ya NO toca
    // nada de ClientView.jsx (ni selectedBarber, ni step, ni
    // adminSubView='booking_wizard' como hacía antes).
    const handleNewBookingFromCalendar = ({ barber, time, date }) => {
      // 🔧 FIX: isBlockFree (usada por el modal nuevo para chequear
      // horarios ya ocupados) compara contra selectedLocation.id — sin
      // esta línea, esa variable quedaba desincronizada de la sucursal
      // del Calendario, y el chequeo de sucursal nunca coincidía. Por
      // eso "ya ocupado" no detectaba nada, aunque el campo startTime
      // ya estuviera bien corregido.
      const adminLocation = locations.find(l => l.id == selectedAdminLocationId);
      if (adminLocation) setSelectedLocation(adminLocation);
      else setSelectedLocation(locations[0]);

      setQuickBookingPrefill({ barber: barber || null, time: time || null, date: date ? format(date, 'yyyy-MM-dd') : null });
      setShowQuickBookingModal(true);
    };

    // =====================================================================
    // 💾 GUARDAR TURNO — desde el modal nuevo del Calendario. Autónomo,
    // no reutiliza confirmBooking (esa función está pensada para el
    // flujo de ClientView, con socios/membresías/pago online que acá no
    // aplican). Si la fecha/hora elegida ya pasó, nace directo como
    // "completado" (cuenta para Comisiones sin pasos extra) — mismo
    // criterio que ya usábamos antes. El envío de WhatsApp lo decide el
    // checkbox del modal, explícito, sin ninguna regla escondida.
    // =====================================================================
    const handleSaveQuickBooking = async ({ barber, services: serviciosElegidos, date, time, client, totalPriceOverride, enviarWhatsApp, turnoYaHecho, membershipId, membershipPlanName, forzado }) => {
      if (!barber || !serviciosElegidos?.length || !date || !time || !client?.name) {
        addToast("Faltan datos", "Completá barbero, servicios, fecha, hora y nombre del cliente.", "error");
        return false;
      }
      setLoading(true);
      try {
        const totalDuration = serviciosElegidos.reduce((sum, s) => sum + getServiceDuration(s, barber), 0);
        const totalPriceCalculado = serviciosElegidos.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
        const totalPriceFinal = (totalPriceOverride !== '' && totalPriceOverride !== null && totalPriceOverride !== undefined)
          ? Number(totalPriceOverride)
          : totalPriceCalculado;

        // 🎨 Antes esto se decidía solo, mirando si la fecha/hora ya
        // había pasado. Ahora lo elige el admin directo en el modal
        // ("¿Ya se hizo el turno?") — más simple y sin sorpresas: si
        // no se tildó, queda "confirmado" (azul); si se tildó, queda
        // "completado" (verde).
        const status = turnoYaHecho ? 'completed' : 'confirmed';

        const bookingGroupId = `manual-${Date.now()}`;
        const adminLocation = locations.find(l => l.id == selectedAdminLocationId) || locations[0];
        const targetCompanyId = adminLocation?.companyId || currentUser?.companyId || null;

        const nuevaReserva = {
          locationId: selectedAdminLocationId,
          companyId: targetCompanyId,
          client: { name: client.name, phone: client.phone || '' },
          barber: { id: barber.id, name: barber.name },
          services: serviciosElegidos,
          date,
          startTime: time,
          totalDuration,
          totalPrice: totalPriceFinal,
          status,
          isPrimary: true,
          bookingGroupId,
          isReviewed: true,
          ratingTemplateSent: true,
          reminderSent: true,
          createdAt: Timestamp.now(),
          // 🔔 Sin esto, el aviso de "¡Nueva Reserva!" (pensado para
          // avisarle a OTROS del equipo sobre un turno que no crearon
          // ellos) te saltaba a vos mismo por tus propios turnos
          // cargados acá — ese aviso filtra por userId != el tuyo.
          userId: currentUser?.uid || null,
          // 📲 Se guarda la elección del checkbox EN el turno — así,
          // más adelante (por ejemplo al eliminarlo), otras partes del
          // sistema saben que este cliente puntual no debe recibir
          // WhatsApp de este turno, sin importar qué acción se haga.
          sinWhatsApp: !enviarWhatsApp,
          // 🎫 Si el modal detectó que este cliente es socio, se guarda
          // el vínculo — mismo campo que usa el resto de la app para
          // saber que este turno pertenece a un plan de membresía. El
          // nombre del plan se guarda APARTE (no solo el ID) para que
          // BarberPortal/BarberMobileView lo puedan mostrar directo,
          // sin tener que consultar el plan de nuevo cada vez.
          ...(membershipId ? { membershipId, membershipPlanName: membershipPlanName || null } : {}),
        };

        // 🔧 FIX: antes se creaba UN SOLO documento en Firestore sin
        // importar cuántos minutos durara el turno — el calendario solo
        // bloqueaba el primer slot de 30 min, y el resto del tiempo
        // quedaba "libre" para el sistema (podía pisarse con otra
        // reserva). Ahora se reserva un doc POR CADA slot de 30 min que
        // realmente ocupa (mismo patrón que usa el flujo del cliente en
        // confirmBooking): el primero lleva todos los datos (servicios,
        // precio, cliente), los siguientes son placeholders vacíos que
        // solo existen para marcar esos horarios como ocupados.
        const slotsNeededQuick = Math.max(1, Math.ceil(totalDuration / SLOT_DURATION));
        // 🔧 FIX: getBookingSlots devuelve null para horarios fuera del
        // rango normal del local (ej: "21:00" con el local cerrando a
        // las 20:00) — antes, el respaldo "|| [time]" solo generaba UN
        // bloque de 30 min sin importar cuántos necesitaba el turno
        // real, así que una reserva de varios servicios fuera de hora
        // quedaba con la mitad del tiempo sin bloquear en el calendario.
        // Ahora, si getBookingSlots falla, se arman los slots
        // consecutivos a mano (mismo criterio que isBlockFreeForzado),
        // sin depender de que existan dentro de timeSlots.
        const generarSlotsAMano = (horaInicio, cantidad) => {
          const resultado = [];
          let [h, m] = horaInicio.split(':').map(Number);
          for (let i = 0; i < cantidad; i++) {
            resultado.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
            m += SLOT_DURATION;
            if (m >= 60) { h += 1; m -= 60; }
          }
          return resultado;
        };
        const slotsToBookQuick = getBookingSlots(time, slotsNeededQuick) || generarSlotsAMano(time, slotsNeededQuick);

        // 🔒 A pedido (revisión de doble reserva): re-chequeo justo antes de
        // guardar — la lista de horarios libres sale de datos que pudieron
        // quedar viejos (otro admin/cliente pudo tomar ese horario en el medio).
        // Si el admin eligió "forzar" a propósito (ya lo confirmó en pantalla),
        // se respeta; si no, no se guarda encima de otro turno.
        if (!forzado) {
          const snapQuick = await getDocs(query(
            collection(db, 'bookings'),
            where('date', '==', date),
            where('locationId', '==', String(selectedAdminLocationId))
          ));
          const ocupadoQuick = snapQuick.docs.some(d => {
            const b = d.data();
            if (b.status === 'cancelled' || b.status === 'no_show' || b.isManualEntry) return false;
            if (String(b.barber?.id || '').trim() !== String(barber.id).trim()) return false;
            return slotsToBookQuick.includes(b.time || b.startTime);
          });
          if (ocupadoQuick) {
            addToast("Horario ocupado", "Ese horario ya tiene otro turno (se acaba de tomar). Elegí otro horario.", "error");
            return false;
          }
        }

        const batchQuick = writeBatch(db);
        let firstDocRef = null;
        slotsToBookQuick.forEach((slotTime, i) => {
          const ref = doc(collection(db, 'bookings'));
          if (i === 0) firstDocRef = ref;
          batchQuick.set(ref, {
            ...nuevaReserva,
            time: slotTime,
            isPrimary: i === 0,
            services: i === 0 ? serviciosElegidos : [],
            totalDuration: i === 0 ? totalDuration : 0,
            totalPrice: i === 0 ? totalPriceFinal : 0,
            client: i === 0 ? nuevaReserva.client : { name: nuevaReserva.client.name },
          });
        });
        await batchQuick.commit();
        const docRef = firstDocRef;

        // 🎫 Si nace DIRECTO como completado (turnoYaHecho) y es de un
        // socio, hay que descontarle la visita ahora mismo — el
        // descuento automático de siempre vive en updateBookingStatus,
        // que este turno nunca pasa por acá (se crea directo con
        // addDoc, no a través de esa función). Mismo seguro
        // anti-duplicado (membershipVisitDeducted) que usa el resto de
        // la app.
        if (status === 'completed' && membershipId) {
          try {
            await incrementarVisitaMembresia(selectedAdminLocationId, membershipId, serviciosElegidos, docRef.id);
            await updateDoc(docRef, { membershipVisitDeducted: true });
          } catch (eMembresia) {
            console.error('Error descontando visita de membresía:', eMembresia);
          }
        }

        // 🔕 A pedido: el modal rápido del calendario (QuickBookingModal)
        // ya NUNCA manda WhatsApp, para ninguna barbería — antes esto
        // dependía del checkbox del modal (enviarWhatsApp), que a su vez
        // dependía de que el flag "pruebaFlujoWhatsapp" estuviera bien
        // detectado para ocultarlo — cadena que falló al menos una vez
        // en la práctica (Capelli lo vio mandar igual). Ahora es fijo:
        // este flujo nunca dispara ningún mensaje, sin depender de
        // ningún flag ni checkbox.

        addToast("Turno guardado", `${client.name} — ${format(new Date(date + 'T00:00:00'), 'dd/MM/yyyy')} ${time}`, "success");
        setShowQuickBookingModal(false);
        setQuickBookingPrefill(null);
        return true;
      } catch (error) {
        console.error("Error guardando turno rápido:", error);
        addToast("Error", "No se pudo guardar el turno.", "error");
        return false;
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        <ModernAdminLayout
          activeView={adminSubView}
          setActiveView={handleViewChange}
          onLogout={handleAdminLogout}
          userEmail={adminEmail}
          locations={locations}
          shopSettings={shopSettings}
          notificationCount={notificationCount}
          selectedLocationId={selectedAdminLocationId}
          setSelectedLocationId={setSelectedAdminLocationId}
          currentPlan={currentPlan}
          currentUser={currentUser}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          exchangeRates={exchangeRates}
          enabledFeatures={enabledFeatures}
        >
          {adminSubView === 'dashboard' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <AdminDashboard
              bookings={bookings}
              barbers={barbers}
              services={services}
              products={products}
              shopSettings={shopSettings}
              reportPeriod={reportPeriod}
              setReportPeriod={setReportPeriod}
              selectedAdminLocationId={selectedAdminLocationId}
              activarNotificaciones={activarNotificaciones}
              onOpenCashModal={() => setShowCashModal(true)}
              onNavigate={handleViewChange}
              isDarkMode={isDarkMode}
            />
          )}

          {adminSubView === 'settings' && (
            <SettingsView
              shopSettings={shopSettings}
              setShopSettings={setShopSettings}
              saveSettings={saveSettings}
              loading={loading}
              currentPlan={currentPlan}
              fileToUpload={fileToUpload}
              setFileToUpload={setFileToUpload}
              isDarkMode={isDarkMode}
              bancardKeys={bancardKeys}
              setBancardKeys={setBancardKeys}
              bannerToUpload={bannerToUpload}
              setBannerToUpload={setBannerToUpload}
              enabledFeatures={enabledFeatures}
              barbers={barbers}
            />
          )}

          {adminSubView === 'services' && (
            <ServicesManagement
              services={services}
              setServices={setServices}
              onReorderFull={(nuevaLista) => reorderServicesFull(selectedAdminLocationId, nuevaLista)}
              categories={categories}
              showServiceModal={showServiceModal}
              setShowServiceModal={setShowServiceModal}
              currentService={currentService}
              setCurrentService={setCurrentService}

              // 👇 ESTA ES LA FUNCIÓN ACTUALIZADA 👇
              saveService={async () => {
                const exito = await handleSaveService(selectedAdminLocationId, currentService, fileToUpload);
                if (exito) {
                  setShowServiceModal(false); // Cierra el modal
                  setFileToUpload(null);      // Limpia la foto
                  setCurrentService({ id: null, name: '', duration: '', price: '', category: 'General' }); // Limpia el formulario
                }
              }}
              // 👆 ------------------------------- 👆

              deleteService={(id) => handleDeleteService(selectedAdminLocationId, id)}
              loading={loading}
              setFileToUpload={setFileToUpload}
              setShowCategoryModal={setShowCategoryModal}
              onNavigate={handleViewChange}
              shopSettings={shopSettings}
              enabledFeatures={enabledFeatures}
              onSaveDomicilio={saveDomicilio}
              isDarkMode={isDarkMode}
            />
          )}
          {adminSubView === 'barbers' && (
            <BarbersManagement
              barbers={barbers}
              shopSettings={shopSettings}
              services={services}
              setBarbers={setBarbers}
              currentPlan={currentPlan}
              categories={categories}
              showBarberModal={showBarberModal}
              setShowBarberModal={setShowBarberModal}
              currentBarber={currentBarber}
              setCurrentBarber={setCurrentBarber}
              setFileToUpload={setFileToUpload}

              // 👇 ESTA ES LA FUNCIÓN ACTUALIZADA 👇
              saveBarber={async () => {
                const exito = await handleSaveBarber(selectedAdminLocationId, currentBarber, fileToUpload, barbers, categories);
                if (exito) {
                  setShowBarberModal(false); // Cierra el modal de barberos
                  setFileToUpload(null);     // Limpia la foto subida
                  setCurrentBarber({ id: null, docId: null, name: '', specialty: '', image: '' }); // Limpia el formulario

                }
              }}
              // 👆 ------------------------------- 👆

              deleteBarber={(id) => handleDeleteBarber(selectedAdminLocationId, id)}
              loading={loading}
              addToast={addToast}
              isDarkMode={isDarkMode}
              onNavigate={(dest) => setAdminSubView(dest)}
              locationId={selectedAdminLocationId}
            />
          )}

          {adminSubView === 'locations_mgmt' && (
            <LocationsManagement
              locations={locations}
              currentPlan={currentPlan}
              // Estados
              selectedAdminLocationId={selectedAdminLocationId}
              showLocationModal={showLocationModal}
              newLocationInfo={newLocationInfo}
              editingLocationId={editingLocationId}
              fileToUpload={fileToUpload}
              loading={loading}
              isDarkMode={isDarkMode}
              addToast={addToast}
              enabledFeatures={enabledFeatures}

              // Setters
              setShowLocationModal={setShowLocationModal}
              setEditingLocationId={setEditingLocationId}
              setNewLocationInfo={setNewLocationInfo}
              setFileToUpload={setFileToUpload}
              setSelectedAdminLocationId={setSelectedAdminLocationId}

              // Funciones
              deleteLocation={(id) => handleDeleteLocation(id, selectedAdminLocationId, setSelectedAdminLocationId)}
              saveLocation={handleManualSaveLocation}
            />
          )}

          {adminSubView === 'live_view' && (
            ((esEmpresarial || enabledFeatures?.liveView) && shopSettings?.enableLiveView !== false) ? (
              <LiveView
                bookings={bookings}
                barbers={barbers}
                selectedAdminLocationId={selectedAdminLocationId}
                handleEndJob={handleEndJob}
                loading={loading}
                isDarkMode={isDarkMode}
              />
            ) : (
              <div className="text-center p-10 text-gray-400">Módulo desactivado.</div>
            )
          )}

          {adminSubView === 'mensajes' && (() => {
            const companyIdMensajes = currentUser?.companyId || locations.find(l => l.id === selectedAdminLocationId)?.companyId || null;
            return (
              <MessagesLog
                companyId={companyIdMensajes}
                whatsappServerUrl={WHATSAPP_SERVERS[companyIdMensajes] || WHATSAPP_SERVERS['default']}
                isDarkMode={isDarkMode}
              />
            );
          })()}

          {adminSubView === 'calendar' && (
            cargandoLocal ? (
              // 🛑 CORTINA DE CARGA (Evita el parpadeo mientras Firebase trae los barberos)
              <div className="flex items-center justify-center w-full h-full min-h-[600px]" style={{ background: isDarkMode ? '#1C1B19' : '#EDEFF1' }}>
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin shadow-lg"></div>
                  <p className={`text-xs font-bold uppercase tracking-widest animate-pulse ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                    Sincronizando sucursal...
                  </p>
                </div>
              </div>
            ) : isMobile ? (
              // 📱 MODO MÓVIL (Barbero Solitario - VISTA ADMIN)
              <div className="relative">
                <BarberMobileView
                  bookings={bookings}
                  historialListo={historialListo}
                  barbers={barbers}
                  selectedLocationId={selectedAdminLocationId}
                  onStatusChange={(id, status, options) => updateBookingStatus(id, status, options)}
                  currentPlan={currentPlan}
                  sendWhatsApp={sendWhatsApp}
                  isDarkMode={isDarkMode}
                  userRole="admin"
                  onEdit={setBookingToEdit}
                  onBlockTime={handleBlockTime}
                  addToast={addToast}
                  uploadGalleryImage={uploadGalleryImage}
                  enabledFeatures={enabledFeatures}
                  onFueraDeAgenda={enabledFeatures?.serviciosFueraDeAgendaAdmin ? (fecha, barberoId) => setFueraDeAgendaAdmin({ fecha, barberoId }) : null}
                  fechaInicial={currentDate}
                  onVerCalendario={(fecha) => { setCurrentDate(fecha); setIsMobile(false); }}
                />
              </div>
            ) : (!historialListo && format(currentDate, 'yyyy-MM-dd') < format(subDays(new Date(), 7), 'yyyy-MM-dd')) ? (
              // ⚡ Carga en dos etapas: un día de hace más de 7 días todavía
              // no llegó — no se muestra vacío como si no hubiera tenido turnos.
              <CargandoHistorial isDarkMode={isDarkMode} />
            ) : (
              // 💻 MODO ESCRITORIO (Agenda Completa)
              <CustomAgenda
                bookings={bookings}
                historialListo={historialListo}
                barbers={barbers}
                services={services}
                products={products}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                shopSettings={shopSettings}
                selectedLocationId={selectedAdminLocationId}
                onEditBooking={(booking) => setBookingToEdit(booking)}
                onNewBooking={handleNewBookingFromCalendar}
                onDeleteBooking={deleteBooking}
                updateBookingStatus={updateBookingStatus}
                sendWhatsApp={sendWhatsApp}
                isDarkMode={isDarkMode}
                currentPlan={currentPlan}
                blockedTimes={blockedTimes}
                onBlockTime={handleBlockTime}
                onDeleteBlock={handleDeleteBlock}
                exchangeRates={exchangeRates}
                enabledFeatures={enabledFeatures}
                showQuickBookingModal={showQuickBookingModal}
                setShowQuickBookingModal={setShowQuickBookingModal}
                quickBookingPrefill={quickBookingPrefill}
                onSaveQuickBooking={handleSaveQuickBooking}
                checkMembershipCoverage={checkMembershipCoverage}
                loading={loading}
                timeSlots={timeSlots}
                isBlockFree={isBlockFree}
                timeSlotsExtendido={timeSlotsExtendido}
                isBlockFreeForzado={isBlockFreeForzado}
                getEndTime={getEndTime}
                getAvailableDates={getAvailableDates}
                addToast={addToast}
                uploadGalleryImage={uploadGalleryImage}
                companyId={currentUser?.companyId || bookings?.[0]?.companyId || null}
                onFueraDeAgenda={enabledFeatures?.serviciosFueraDeAgendaAdmin ? (fecha, barberoId) => setFueraDeAgendaAdmin({ fecha, barberoId }) : null}
                onVerLista={() => setIsMobile(true)}
              />
            )
          )}
          {/* 🧑‍🔧 Servicio fuera de agenda — el mismo modal del portal del barbero, con paso para elegir barbero */}
          {fueraDeAgendaAdmin && (
            <Suspense fallback={null}>
              <AddManualServiceModal
                onClose={() => setFueraDeAgendaAdmin(null)}
                services={services}
                isDarkMode={isDarkMode}
                currentUser={currentUser}
                miLocationId={selectedAdminLocationId}
                onAddManualService={handleAddManualService}
                addToast={addToast}
                checkMembershipCoverage={checkMembershipCoverage}
                membershipsEnabled={!!shopSettings?.enableMemberships}
                targetDate={fueraDeAgendaAdmin.fecha}
                companyId={currentUser?.companyId || bookings?.[0]?.companyId || null}
                barberos={barbers || []}
                barberoInicialId={fueraDeAgendaAdmin.barberoId}
              />
            </Suspense>
          )}
          {adminSubView === 'caja' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <CashRegisterView
              bookings={bookings}
              barbers={barbers}
              locationId={selectedAdminLocationId}
              locations={locations}
              isDarkMode={isDarkMode}
              shopSettings={shopSettings}
            />
          )}

          {adminSubView === 'clientes_db' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <ClientsDatabase
              bookings={bookings}
              services={services}
              isDarkMode={isDarkMode}
              companyId={currentUser?.companyId || bookings?.[0]?.companyId || null}
              addToast={addToast}
              uploadGalleryImage={uploadGalleryImage}
              enabledFeatures={enabledFeatures}
            />
          )}

          {adminSubView === 'clientes_perdidos' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <LostClients
              bookings={bookings}
              isDarkMode={isDarkMode}
              companyId={currentUser?.companyId || bookings?.[0]?.companyId || null}
              addToast={addToast}
              uploadGalleryImage={uploadGalleryImage}
              enabledFeatures={enabledFeatures}
            />
          )}

          {adminSubView === 'memberships' && (
            <MembershipsManagement
              locationId={selectedAdminLocationId}
              services={services}
              isDarkMode={isDarkMode}
              addToast={addToast}
            />
          )}

          {adminSubView === 'fidelizacion' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <CustomerLoyalty
              bookings={bookings}
              services={services}
              isDarkMode={isDarkMode}
            />
          )}

          {adminSubView === 'historial_caja' && (
            <CashHistory
              locations={locations}
              onBack={() => setAdminSubView('dashboard')}
              isDarkMode={isDarkMode}
            />
          )}

          {adminSubView === 'productos' && (
            <ProductsManagement
              products={products}
              locationId={selectedAdminLocationId}
              isDarkMode={isDarkMode}
              loading={loading}
              setFileToUpload={setFileToUpload}
              // Las funciones para guardar y borrar en Firebase:
              saveProduct={async (locId, productInfo) => {
                setLoading(true);
                try {
                  let finalImageUrl = productInfo.image;
                  if (fileToUpload) finalImageUrl = await uploadImage(fileToUpload);

                  if (productInfo.id) {
                    await updateDoc(doc(db, 'locations', locId, 'products', productInfo.id), {
                      name: productInfo.name, price: productInfo.price, stock: productInfo.stock, image: finalImageUrl || ''
                    });
                  } else {
                    await addDoc(collection(db, 'locations', locId, 'products'), {
                      name: productInfo.name, price: productInfo.price, stock: productInfo.stock, image: finalImageUrl || '', createdAt: Timestamp.now()
                    });
                  }
                  addToast("Éxito", "Producto guardado correctamente", "success");
                  await loadProducts(locId); // Refresca la lista
                } catch (e) {
                  addToast("Error", "No se guardó el producto", "error");
                }
                setLoading(false);
              }}
              deleteProduct={async (productId) => {
                setLoading(true);
                try {
                  await deleteDoc(doc(db, 'locations', selectedAdminLocationId, 'products', productId));
                  addToast("Eliminado", "Producto eliminado", "success");
                  await loadProducts(selectedAdminLocationId);
                } catch (e) {
                  addToast("Error", "No se eliminó el producto", "error");
                }
                setLoading(false);
              }}
            />
          )}

          {adminSubView === 'comisiones' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <Comisiones
              bookings={bookings}
              barbers={barbers}
              locationId={selectedAdminLocationId}
              locations={locations}
              isDarkMode={isDarkMode}
              shopSettings={shopSettings}
            />
          )}

          {adminSubView === 'resenas' && (
            <BarberReviews
              barbers={barbers}
              locationId={selectedAdminLocationId}
              locations={locations} // 👈 ¡FALTABA ESTA LÍNEA!
              isDarkMode={isDarkMode}
            />
          )}





          {adminSubView === 'booking_wizard' && (
            <BookingWizard
              step={step} setStep={setStep}
              selectedBarber={selectedBarber} setSelectedBarber={setSelectedBarber}
              services={services} selectedServices={selectedServices} handleSelectService={handleSelectService}
              selectedDate={selectedDate} setSelectedDate={setSelectedDate}
              selectedTime={selectedTime} setSelectedTime={setSelectedTime}
              clientInfo={clientInfo} setClientInfo={setClientInfo}
              handlePhoneChange={handlePhoneChange} phoneError={phoneError}
              confirmBooking={confirmBooking} loading={loading}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              timeSlots={timeSlots} lunchSlots={lunchSlots} getAvailableDates={getAvailableDates}
              isBlockFree={isBlockFree} getEndTime={getEndTime}
              totalDuration={totalDuration} totalPrice={totalPrice}
              onClose={() => setAdminSubView('calendar')}
              setViewServicePhoto={setViewServicePhoto}
              shopSettings={shopSettings}
              locationName={selectedLocation?.name}
              locationId={selectedLocation?.id}
              address={selectedLocation?.address}
              isDarkMode={isDarkMode}
              currentPlan={currentPlan}
              blockedTimes={blockedTimes}
              enabledFeatures={enabledFeatures}
            />
          )}

          {adminSubView === 'bookings' && (!historialListo ? <CargandoHistorial isDarkMode={isDarkMode} /> :
            <AdminBookings
              bookings={bookings}
              barbers={barbers}
              selectedAdminLocationId={selectedAdminLocationId}
              updateBookingStatus={updateBookingStatus}
              deleteBooking={deleteBooking}
              setBookingToEdit={setBookingToEdit}
              sendWhatsApp={sendWhatsApp}
              isDarkMode={isDarkMode}
              currentPlan={currentPlan}
              shopSettings={shopSettings}
              enabledFeatures={enabledFeatures}
            />
          )}



        </ModernAdminLayout>



        {/* MODALES FLOTANTES */}

        {/* 🗂️ GESTIONAR RESERVA — mismo diseño que el resto de los modales del
            panel (AdminSheet + adminTheme). Solo cambia la presentación: el
            guardado, los estados, el pago y los servicios funcionan igual. */}
        {bookingToEdit && (() => {
          const ui = getAdminTheme(isDarkMode);
          const pagadoBancard = bookingToEdit.paymentMethod === 'bancard';
          const esPremiumOMas = currentPlan?.level === 'premium' || currentPlan?.level === 'empresarial';
          const noEsBasico = !!currentPlan?.level && currentPlan.level.toLowerCase() !== 'basic';
          const subtotalServicios = (bookingToEdit.services || []).reduce((sum, s) => sum + (Number(s.price) || 0), 0);
          const puedeReagendar = !['cancelled', 'completed', 'no_show'].includes(bookingToEdit.status) && !bookingToEdit.isManualEntry;
          const fechaTurno = (() => {
            try { return format(parse(bookingToEdit.date, 'yyyy-MM-dd', new Date()), "EEEE d 'de' MMMM", { locale: es }); }
            catch (e) { return (bookingToEdit.date || '').split('-').reverse().join('/'); }
          })();
          const campo = `w-full h-11 px-3.5 rounded-xl outline-none text-[16px] sm:text-[15px] transition-[border-color,box-shadow] ${ui.input}`;
          const etiqueta = `block text-[13px] font-medium px-1 mb-1.5 ${ui.soft}`;
          const tituloSeccion = `text-[13px] font-semibold px-1 mb-2 ${ui.soft}`;

          // 🧠 CRM: estado del cliente según su historial de visitas completadas
          const cleanPhone = claveTelefono(bookingToEdit.client?.phone);
          const history = cleanPhone ? bookings.filter(b =>
            b.status === 'completed' &&
            claveTelefono(b.client?.phone) === cleanPhone
          ).sort((a, b) => new Date(b.date) - new Date(a.date)) : [];
          const lastVisit = history.length > 0 ? parse(history[0].date, 'yyyy-MM-dd', new Date()) : null;
          const daysAbsent = lastVisit ? differenceInDays(new Date(), lastVisit) : 0;
          const totalVisits = history.length;
          const estadoCliente = !historialListo ? null : daysAbsent > 45
            ? { icon: UserX, tono: 'bg-rose-500/10 text-rose-500', titulo: `Cliente perdido · ${daysAbsent} días`, texto: 'Elegible para descuento de recuperación.' }
            : (daysAbsent >= 25 && daysAbsent <= 45)
              ? { icon: Clock, tono: 'bg-amber-500/10 text-amber-500', titulo: `En riesgo · ${daysAbsent} días`, texto: 'Considerá ofrecerle un regalo o detalle.' }
              : totalVisits >= 3
                ? { icon: Star, tono: 'bg-emerald-500/10 text-emerald-500', titulo: `Cliente frecuente · ${totalVisits} visitas`, texto: null }
                : null;

          const estadosTurno = [
            { id: 'pending', dot: 'bg-amber-400', on: 'bg-amber-400 text-black', label: 'Pendiente' },
            { id: 'confirmed', dot: 'bg-blue-500', on: 'bg-blue-500 text-white', label: 'Confirmado' },
            { id: 'in_progress', dot: 'bg-violet-500', on: 'bg-violet-500 text-white', label: 'Trabajando' },
            { id: 'completed', dot: 'bg-emerald-500', on: 'bg-emerald-500 text-black', label: 'Completado' },
            { id: 'cancelled', dot: 'bg-rose-500', on: 'bg-rose-500 text-white', label: 'Cancelado' },
            { id: 'no_show', dot: 'bg-gray-400', on: 'bg-gray-500 text-white', label: 'No asistió' }
          ].filter(state => {
            // "Trabajando" solo existe con la pantalla En Vivo (Empresarial)
            if (state.id === 'in_progress') {
              const planName = currentPlan?.level || 'basic';
              if (planName !== 'empresarial') return false;
              if (shopSettings?.enableLiveView === false) return false;
            }
            return true;
          });

          const fullBarber = barbers.find(b => b.id === bookingToEdit.barber?.id);
          const serviciosParaAgregar = fullBarber?.allowedCategories ? services.filter(s => fullBarber.allowedCategories.includes(s.category || 'General')) : services;

          return (
            <AdminSheet
              isDarkMode={isDarkMode}
              zIndex={99999}
              eyebrow={`Ticket #${bookingToEdit.bookingGroupId?.slice(-5) || ''}`}
              title="Gestionar reserva"
              onClose={() => setBookingToEdit(null)}
              footer={
                <>
                  <button onClick={() => { const idToDelete = bookingToEdit.bookingGroupId || bookingToEdit.id; deleteBooking(idToDelete); setBookingToEdit(null); }}
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-rose-500/10 hover:bg-rose-500/15 text-rose-500 transition-colors active:scale-95"
                    title="Eliminar reserva" aria-label="Eliminar reserva">
                    <Trash2 size={18} />
                  </button>

                  {bookingToEdit.status === 'completed' && shopSettings?.sifen?.habilitado && (
                    <button
                      onClick={() => setShowFacturacionModal(true)}
                      title={bookingToEdit.factura?.cdc ? 'Ver Factura' : 'Emitir Factura'}
                      className={`h-12 px-4 rounded-xl flex items-center gap-2 text-[14px] font-semibold shrink-0 transition-colors ${bookingToEdit.factura?.cdc ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/15' : 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/15'}`}
                    >
                      <FileText size={16} />
                      {bookingToEdit.factura?.cdc ? `N°${bookingToEdit.factura.numeroFormateado?.split('-')[2]}` : 'Facturar'}
                    </button>
                  )}

                  <button
                    onClick={async () => {
                    if (!bookingToEdit.id) return addToast("Error", "ID inválido.", "error");

                    if (bookingToEdit.paymentMethod === 'online') {
                      // 🔴 AGREGAR ESTA LÍNEA: Marcar explícitamente que salimos del admin
                      localStorage.setItem('paymentOrigin', 'admin');
                      // 🔐 AUDITORÍA: ticket que inicia el pago (ver usePaymentCheck)
                      try { localStorage.setItem('paymentTicket', String(bookingToEdit.bookingGroupId || bookingToEdit.id)); } catch (e) { /* navegador restringido */ }

                      setShowBancardModal(true);
                      return;
                    }
                    setLoading(true);
                    try {
                      // 🎫 Decisión de descuento de visita de membresía — se
                      // calcula ACÁ (antes del batch) para poder incluir la
                      // marca anti-duplicado en el mismo commit atómico.
                      const debeDescontarVisita = bookingToEdit.status === 'completed'
                        && bookingToEdit.membershipId
                        && !bookingToEdit.membershipVisitDeducted;

                      const updateData = {
                        status: bookingToEdit.status,
                        client: bookingToEdit.client,
                        paymentMethod: bookingToEdit.paymentMethod || 'local',
                        services: bookingToEdit.services,
                        totalPrice: bookingToEdit.totalPrice,
                        discount: bookingToEdit.discount || 0,
                        discountPercentage: bookingToEdit.discountPercentage || 0,
                        locationId: bookingToEdit.locationId || selectedAdminLocationId,
                        updatedAt: Timestamp.now()
                      };

                      if (bookingToEdit.status === 'in_progress') {
                        updateData.startedAt = bookingToEdit.startedAt || Timestamp.now();
                      }
                      if (debeDescontarVisita) {
                        updateData.membershipVisitDeducted = true;
                      }

                      const batch = writeBatch(db);
                      const q = query(
                        collection(db, "bookings"),
                        where("bookingGroupId", "==", bookingToEdit.bookingGroupId),
                        where("date", "==", bookingToEdit.date)
                      );
                      const querySnapshot = await getDocs(q);

                      // 🔧 A pedido: si el turno guardado está Cancelado / "No asistió"
                      // y el admin lo quiere volver a poner activo (Confirmado,
                      // Pendiente, etc.), se verifica que nadie más haya tomado ese
                      // horario mientras tanto. Si ya hay un turno activo ahí, NO se
                      // guarda el cambio.
                      const docsActuales = querySnapshot.empty ? [] : querySnapshot.docs;
                      let estadoGuardado = (docsActuales.find(d => d.data().isPrimary === true) || docsActuales[0])?.data().status;
                      if (!estadoGuardado && querySnapshot.empty) {
                        estadoGuardado = (await getDoc(doc(db, 'bookings', bookingToEdit.id))).data()?.status;
                      }
                      const reactiva =
                        ['cancelled', 'no_show'].includes(estadoGuardado) &&
                        ['pending', 'confirmed', 'in_progress', 'completed'].includes(bookingToEdit.status);
                      if (reactiva) {
                        const horariosOcupados = (docsActuales.length > 0 ? docsActuales.map(d => d.data().time) : [bookingToEdit.time]).filter(Boolean);
                        const snapConflicto = await getDocs(query(
                          collection(db, 'bookings'),
                          where('locationId', '==', bookingToEdit.locationId || selectedAdminLocationId),
                          where('barber.id', '==', bookingToEdit.barber?.id),
                          where('date', '==', bookingToEdit.date)
                        ));
                        const hayConflicto = snapConflicto.docs.some(d => {
                          const b = d.data();
                          if (String(b.bookingGroupId || d.id) === String(bookingToEdit.bookingGroupId || bookingToEdit.id)) return false;
                          if (!['pending', 'confirmed', 'in_progress', 'completed'].includes(b.status)) return false;
                          return horariosOcupados.includes(b.time);
                        });
                        if (hayConflicto) {
                          addToast("No se puede confirmar", "Ese horario ya tiene otro turno activo (otro cliente lo tomó). Dejá este turno como cancelado o cambiá el horario.", "error");
                          setLoading(false);
                          return;
                        }
                      }

                      if (querySnapshot.empty) {
                        const docRef = doc(db, 'bookings', bookingToEdit.id);
                        batch.update(docRef, updateData);
                      } else {
                        querySnapshot.forEach((doc) => {
                          batch.update(doc.ref, updateData);
                        });
                      }

                      // 1. Guardamos en la base de datos
                      await batch.commit();

                      // 📧 Si esta reserva es de un socio y el barbero cargó
                      // o corrigió el email acá, lo sincronizamos en su
                      // ficha de membresía — así puede usarlo como
                      // identificador además del teléfono. Best-effort: si
                      // falla, no frena el guardado de la reserva.
                      if (bookingToEdit.membershipId && bookingToEdit.client?.email) {
                        try {
                          await updateDoc(
                            doc(db, 'locations', bookingToEdit.locationId || selectedAdminLocationId, 'members', bookingToEdit.membershipId),
                            { clientEmail: bookingToEdit.client.email.trim().toLowerCase() }
                          );
                        } catch (eSync) {
                          console.error('⚠️ No se pudo sincronizar el email del socio:', eSync.message);
                        }
                      }

                      // 👇=== 🚀 2. LA MAGIA: ENVIAR WHATSAPP SI SE CANCELA ===👇
                      if (bookingToEdit.status === 'cancelled') {
                        await enviarWhatsAppAutomatico({
                          client: bookingToEdit.client,
                          date: bookingToEdit.date,
                          startTime: bookingToEdit.startTime || bookingToEdit.time,
                          locationId: bookingToEdit.locationId || selectedAdminLocationId,
                          // ✅ mismo fix
                          companyId: bookingToEdit.companyId || currentUser?.companyId || null,
                          barber: bookingToEdit.barber,
                          services: bookingToEdit.services,
                          totalPrice: bookingToEdit.totalPrice,
                          bookingGroupId: bookingToEdit.bookingGroupId,
                          status: 'cancelled'
                        });
                      }

                      if (bookingToEdit.status === 'completed') {
                        const planName = currentPlan?.level?.toLowerCase() || 'basic';

                        // 🎁 Descontar la visita de membresía (blindado con
                        // debeDescontarVisita, calculado antes del batch, y
                        // con membershipVisitDeducted ya guardado para que
                        // no se repita si se vuelve a guardar este modal).
                        // incrementarVisitaMembresia ya sabe distinguir el
                        // cupo principal del bonus, mirando los servicios
                        // reales de esta reserva.
                        if (debeDescontarVisita) {
                          await incrementarVisitaMembresia(
                            bookingToEdit.locationId || selectedAdminLocationId,
                            bookingToEdit.membershipId,
                            bookingToEdit.services,
                            bookingToEdit.bookingGroupId || bookingToEdit.id
                          );
                        }

                        if (planName === 'premium' || planName === 'empresarial') {
                          await enviarWhatsAppAutomatico({
                            client: bookingToEdit.client,
                            date: bookingToEdit.date,
                            startTime: bookingToEdit.startTime || bookingToEdit.time,
                            locationId: bookingToEdit.locationId || selectedAdminLocationId,
                            companyId: bookingToEdit.companyId || currentUser?.companyId || null,
                            barber: bookingToEdit.barber,
                            services: bookingToEdit.services,
                            totalPrice: bookingToEdit.totalPrice,
                            bookingGroupId: bookingToEdit.bookingGroupId,
                            id: bookingToEdit.id, // 🆕 ESTA LÍNEA ES CLAVE
                            status: 'completed'
                          });
                        }
                      }

                      setBookingToEdit(null);
                      addToast("Actualizado", "Cambios guardados correctamente.", "success");
                    } catch (error) {
                      console.error("Error al guardar:", error);
                      addToast("Error", "No se pudo guardar.", "error");

                    }
                    setLoading(false);
                  }}
                    disabled={loading}
                    className={`${adButton.primary} flex-1 h-12 text-[15px] disabled:opacity-60`}
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                    {bookingToEdit.paymentMethod === 'online' ? 'Ir al pago' : 'Guardar cambios'}
                  </button>
                </>
              }
            >
              <div className="space-y-5">

                {/* Sellos del turno */}
                {(bookingToEdit.membershipId || bookingToEdit.senaTransferenciaPendiente || clienteBloqueadoInfo?.blocked) && (
                  <div className="flex flex-wrap gap-1.5 -mt-1">
                    {bookingToEdit.membershipId && (
                      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[12px] font-semibold bg-amber-500/10 text-amber-500">
                        <Ticket size={12} /> Socio{bookingToEdit.membershipPlanName ? ` · ${bookingToEdit.membershipPlanName}` : ''}
                      </span>
                    )}
                    {bookingToEdit.senaTransferenciaPendiente && (
                      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[12px] font-semibold bg-amber-500/10 text-amber-500">
                        <Banknote size={12} /> Seña pendiente
                      </span>
                    )}
                    {clienteBloqueadoInfo?.blocked && (
                      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[12px] font-semibold bg-rose-500/10 text-rose-500">
                        <Ban size={12} /> Bloqueado
                      </span>
                    )}
                  </div>
                )}

                {/* 💰 Seña por transferencia pendiente — confirmarla desde acá */}
                {bookingToEdit.status !== 'cancelled' && bookingToEdit.senaTransferenciaPendiente && (
                  <div className="p-4 rounded-2xl flex items-center justify-between gap-3 bg-amber-500/10">
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-amber-500">Seña por transferencia pendiente</p>
                      <p className={`text-[13px] mt-0.5 ${ui.soft}`}>
                        Monto esperado: {formatPrice(bookingToEdit.senaMonto || 0)}{bookingToEdit.senaPorcentaje ? ` (${bookingToEdit.senaPorcentaje}%)` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        updateBookingStatus(bookingToEdit.bookingGroupId || bookingToEdit.id, 'confirmed', { confirmarSenia: true });
                        setBookingToEdit(prev => prev ? { ...prev, senaTransferenciaPendiente: false, status: 'confirmed' } : prev);
                      }}
                      className="h-10 px-4 rounded-xl text-[14px] font-semibold shrink-0 bg-amber-500 hover:bg-amber-400 text-black transition-colors active:scale-95"
                    >
                      Confirmar seña
                    </button>
                  </div>
                )}

                {/* Turno cancelado por no confirmar la seña a tiempo: se puede
                    recuperar (se verifica que el horario siga libre). */}
                {bookingToEdit.status === 'cancelled' && (bookingToEdit.cancelReason === 'sena_no_confirmada' || bookingToEdit.senaTransferenciaPendiente) && (
                  <div className="p-4 rounded-2xl flex items-center justify-between gap-3 bg-emerald-500/10">
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-emerald-500">Cancelado por falta de seña</p>
                      <p className={`text-[13px] mt-0.5 ${ui.soft}`}>
                        Si el comprobante llegó tarde, podés recuperar este turno. Se verifica que el horario siga libre.
                      </p>
                    </div>
                    <button
                      onClick={() => updateBookingStatus(bookingToEdit.bookingGroupId || bookingToEdit.id, 'confirmed', { confirmarSenia: true })}
                      className="h-10 px-4 rounded-xl text-[14px] font-semibold shrink-0 bg-emerald-500 hover:bg-emerald-400 text-black transition-colors active:scale-95"
                    >
                      Recuperar turno
                    </button>
                  </div>
                )}

                {/* 🚫 Bloquear / desbloquear al cliente (para quienes reservan y no
                    asisten) — mismo mecanismo que la Ficha del cliente. */}
                {bookingToEdit.client?.phone && (
                  <div className={`p-4 rounded-2xl flex items-center justify-between gap-3 ${clienteBloqueadoInfo?.blocked ? 'bg-rose-500/10' : ui.inset}`}>
                    <div className="min-w-0">
                      <p className={`text-[14px] font-semibold ${clienteBloqueadoInfo?.blocked ? 'text-rose-500' : ui.ink}`}>
                        {clienteBloqueadoInfo?.blocked ? 'Cliente bloqueado' : 'Bloquear cliente'}
                      </p>
                      <p className={`text-[13px] mt-0.5 ${ui.soft}`}>
                        {clienteBloqueadoInfo?.blocked
                          ? (clienteBloqueadoInfo.reason || 'No puede hacer nuevas reservas.')
                          : 'Para clientes que reservan y no asisten.'}
                      </p>
                    </div>
                    <button
                      onClick={handleToggleBlockDesdeModal}
                      className={`h-10 px-4 rounded-xl text-[14px] font-semibold shrink-0 transition-colors active:scale-95 ${clienteBloqueadoInfo?.blocked ? ui.btnSecondary : 'bg-rose-500/10 text-rose-500 hover:bg-rose-500/15'}`}
                    >
                      {clienteBloqueadoInfo?.blocked ? 'Desbloquear' : 'Bloquear'}
                    </button>
                  </div>
                )}

                {/* 📅 Turno: fecha, hora y profesional (+ reagendar) */}
                <div className={`p-4 rounded-2xl ${ui.card}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-amber-500/15 text-amber-500"><Calendar size={18} /></span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[15px] font-semibold capitalize truncate ${ui.ink}`}>{fechaTurno} · {bookingToEdit.startTime || bookingToEdit.time}</p>
                      <p className={`text-[13px] truncate ${ui.soft}`}>con {bookingToEdit.barber?.name}</p>
                    </div>
                    {puedeReagendar && (
                      <button
                        onClick={() => setShowReagendar(true)}
                        className={`${adButton.base} shrink-0 ${ui.btnSecondary}`}
                      >
                        <Calendar size={15} /> Reagendar
                      </button>
                    )}
                  </div>
                  <DomicilioInfo booking={bookingToEdit} isDarkMode={isDarkMode} className="mt-3" />
                </div>

                {/* 🧠 Estado del cliente (CRM) */}
                {estadoCliente && (
                  <div className={`px-4 py-3 rounded-2xl flex items-center gap-3 ${estadoCliente.tono}`}>
                    <estadoCliente.icon size={18} className="shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold">{estadoCliente.titulo}</p>
                      {estadoCliente.texto && <p className={`text-[13px] ${ui.soft}`}>{estadoCliente.texto}</p>}
                    </div>
                  </div>
                )}

                {/* 👤 Cliente */}
                <section>
                  <p className={tituloSeccion}>Cliente</p>
                  <div className={`p-4 rounded-2xl space-y-3 ${ui.card}`}>
                    <div>
                      <label className={etiqueta}>Nombre</label>
                      <input
                        type="text"
                        value={bookingToEdit.client.name}
                        onChange={(e) => setBookingToEdit({ ...bookingToEdit, client: { ...bookingToEdit.client, name: e.target.value } })}
                        className={`${campo} font-semibold`}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={etiqueta}>Teléfono</label>
                        <input
                          type="tel"
                          inputMode="numeric"
                          value={bookingToEdit.client.phone}
                          // Solo dígitos, máximo 15 (admite números de Brasil
                          // u otros países, no solo los 10 de Paraguay).
                          onChange={(e) => {
                            const soloDigitos = e.target.value.replace(/\D/g, '').slice(0, 15);
                            setBookingToEdit({ ...bookingToEdit, client: { ...bookingToEdit.client, phone: soloDigitos } });
                          }}
                          maxLength={15}
                          className={`${campo} tabular-nums`}
                        />
                      </div>
                      {/* CI — lo carga el barbero con el cliente presente. Útil para facturación (SIFEN). */}
                      <div>
                        <label className={etiqueta}>Cédula (CI)</label>
                        <input
                          type="text"
                          value={bookingToEdit.client.ci || ''}
                          onChange={(e) => setBookingToEdit({ ...bookingToEdit, client: { ...bookingToEdit.client, ci: e.target.value } })}
                          placeholder="Ej: 4.123.456"
                          className={`${campo} tabular-nums`}
                        />
                      </div>
                    </div>
                    {/* Email — si es socio, se sincroniza con su ficha de membresía al guardar */}
                    <div>
                      <label className={etiqueta}>Email</label>
                      <input
                        type="email"
                        value={bookingToEdit.client.email || ''}
                        onChange={(e) => setBookingToEdit({ ...bookingToEdit, client: { ...bookingToEdit.client, email: e.target.value } })}
                        placeholder="cliente@email.com"
                        className={campo}
                      />
                    </div>
                  </div>
                </section>

                {/* 🚦 Estado del turno */}
                <section>
                  <p className={tituloSeccion}>Estado del turno</p>
                  <div role="radiogroup" aria-label="Estado del turno" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {estadosTurno.map((state) => {
                      const activo = bookingToEdit.status === state.id;
                      return (
                        <button
                          key={state.id}
                          role="radio"
                          aria-checked={activo}
                          onClick={() => {
                            const updates = { status: state.id };
                            if (state.id === 'in_progress') {
                              updates.startedAt = Timestamp.now();
                            }
                            setBookingToEdit({ ...bookingToEdit, ...updates });
                          }}
                          className={`h-11 px-3 rounded-xl flex items-center gap-2 text-[14px] font-semibold transition-colors active:scale-[0.98] ${activo ? state.on : ui.btnSecondary}`}
                        >
                          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${activo ? 'bg-current opacity-70' : state.dot}`} />
                          {state.label}
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* 💳 Método de pago */}
                <section>
                  <p className={tituloSeccion}>Método de pago</p>
                  <select
                    value={bookingToEdit.paymentMethod || 'local'}
                    onChange={(e) => setBookingToEdit({ ...bookingToEdit, paymentMethod: e.target.value })}
                    disabled={pagadoBancard}
                    className={`${campo} ${pagadoBancard ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <option value="local">💵 Efectivo</option>
                    <option value="transferencia">🔁 Transferencia</option>
                    <option value="pos">💳 POS / Tarjeta</option>
                    {/* Bancard: plan no básico + activado por la barbería + habilitado por Joel */}
                    {noEsBasico && shopSettings?.enableBancard && enabledFeatures?.bancard && (
                      <>
                        <option value="online">💸 Solicitar pago online</option>
                        <option value="bancard">✅ Pagado con Bancard</option>
                      </>
                    )}
                  </select>

                  {noEsBasico && pagadoBancard && (
                    <div className="mt-2 h-10 px-3 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold bg-emerald-500/10 text-emerald-500">
                      <CheckCircle size={14} /> Transacción Bancard aprobada
                    </div>
                  )}

                  {bookingToEdit.paymentMethod === 'online' && (
                    <a href={`https://wa.me/${telefonoInternacional(bookingToEdit.client.phone)}?text=Hola ${bookingToEdit.client.name}, para confirmar tu reserva...`} target="_blank" rel="noreferrer"
                      className="mt-2 h-10 px-3 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold bg-blue-500/10 text-blue-500 hover:bg-blue-500/15 transition-colors">
                      <MessageCircle size={14} /> Solicitar pago por WhatsApp
                    </a>
                  )}
                </section>

                {/* ✂️ Servicios */}
                <section>
                  <div className="flex items-center justify-between gap-2 px-1 mb-2">
                    <p className={`text-[13px] font-semibold ${ui.soft}`}>Servicios</p>
                    {pagadoBancard && (
                      <span className="text-[12px] font-semibold px-2 h-6 inline-flex items-center rounded-md bg-amber-500/10 text-amber-500">
                        Bloqueado por pago online
                      </span>
                    )}
                  </div>

                  <div className={`rounded-2xl overflow-hidden ${ui.card}`}>
                    {(bookingToEdit.services || []).length === 0 ? (
                      <p className={`px-4 py-5 text-center text-[14px] ${ui.soft}`}>Sin servicios cargados.</p>
                    ) : (
                      <ul className={`divide-y ${ui.divide}`}>
                        {bookingToEdit.services.map((s, index) => (
                          <li key={index} className="flex items-center gap-3 pl-4 pr-2 py-2.5">
                            <span className={`flex-1 min-w-0 text-[15px] font-medium truncate ${ui.ink}`}>{s.name}</span>
                            <span className={`text-[14px] tabular-nums shrink-0 ${ui.soft}`}>{formatPrice(s.price)}</span>
                            <button
                              onClick={() => { if (!pagadoBancard) removeServiceFromBooking(bookingToEdit, index); }}
                              disabled={pagadoBancard}
                              aria-label={`Quitar ${s.name}`}
                              className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${pagadoBancard ? `${ui.faint} cursor-not-allowed opacity-50` : 'text-rose-500 hover:bg-rose-500/10'}`}
                            >
                              <Trash2 size={15} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* 🩹 Recalcular duración — para turnos que quedaron con menos
                      horarios bloqueados de los que necesitan de verdad. */}
                  {!pagadoBancard && (
                    <button
                      onClick={() => recalcularDuracionBooking(bookingToEdit)}
                      disabled={loading}
                      className={`mt-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50 ${ui.soft} ${ui.hover}`}
                      title="Recalcula la duración real y bloquea los horarios que falten, sin tocar los servicios cargados"
                    >
                      <RefreshCw size={13} /> Recalcular duración
                    </button>
                  )}

                  {/* ➕ Agregar servicio / producto */}
                  <div className={`mt-3 space-y-3 transition-opacity ${pagadoBancard ? 'opacity-40 pointer-events-none' : ''}`}>
                    <div>
                      <p className={`text-[13px] font-medium px-1 mb-1.5 ${ui.faint}`}>Agregar servicio</p>
                      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                        {serviciosParaAgregar.map(s => (
                          <button
                            key={s.id}
                            onClick={() => addServiceToBooking(bookingToEdit, s)}
                            disabled={pagadoBancard}
                            className={`h-9 pl-2.5 pr-3 rounded-full inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors active:scale-95 ${ui.btnSecondary}`}
                          >
                            <PlusCircle size={14} className="text-amber-500" /> {s.name}
                            <span className={`font-medium tabular-nums ${ui.faint}`}>{formatPrice(s.price)}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 🛍️ Producto extra: mismo mecanismo (duración 0, solo suma precio).
                        El prefijo 🛍️ lo usan Caja y Comisiones para reconocerlo. */}
                    {products.length > 0 && (
                      <div>
                        <p className={`text-[13px] font-medium px-1 mb-1.5 ${ui.faint}`}>Agregar producto</p>
                        <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                          {products.map(p => (
                            <button
                              key={p.id}
                              onClick={() => addServiceToBooking(bookingToEdit, { name: `🛍️ ${p.name}`, price: Number(p.price) || 0, duration: 0 })}
                              disabled={pagadoBancard}
                              className={`h-9 pl-2.5 pr-3 rounded-full inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors active:scale-95 ${ui.btnSecondary}`}
                            >
                              <ShoppingBag size={14} className="text-amber-500" /> {p.name}
                              <span className={`font-medium tabular-nums ${ui.faint}`}>{formatPrice(p.price)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* 💰 Descuento y total */}
                <section className={`p-4 rounded-2xl space-y-3 ${ui.card}`}>
                  {/* Descuento: solo Premium/Empresarial y si NO está pagado online */}
                  {!pagadoBancard && esPremiumOMas && (
                    <div className="flex items-center justify-between gap-3">
                      <label htmlFor="descuento-reserva" className={`text-[14px] font-medium flex items-center gap-1.5 ${ui.soft}`}>
                        <Percent size={14} /> Descuento
                      </label>
                      <div className="relative w-28">
                        <input
                          id="descuento-reserva"
                          type="number"
                          inputMode="numeric"
                          placeholder="0"
                          min="0"
                          max="100"
                          value={bookingToEdit.discountPercentage || ''}
                          onChange={(e) => {
                            const percent = Number(e.target.value);
                            const subtotal = (bookingToEdit.services || []).reduce((sum, s) => sum + (Number(s.price) || 0), 0);
                            const discountAmount = (subtotal * percent) / 100;
                            const newTotal = Math.max(0, subtotal - discountAmount);
                            // Se guarda el porcentaje explícitamente, además del monto
                            setBookingToEdit({
                              ...bookingToEdit,
                              discountPercentage: percent,
                              discount: discountAmount,
                              totalPrice: newTotal
                            });
                          }}
                          className={`w-full h-10 pl-3 pr-8 rounded-xl outline-none text-right text-[15px] font-semibold tabular-nums transition-[border-color,box-shadow] ${ui.input}`}
                        />
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold ${ui.faint}`}>%</span>
                      </div>
                    </div>
                  )}

                  {bookingToEdit.discount > 0 && (
                    <div className={`space-y-1 pb-3 border-b ${ui.line}`}>
                      <div className="flex justify-between text-[13px]">
                        <span className={ui.soft}>Subtotal</span>
                        <span className={`tabular-nums line-through ${ui.faint}`}>{formatPrice(subtotalServicios)}</span>
                      </div>
                      <div className="flex justify-between text-[13px] text-rose-500 font-semibold">
                        <span>Descuento aplicado</span>
                        <span className="tabular-nums">− {formatPrice(bookingToEdit.discount)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-end justify-between gap-3">
                    <span className={`text-[14px] font-semibold ${ui.ink}`}>Total a pagar</span>
                    <div className="text-right">
                      <p className={`${adType.number} text-[30px] text-emerald-500`}>{formatPrice(bookingToEdit.totalPrice)}</p>
                      {altCurrencyEnabled && (
                        <p className={`text-[12px] font-medium mt-1 ${ui.soft}`}>≈ {formatAltCurrency(bookingToEdit.totalPrice)}</p>
                      )}
                    </div>
                  </div>
                </section>

              </div>
            </AdminSheet>
          );
        })()}


        {showCashModal && !historialListo && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => setShowCashModal(false)}>
            <div className={`rounded-2xl px-10 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}><CargandoHistorial isDarkMode={isDarkMode} /></div>
          </div>
        )}
        <CashRegisterModal isOpen={showCashModal && historialListo} onClose={() => setShowCashModal(false)} locationId={selectedAdminLocationId} bookings={bookings} barbers={barbers} locations={locations} addToast={addToast} />

        {showReagendar && bookingToEdit && (
          <Suspense fallback={null}>
            <RescheduleModal
              booking={bookingToEdit}
              isDarkMode={isDarkMode}
              onClose={() => setShowReagendar(false)}
              cargarHorarios={(dateStr) => calcularHorariosReagendar(bookingToEdit, dateStr)}
              onConfirm={(dateStr, opcion) => reagendarReserva(bookingToEdit, dateStr, opcion)}
            />
          </Suspense>
        )}

        {showBancardModal && bookingToEdit && (
          <div style={{ position: 'relative', zIndex: 100000 }}>
            <BancardPaymentModal
              ticketId={bookingToEdit.bookingGroupId || bookingToEdit.id}
              total={bookingToEdit.totalPrice}
              onClose={() => setShowBancardModal(false)}
              onCancel={() => { setShowBancardModal(false); addToast("Pago Cancelado", "No se completó el pago online.", "info"); }}
              allowPayLater={true}
              onPayLater={async () => { setShowBancardModal(false); }}
            />
          </div>
        )}




        {/* Modal Categorías - VERSIÓN ANTI-TONTOS */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[10000] backdrop-blur-sm" style={{ zIndex: 99999 }}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`rounded-[2.5rem] p-8 w-full max-w-md border shadow-2xl transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
                }`}
            >
              {/* --- HEADER --- */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className={`text-2xl font-black flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    <Tag size={24} className="text-amber-500" /> Categorías
                  </h3>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1">Organiza tus servicios</p>
                </div>
                <button
                  onClick={() => { setShowCategoryModal(false); setEditingCategory(null); setNewCategoryName(''); }}
                  className={`p-2 rounded-full transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-400 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* --- 🛡️ BANNER EDUCATIVO (Solo sale si solo hay defaults) --- */}
              {categories.every(cat => String(cat.id).startsWith('def')) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex gap-3"
                >
                  <Sparkles className="text-amber-500 shrink-0" size={20} />
                  <p className="text-[11px] font-bold text-amber-600 leading-tight">
                    <span className="block mb-1 text-xs font-black uppercase">¡Atención!</span>
                    Crea categorías propias (ej: "Barba", "Corte Niño"). Luego podrás asignarlas a tus barberos para que sus servicios aparezcan en el link.
                  </p>
                </motion.div>
              )}

              {/* --- INPUT DE CREACIÓN --- */}
              <div className="flex gap-2 mb-8">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Ej: Colorimetría..."
                    className={`w-full rounded-2xl px-4 py-4 text-sm font-bold border outline-none transition-all ${isDarkMode
                      ? 'bg-gray-900 border-gray-700 text-white focus:border-amber-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-amber-400'
                      }`}
                  />
                </div>
                <button
                  onClick={saveCategory}
                  disabled={!newCategoryName.trim() || loading}
                  className={`px-6 rounded-2xl font-black transition-all shadow-lg active:scale-95 flex items-center justify-center ${editingCategory
                    ? 'bg-green-600 text-white hover:bg-green-500 shadow-green-500/20'
                    : 'bg-amber-500 text-black hover:bg-amber-400 shadow-amber-500/20'
                    }`}
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : editingCategory ? <Check size={20} /> : <PlusCircle size={20} />}
                </button>
                {editingCategory && (
                  <button
                    onClick={() => { setEditingCategory(null); setNewCategoryName(''); }}
                    className={`p-4 rounded-2xl font-bold transition-all ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-slate-200 text-slate-600'}`}
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              {/* --- LISTA DE CATEGORÍAS --- */}
              <div className="space-y-3 max-h-72 overflow-y-auto pr-2 custom-scrollbar">
                {categories.map(cat => {
                  const isDefault = String(cat.id).startsWith('def');
                  return (
                    <div
                      key={cat.id}
                      className={`flex justify-between items-center p-4 rounded-2xl border transition-all ${editingCategory?.id === cat.id
                        ? 'bg-amber-500/10 border-amber-500 ring-2 ring-amber-500/20'
                        : (isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-white border-slate-100 shadow-sm')
                        } ${isDefault ? 'opacity-60 grayscale' : ''}`}
                    >
                      <div className="flex flex-col">
                        <span className={`text-sm font-black ${isDarkMode ? 'text-gray-200' : 'text-slate-800'}`}>
                          {cat.name}
                        </span>
                        {isDefault && <span className="text-[9px] font-black text-amber-600 uppercase tracking-tighter">Ejemplo</span>}
                      </div>

                      <div className="flex gap-1">
                        {!isDefault ? (
                          <>
                            <button
                              onClick={() => { setEditingCategory(cat); setNewCategoryName(cat.name); }}
                              className={`p-2 rounded-xl transition-all ${isDarkMode ? 'hover:bg-blue-500/20 text-blue-400' : 'hover:bg-blue-50 text-blue-600'}`}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => deleteCategory(cat.id)}
                              className={`p-2 rounded-xl transition-all ${isDarkMode ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-600'}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        ) : (
                          <div className="p-2 text-gray-500" title="Las categorías por defecto no se pueden borrar">
                            <Lock size={14} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="mt-6 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                Importante: Asigna estas categorías a tus barberos en la pestaña "Equipo".
              </p>
            </motion.div>
          </div>
        )}
        {showFacturacionModal && bookingToEdit && (
          <FacturacionModal
            booking={bookingToEdit}
            bookingId={bookingToEdit.id}
            companyId={bookingToEdit.companyId || currentUser?.companyId || selectedAdminLocationId}
            shopSettings={shopSettings}
            onClose={() => setShowFacturacionModal(false)}
            onSuccess={(resultado) => {
              setBookingToEdit(prev => ({
                ...prev,
                factura: {
                  cdc: resultado.cdc,
                  numeroFormateado: resultado.numeroFormateado,
                  kudeUrl: resultado.kudeUrl || null,
                  estado: resultado.estado,
                  total: resultado.total,
                }
              }));
              addToast("🧾 Factura emitida", `N° ${resultado.numeroFormateado}`, "success");
            }}
          />
        )}

      </>
    );
  };



  // =====================================================================
  // ⚡ A pedido — Precarga de locales: antes, la lista de locales recién
  // se pedía a Firebase DESPUÉS de que Firebase Auth terminaba de
  // inicializarse (onAuthStateChanged tarda en dispararse la primera vez,
  // sobre todo con el caché vacío) — y recién ahí arrancaba todo lo
  // demás (barberos, servicios, ajustes). Pero para entrar al link de
  // una barbería el cliente NO necesita estar logueado para nada, así que
  // esa espera era tiempo perdido. Ahora, en rutas públicas, la carga
  // arranca apenas monta la app, en paralelo con Auth. El listener de
  // abajo solo la repite si esta falló.
  // =====================================================================
  useEffect(() => {
    const pathActual = window.location.pathname;
    const esRutaDeAdmin = pathActual === '/login' || pathActual === '/god-mode-2026';
    if (esRutaDeAdmin) return;
    locationsPublicasPrecargadasRef.current = true;
    loadLocations(null);

    // ⚡ Segunda parte: si este navegador YA entró antes a este link, se
    // guardó qué local era (ver ClientViewDynamic). En ese caso los
    // profesionales y servicios se piden YA, en paralelo con la lista de
    // locales, en vez de esperar a que esa lista llegue para recién
    // después pedirlos — para el visitante que vuelve, se ahorra un viaje
    // completo a Firebase. Es una carga "especulativa": solo se usa si
    // después se elige justo ese mismo local (ver handleSelectLocation);
    // si el dato guardado ya no sirve, se descarta y todo sigue normal.
    try {
      const slugUrl = pathActual.split('/').filter(Boolean)[0];
      const guardado = slugUrl ? JSON.parse(localStorage.getItem('bg-local-rapido:' + slugUrl) || 'null') : null;
      if (guardado && typeof guardado.id === 'string' && guardado.id) {
        const promesa = Promise.all([
          loadBarbers(guardado.id, { desdeCachePublico: true }),
          loadServices(guardado.id, { desdeCachePublico: true }),
        ]).then(r => r.every(Boolean)).catch(() => false);
        precargaPublicaRef.current = { id: guardado.id, promesa };
      }
    } catch (e) { /* navegador restringido (ej: Instagram in-app) — sin carga adelantada, todo sigue normal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // 🔧 FIX: Firebase Auth reconoce la sesión guardada en CUALQUIER
      // ruta del mismo navegador (no es por pestaña) — antes, esto
      // significaba que si el dueño de una barbería abría su propio
      // link de cliente estando logueado en el panel admin (en esa
      // misma pestaña o en una nueva, sin incógnito), este listener
      // igual detectaba su sesión de Firebase y le aplicaba TODO el
      // contexto de admin (isAdminAuth, currentUser con rol 'owner',
      // reglas especiales de horario/backfill) sobre la vista de
      // cliente — rompiendo cosas que solo tienen sentido para un
      // visitante normal. Ahora, si la ruta actual es una de las
      // rutas públicas (la reserva de cualquier barbería, o el demo),
      // se ignora por completo el contexto de admin, sin importar si
      // hay una sesión de Firebase válida — se trata siempre como
      // visitante/cliente en esas rutas.
      const pathActual = window.location.pathname;
      const esRutaDeAdmin = pathActual === '/login' || pathActual === '/god-mode-2026';

      if (!esRutaDeAdmin) {
        setIsAdminAuth(false);
        setCurrentUser(null);
        setIsPlanLoading(false);
        // 👇 Se preserva esto igual que antes — la vista de cliente
        // (ClientViewDynamic) necesita que "locations" esté cargado
        // para poder encontrar la barbería por el slug de la URL.
        // (Si la precarga de arriba ya la pidió y no falló, no se repite.)
        if (!locationsPublicasPrecargadasRef.current) loadLocations(null);
        return;
      }

      if (firebaseUser) {
        setIsPlanLoading(true);

        try {
          let currentUserObj = null;

          // 👑 1. ESCUDO DE SUPERADMIN
          if (firebaseUser.email === 'joelgomez451@gmail.com') {
            currentUserObj = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'superadmin'
            };
            setIsAdminAuth(true);
            localStorage.setItem('admin-auth', 'true');
            setCurrentUser(currentUserObj);
            setIsPlanLoading(false);
            return;
          }

          // 💼 2. BUSCAR SI ES DUEÑO DE UNA EMPRESA
          const qCompany = query(collection(db, 'companies'), where('ownerEmail', '==', firebaseUser.email));
          const companySnap = await getDocs(qCompany);

          if (!companySnap.empty) {
            const companyDoc = companySnap.docs[0];
            const companyData = companyDoc.data();

            if (companyData.status === 'suspended' || companyData.status === 'deleted') {
              addToast("Cuenta Inactiva", "Contacta a soporte.", "error");
              await signOut(auth);
              setIsPlanLoading(false);
              return;
            }

            // 🔒 A pedido: si ya venció la prueba gratuita (14 días) o
            // el período ya pagado, se bloquea el ingreso acá mismo —
            // antes esto solo se chequeaba al elegir un local para
            // RESERVAR (del lado del cliente), nunca al hacer login
            // como dueño/admin.
            if (chequearSuscripcionVencida(companyData)) {
              addToast("Suscripción Vencida", "Tu prueba o período pagado ya venció. Contactá a soporte para renovar.", "error");
              await signOut(auth);
              setIsPlanLoading(false);
              return;
            }

            // 🔐 AUDITORÍA DE SEGURIDAD: las reglas nuevas de Firestore reconocen al
            // personal (dueño/admin/barbero) por su documento en "users". Los
            // barberos y admins ya lo tienen; el dueño se identificaba solo por
            // companies.ownerEmail, así que se registra acá, en cada ingreso
            // (merge: no pisa nada). Es inofensivo con las reglas actuales.
            // Va en segundo plano: no hace falta esperarlo para mostrar el panel.
            setDoc(doc(db, 'users', firebaseUser.uid), {
              email: firebaseUser.email,
              role: 'owner',
              companyId: companyDoc.id,
              name: companyData.name || '',
              updatedAt: new Date()
            }, { merge: true }).catch((eRegDueno) => {
              console.error('No se pudo registrar al dueño en users:', eRegDueno.message);
            });

            currentUserObj = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'owner',
              companyId: companyDoc.id,
              plan: companyData.plan
            };
            setIsAdminAuth(true);
            localStorage.setItem('admin-auth', 'true');
            setCurrentPlan({ level: companyData.plan });
            setEnabledFeatures(companyData.enabledFeatures || {});

          } else {
            // ✂️ 3. ¿ES UN BARBERO EMPLEADO?
            const qUser = query(collection(db, "users"), where("email", "==", firebaseUser.email));
            const userSnap = await getDocs(qUser);

            if (!userSnap.empty) {
              const userData = userSnap.docs[0].data();

              // 🔒 A pedido: se intentó acá una verificación que cortaba
              // el acceso si el barbero referenciado (userData.barberId)
              // ya no existía — pensada para el caso de un registro de
              // acceso huérfano después de eliminar un barbero. Se sacó
              // por completo: dependía de que "companyId" estuviera
              // perfectamente enlazado entre el documento de acceso, la
              // ubicación y el barbero, y en datos reales ese enlace no
              // siempre coincide — terminó bloqueando a barberos que sí
              // seguían existiendo (incluso después de un primer intento
              // de hacerla "fail-safe"). La protección real contra el
              // problema original ya está resuelta en otro lado: al
              // eliminar un barbero (handleDeleteBarber, en
              // useCrudActions.js), su registro de acceso se borra en el
              // mismo momento — así que un barbero recién eliminado ya no
              // puede entrar, sin necesitar este chequeo extra acá.

              currentUserObj = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                role: userData.role || 'barber',
                barberId: userData.barberId,
                companyId: userData.companyId,
                name: userData.name || firebaseUser.displayName
              };

              // Cargar el plan de la empresa del barbero
              if (userData.companyId) {
                const compRef = doc(db, "companies", userData.companyId);
                const compSnap = await getDoc(compRef);
                if (compSnap.exists()) {
                  const compDataBarbero = compSnap.data();
                  // 🔒 Mismo candado que para el dueño — si la empresa
                  // a la que pertenece este barbero está vencida o
                  // suspendida, tampoco puede entrar (el dueño no pagó,
                  // así que los empleados quedan afuera también).
                  if (chequearSuscripcionVencida(compDataBarbero)) {
                    addToast("Suscripción Vencida", "La barbería a la que pertenecés tiene la suscripción vencida. Avisale al dueño.", "error");
                    await signOut(auth);
                    setIsPlanLoading(false);
                    return;
                  }
                  setCurrentPlan({
                    level: compDataBarbero.plan || 'basic',
                    limits: compDataBarbero.limits
                  });
                  setEnabledFeatures(compDataBarbero.enabledFeatures || {});
                } else {
                  setCurrentPlan({ level: 'basic' });
                }

                // ✅ FIX MEDALLA: Cargar locales Y barberos inmediatamente
                try {
                  const locsSnap = await getDocs(
                    query(collection(db, 'locations'), where('companyId', '==', userData.companyId))
                  );
                  if (!locsSnap.empty) {
                    const firstLocationId = locsSnap.docs[0].id;
                    setSelectedAdminLocationId(firstLocationId);
                    await loadBarbers(firstLocationId);
                    await loadProducts(firstLocationId); // ← esta línea faltaba
                  }
                } catch (e) {
                  console.error("Error cargando local del barbero:", e);
                }

              } else {
                setCurrentPlan({ level: 'basic' });
              }

              setIsAdminAuth(true);
              localStorage.setItem('admin-auth', 'true');
              setView('admin');
            } else {
              // 👤 4. CLIENTE NORMAL
              currentUserObj = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                role: 'client'
              };
              setIsAdminAuth(false);
              localStorage.removeItem('admin-auth');
              setCurrentPlan({ level: 'basic' });
            }
          }

          setCurrentUser(currentUserObj);

          if (currentUserObj?.role === 'owner' || currentUserObj?.role === 'barber' || currentUserObj?.role === 'superadmin') {
            // 🔧 FIX: esta rama entra tanto al iniciar sesión a mano como
            // al recargar la página con una sesión ya guardada
            // (browserLocalPersistence). En cualquiera de los dos casos,
            // "barbers"/"locations"/"selectedLocation" podían venir con
            // datos residuales de la vista de cliente (reserva pública)
            // si esta pestaña había estado en esa vista antes — se limpian
            // acá, justo antes de cargar los datos reales del admin, para
            // que nunca se llegue a ver ni un instante algo de otro
            // contexto.
            setBarbers([]);
            setSelectedLocation(null);
            loadLocations(currentUserObj);
            loadBookings(currentUserObj);
          } else {
            loadLocations(null);
          }

          setIsPlanLoading(false);

        } catch (error) {
          console.error("Error validando usuario:", error);
          setIsPlanLoading(false);
        }

      } else {
        // SIN SESIÓN
        setCurrentUser(null);
        setIsAdminAuth(false);
        localStorage.removeItem('admin-auth');
        setIsPlanLoading(false);
        loadLocations(null);
      }
    });

    return () => unsubscribe();
  }, []);


  const handleClientLogout = async () => {
    const auth = getAuth();
    await signOut(auth);

    setCurrentUser(null);
    setCurrentPlan(null);
    setEnabledFeatures({});
    setClientInfo({ name: '', phone: '', email: '', ci: '', observaciones: '' });

    // 2. FORZAR LA VISTA AL WIZARD DE RESERVA (CLIENTE)
    setView('booking');

    // 🟢 CORRECCIÓN: Si estamos en la URL de un local (selectedLocation existe), 
    // lo regresamos al paso 1 (Barberos). Si no hay local, al paso 0.
    if (selectedLocation) {
      setStep(1);
    } else {
      setStep(0);
    }

    // 3. LIMPIAR LA URL (Solo quitamos el hash del admin si se quedó pegado, 
    // pero respetamos si está en /mister-barber)
    if (window.location.hash.includes('admin')) {
      window.history.pushState("", document.title, window.location.pathname + window.location.search);
    }

    addToast("Sesión Cerrada", "Has cerrado tu sesión.", "info");
  };



  const handleSelectLocation = async (location) => {
    setSelectedLocation(location);
    // ⚡ Si ya se adelantó la carga de profesionales y servicios de ESTE
    // mismo local (visitante que ya había entrado antes — ver "Precarga de
    // locales"), no se piden de nuevo (además, volver a pedirlos los vacía
    // un instante y se vería "Cargando Profesionales..." otra vez). Si esa
    // carga adelantada falló, se reintenta normal.
    const precarga = precargaPublicaRef.current;
    if (precarga && precarga.id === location.id) {
      precargaPublicaRef.current = null;
      precarga.promesa.then(ok => {
        if (!ok) { loadBarbers(location.id); loadServices(location.id); }
      });
    } else {
      loadBarbers(location.id);
      loadServices(location.id);
    }
    // ⚡ Se le pasa el local ya conocido para que loadSettings no lo relea.
    loadSettings(location.id, location);

    // Reiniciamos el estado por si acaso
    setIsShopSuspended(false);

    // 👇 LA MAGIA: Buscar el plan real y VERIFICAR SI ESTÁ SUSPENDIDA
    if (location.companyId) {
      try {
        const companyDocRef = doc(db, "companies", location.companyId);
        const companySnap = await getDoc(companyDocRef);

        if (companySnap.exists()) {
          const compData = companySnap.data();

          // 🛑 EL CANDADO PARA CLIENTES (Vigila prueba y mensualidad de forma segura)
          // 🔧 Ahora reusa chequearSuscripcionVencida (misma función que
          // usa el login del admin), en vez de tener esta lógica
          // duplicada acá — un solo lugar para mantenerla.
          if (chequearSuscripcionVencida(compData)) {
            setIsShopSuspended(true);
            return; // Detenemos la ejecución, no pasa al "Paso 1"
          }

          setCurrentPlan({
            level: compData.plan || 'basic',
            limits: compData.limits
          });
          setEnabledFeatures(compData.enabledFeatures || {});
        }
      } catch (error) {
        console.error("Error al cargar el plan de la empresa:", error);
      }
    }

    setStep(1);
  };

  // --------------------------------------------------------------------------
  // LÓGICA DE REACT ROUTER: COMPONENTES DINÁMICOS Y PROPS
  // --------------------------------------------------------------------------
  const ClientViewWrapper = (props) => {
    const { shopSlug } = useParams();

    useEffect(() => {
      if (shopSlug && props.locations.length > 0) {
        // Busca el local por su campo 'slug' (ej: javier-barber) o formatea el nombre
        const found = props.locations.find(l =>
          l.slug === shopSlug ||
          l.name.toLowerCase().replace(/\s+/g, '-') === shopSlug
        );

        if (found && props.selectedLocation?.id !== found.id) {
          props.handleSelectLocation(found);
        }
      }
    }, [shopSlug, props.locations]);

    return <ClientView {...props} />;
  };

  // Empaquetamos todos los props de ClientView para no repetir código
  const clientViewProps = {
    currentUser, handleClientLogout, setShowLoginModal, showLoginModal, loading,
    isDarkMode, themeClasses, shopSettings, logoVersion, view, setView, step, setStep,
    locations, barbers, services, selectedLocation, handleSelectLocation, setSelectedLocation,
    selectedBarber, setSelectedBarber, selectedServices, handleSelectService, setSelectedServices,
    selectedDate, setSelectedDate, selectedTime, setSelectedTime, clientInfo, setClientInfo,
    formatOpenDays, getAvailableDates, isDateBookable, timeSlots, lunchSlots, isBlockFree, getEndTime,
    totalDuration, totalPrice, slotsNeeded, confirmBooking, deleteBooking, generateGoogleCalendarLink,
    myBookings, searchPhone, setSearchPhone, searchMyBookings, showPaymentModal, setShowPaymentModal,
    showSuccess, setShowSuccess, isPaid, setIsPaid, lastBookingId, setLastBookingId, paymentMethod,
    setPaymentMethod, showModal, setShowModal, modalTitle, modalMessage, viewServicePhoto,
    setViewServicePhoto, activeCategory, setActiveCategory, phoneError, handlePhoneChange,
    showAlert, toasts, removeToast, currentPlan, loadSettings, blockedTimes, submitReview, exchangeRates,
    membershipCoverage, effectiveTotalPrice, myMembership, checkMyMembership,
    membershipPlansAll,
    enabledFeatures, loadingLocations, clientLoading, setClientLoading,
    isAdminAuth, enviarWhatsAppManual, setEnviarWhatsAppManual,
    modoDomicilio, setModoDomicilio, datosDomicilio, setDatosDomicilio
  };

  // --------------------------------------------------------------------------
  // EL RENDER PRINCIPAL CON RUTAS LIMPIAS
  // --------------------------------------------------------------------------
  return (
    <ErrorBoundary>
      <BrowserRouter>
        {/* ⚡ Pantalla de espera solo para el panel admin/SuperAdmin mientras
            baja su código (ver "CARGA DIFERIDA" arriba). El link de cliente
            no usa nada diferido, así que nunca ve esto. */}
        <Suspense fallback={
          <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: isDarkMode ? '#1C1B19' : '#EDEFF1' }}>
            <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', borderTopColor: '#d4af37' }} />
          </div>
        }>
        <Routes>

          <Route path="/god-mode-2026" element={
            <SuperAdminDashboard
              addToast={addToast}
              isDarkMode={isDarkMode}
            />
          } />

          {/* 🔴 RUTA DE ADMINISTRADOR: /login */}
          <Route path="/login" element={
            !isAdminAuth ? (
              <>
                <AdminLoginForm
                  adminEmail={adminEmail} setAdminEmail={setAdminEmail}
                  adminPassword={adminPassword} setAdminPassword={setAdminPassword}
                  handleAdminLogin={handleAdminLogin} loading={loading}
                />
                <ToastContainer toasts={toasts} removeToast={removeToast} />
              </>
            ) : (
              <div className={`min-h-screen transition-colors duration-500 ${themeClasses.mainBg}`}>
                {renderAdminPanel()}
                <ToastContainer toasts={toasts} removeToast={removeToast} />

                <AnimatePresence>
                  {showSuccess && (
                    <div className="fixed inset-0 z-[100002] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
                      <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={400} gravity={0.15} />
                      <motion.div initial={{ scale: 0.5, opacity: 0, y: 50 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }} transition={{ type: "spring", duration: 0.6 }} className={`rounded-3xl p-8 max-w-sm w-full text-center border-2 shadow-2xl relative overflow-hidden ${isDarkMode ? 'bg-gray-800 border-green-500/50' : 'bg-white border-green-500'}`}>
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 to-emerald-600"></div>
                        <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/40 animate-in zoom-in duration-300">
                          <CheckCircle size={48} className="text-white stroke-[3]" />
                        </div>
                        <h2 className={`text-3xl font-black mb-2 tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>¡PAGO EXITOSO!</h2>
                        <p className={`text-lg font-medium mb-8 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>La reserva se ha confirmado y el pago se ha registrado correctamente.</p>
                        {lastBookingId && (<div className={`mb-8 p-3 rounded-lg border font-mono text-sm font-bold ${isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-slate-100 border-slate-200 text-slate-500'}`}>Ticket: #{lastBookingId.slice(-5)}</div>)}
                        <button onClick={() => { setShowSuccess(false); setBookingToEdit(null); if (adminSubView === 'booking_wizard') setAdminSubView('calendar'); loadBookings(); }} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold py-4 rounded-xl shadow-xl transition-all transform active:scale-95 flex items-center justify-center gap-2">
                          <Check size={20} strokeWidth={3} /> Finalizar
                        </button>
                      </motion.div>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            )
          } />

          <Route path="/reset-password" element={<ResetPassword />} />



          {/* 🟢 1. RUTA RAÍZ: Patea al visitante a tu página web de ventas */}
          <Route path="/" element={<RedirectToLanding />} />

          {/* 🟡 2. TU RUTA SECRETA DE DEMOSTRACIÓN: Para mostrar a tus clientes */}
          <Route path="/demo" element={<ClientView {...clientViewProps} />} />

          {/* 🟢 3. RUTA PÚBLICA DINÁMICA: El link real que usarán las barberías (Ej: /blas-barber) */}
          {/* 🟢 3. RUTA PÚBLICA DINÁMICA CON CANDADO DE SUSPENSIÓN */}
          <Route path="/:shopSlug" element={
            isShopSuspended ? (
              <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6 text-center animate-in fade-in zoom-in duration-500">
                <div className="p-6 bg-red-500/10 rounded-full mb-6 border border-red-500/30">
                  <Ban size={64} className="text-red-500" />
                </div>
                <h1 className="text-3xl font-black text-white mb-3">Servicio Suspendido</h1>
                <p className="text-gray-400 max-w-md text-sm leading-relaxed">
                  Esta barbería se encuentra temporalmente inactiva y no puede recibir nuevas reservas en este momento.
                  Por favor, intenta nuevamente más tarde.
                </p>
              </div>
            ) : (
              <ClientViewDynamic {...clientViewProps} />
            )
          } />

        </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
};




export default App;